import { AnalyticsDashboard } from "@/components/analytics/AnalyticsDashboard";
import { AttendanceCalendar } from "@/components/AttendanceCalendar";
import { ChangePassword } from "@/components/ChangePassword";
import { CoreHRDashboard } from "@/components/core-hr/CoreHRDashboard";
import { ForgotPassword } from "@/components/ForgotPassword";
import { LifecycleDashboard } from "@/components/lifecycle/LifecycleDashboard";
import { LMSDashboard } from "@/components/lms/LMSDashboard";
import { Login } from "@/components/Login";
import { PayrollDashboard } from "@/components/payroll/PayrollDashboard";
import { PerformanceDashboard } from "@/components/performance/PerformanceDashboard";
import { RecruitmentDashboard } from "@/components/recruitment/RecruitmentDashboard";
import { ResetPassword } from "@/components/ResetPassword";
import { TeamAttendanceSection } from "@/components/TeamAttendanceSection";
import { ShiftManagement } from "@/components/settings/ShiftManagement";
import { ShiftAssignmentManager } from "@/components/settings/ShiftAssignmentManager";
import { NotificationPreferences } from "@/components/settings/NotificationPreferences";
import { AutoCheckoutMetrics } from "@/components/dashboard/AutoCheckoutMetrics";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    CommandShortcut,
} from "@/components/ui/command";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserManagement } from "@/components/UserManagement";
import { UserProfileSection } from "@/components/UserProfileSection";
import { useApp } from "@/context/AppContext";
import { DashboardView } from "@/features/dashboard/DashboardView";
import { useKV } from "@/hooks/use-kv-adapter";
import "@/i18n";
import { api } from "@/lib/api";
import {
    exportOrganizationData,
    exportToCSV,
    formatAttendanceReport,
    formatEmployeeReport,
    formatLeaveReport,
    formatTimesheetReport,
} from "@/lib/export";
import {
    ArrowRight,
    BookOpen,
    Briefcase,
    CalendarCheck,
    CalendarDots,
    Calendar as CalendarIcon,
    CaretLeft,
    CaretRight,
    ChartLine,
    Check,
    CheckCircle,
    Clock,
    ClockAfternoon,
    CurrencyDollar,
    DownloadSimple,
    FileText,
    Folder,
    Gear,
    GearSix,
    Gift,
    GraduationCap,
    Heart,
    House,
    Keyboard,
    List,
    Lock,
    MagnifyingGlass,
    Megaphone,
    Paperclip,
    Pause,
    Pencil,
    Play,
    Plus,
    Receipt,
    SignOut,
    Smiley,
    Star,
    Target,
    Trash,
    TrendUp,
    UploadSimple,
    User,
    UserGear,
    Users,
    UsersThree,
    Warning,
    X
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { toast } from "sonner";

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo?: string;
  primaryColor?: string;
  domain?: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  createdAt: string;
  status: "active" | "suspended" | "trial";
  subscriptionPlan: "free" | "basic" | "professional" | "enterprise";
  subscriptionExpiresAt?: string;
  billingEmail?: string;
  settings: {
    allowEmployeeSelfEdit: boolean;
    requireManagerApprovalForLeave: boolean;
    enableGeofencing: boolean;
    enableOvertimeTracking: boolean;
    workingHoursPerDay: number;
    workingDaysPerWeek: number;
    fiscalYearStart: string;
  };
}

interface OrganizationDepartment {
  id: string;
  organizationId: string;
  name: string;
  code: string;
  managerId?: string;
  description?: string;
  createdAt: string;
}

interface OrganizationLocation {
  id: string;
  organizationId: string;
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  timezone: string;
  coordinates?: {
    latitude: number;
    longitude: number;
    radius: number; // meters for geofencing
  };
  createdAt: string;
}

interface User {
  id: string;
  organizationId: string;
  name: string;
  email: string;
  password?: string;
  role: "superadmin" | "admin" | "hr" | "manager" | "employee";
  department: string;
  avatar: string;
  joinedAt: string;
  phone?: string;
  organizations?: string[]; // Array of org IDs for multi-org users
}

interface PasswordResetToken {
  id: string;
  userId: string;
  email: string;
  token: string;
  expiresAt: string;
  createdAt: string;
  used: boolean;
}

interface OrganizationInvitation {
  id: string;
  organizationId: string;
  email: string;
  role: "admin" | "hr" | "manager" | "employee";
  departmentId?: string;
  locationId?: string;
  token: string;
  status: "pending" | "accepted" | "expired" | "cancelled";
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  acceptedAt?: string;
  cancelledAt?: string;
}

type PermissionAction = "view" | "create" | "edit" | "delete" | "approve";
type PermissionModule =
  | "attendance"
  | "leaves"
  | "timesheets"
  | "employees"
  | "reports"
  | "settings"
  | "shifts"
  | "expenses"
  | "documents"
  | "performance"
  | "onboarding"
  | "offboarding";

interface Permission {
  module: PermissionModule;
  actions: PermissionAction[];
}

interface CustomRole {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  permissions: Permission[];
  inheritsFrom?: string; // ID of parent role for inheritance
  isCustom: boolean; // true for custom roles, false for system defaults
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

interface RoleAssignment {
  id: string;
  userId: string;
  roleId: string;
  organizationId: string;
  assignedAt: string;
  assignedBy: string;
}

interface RoleAuditLog {
  id: string;
  organizationId: string;
  action:
    | "role-created"
    | "role-updated"
    | "role-deleted"
    | "role-assigned"
    | "role-unassigned";
  roleId: string;
  roleName: string;
  userId?: string; // User affected by the action
  performedBy: string;
  performedAt: string;
  changes?: Record<string, any>;
}

interface SubscriptionPlan {
  id: string;
  name: "free" | "basic" | "professional" | "enterprise";
  displayName: string;
  price: number;
  billingCycle: "monthly" | "annual";
  features: {
    maxEmployees: number | "unlimited";
    maxDepartments: number | "unlimited";
    maxLocations: number | "unlimited";
    attendanceTracking: boolean;
    leaveManagement: boolean;
    timesheets: boolean;
    performanceReviews: boolean;
    customRoles: boolean;
    apiAccess: boolean;
    advancedReports: boolean;
    prioritySupport: boolean;
  };
}

interface BillingInvoice {
  id: string;
  organizationId: string;
  invoiceNumber: string;
  amount: number;
  currency: string;
  status: "draft" | "issued" | "paid" | "overdue" | "cancelled";
  billingPeriodStart: string;
  billingPeriodEnd: string;
  dueDate: string;
  paidAt?: string;
  items: {
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }[];
  createdAt: string;
}

interface BillingAlert {
  id: string;
  organizationId: string;
  type:
    | "trial-ending"
    | "payment-due"
    | "payment-failed"
    | "limit-approaching"
    | "limit-exceeded";
  message: string;
  severity: "info" | "warning" | "error";
  createdAt: string;
  acknowledgedAt?: string;
}

interface OrganizationDeactivation {
  id: string;
  organizationId: string;
  reason?: string;
  deactivatedBy: string;
  deactivatedAt: string;
  gracePeriodEndsAt: string;
  reactivatedAt?: string;
  permanentlyDeletedAt?: string;
  archiveUrl?: string;
}

interface AttendanceRecord {
  id: string;
  employeeId?: string;
  date: string;
  checkIn: string;
  checkOut?: string;
  status:
    | "present"
    | "absent"
    | "late"
    | "half-day"
    | "sick"
    | "vacation"
    | "holiday";
  location?: string;
  breakTime?: number; // minutes
  duration?: number;
  notes?: string;
  geofenceZoneId?: string;
  isAutoCheckedIn?: boolean;
}

interface LeaveRequest {
  id: string;
  employeeName: string;
  employeeId: string;
  startDate: string;
  endDate: string;
  type: "vacation" | "sick" | "personal" | "emergency";
  status: "pending" | "approved" | "rejected";
  reason: string;
  requestedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  totalDays: number;
  attachments?: Array<{
    id: string;
    name: string;
    type: string;
    size: number;
    data: string; // base64 encoded
    uploadedAt: string;
  }>;
}

interface TimesheetEntry {
  id: string;
  employeeId?: string;
  date: string;
  project: string;
  task: string;
  hours: number;
  description: string;
  status: "draft" | "submitted" | "approved" | "rejected";
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  weekStartDate: string; // ISO date string for the Monday of the week
  overtimeHours?: number; // Hours over 8 in a day
  overtimeSubmitted?: boolean; // If overtime request has been created
  attachments?: Array<{
    id: string;
    name: string;
    type: string;
    size: number;
    data: string; // base64 encoded
    uploadedAt: string;
  }>;
}

interface OvertimeEntry {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  regularHours: number;
  overtimeHours?: number;
  hours?: number;
  reason: string;
  status: "pending" | "approved" | "rejected";
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  timesheetEntryId?: string;
  project?: string;
  compensationType: "pay" | "compTime";
  createdAt: string;
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  department: string;
  email: string;
  avatar: string;
  status: "online" | "away" | "busy" | "offline";
  isOnLeave?: boolean;
}

interface UserProfile {
  name: string;
  email: string;
  phone: string;
  department: string;
  role: string;
  startDate: string;
  avatar: string;
}

interface WeeklyGoal {
  id: string;
  title: string;
  target: number;
  current: number;
  type: "hours" | "tasks" | "projects";
  week: string;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: "info" | "success" | "warning" | "error" | "delegation";
  timestamp: string;
  read: boolean;
  userId?: string;
}

interface NotificationPreferences {
  email: boolean;
  push: boolean;
  inApp: boolean;
}

type NotificationChannel = "email" | "push" | "inApp";

interface NotificationLog {
  id: string;
  notificationId: string;
  title: string;
  message: string;
  channels: NotificationChannel[];
  timestamp: string;
  deliveryStatus: {
    email?: "sent" | "failed";
    push?: "sent" | "failed";
    inApp?: "sent" | "failed";
  };
}

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: "urgent" | "info" | "warning" | "success";
  createdBy: string;
  createdByName: string;
  createdAt: string;
  expiresAt?: string;
  isPinned: boolean;
  audience: "all" | "managers" | "hr" | "employees";
  readBy: string[];
}

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  target?: string;
  action?: string;
  completed: boolean;
}

interface GeofenceZone {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  radius: number;
  isActive: boolean;
}

interface GeofenceState {
  isEnabled: boolean;
  permissionStatus: "prompt" | "granted" | "denied";
  watchId: number | null;
  currentZoneId: string | null;
}

interface OnboardingState {
  isFirstTime: boolean;
  currentStep: number;
  steps: OnboardingStep[];
  showTooltip: boolean;
  completedSteps: string[];
}

interface Holiday {
  id: string;
  organizationId?: string;
  name: string;
  date: string; // ISO date string
  type: "public" | "company" | "optional";
  description?: string;
  recurring: boolean;
  departments?: string[];
  locations?: string[];
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
}

interface EmployeeProfile {
  id: string;
  name: string;
  email: string;
  password?: string;
  role: "superadmin" | "admin" | "hr" | "manager" | "employee";
  department: string;
  avatar: string;
  phone: string;
  createdAt: string;
  skills: Array<{
    id: string;
    name: string;
    proficiency: "Beginner" | "Intermediate" | "Expert";
  }>;
  projects: Array<{
    id: string;
    name: string;
    role: string;
    status: "Active" | "Completed";
  }>;
}

interface OnboardingChecklistItem {
  id: string;
  title: string;
  description: string;
  category:
    | "documentation"
    | "training"
    | "equipment"
    | "access"
    | "orientation"
    | "compliance";
  required: boolean;
  dueDate?: string;
  completedAt?: string;
  completedBy?: string;
  assignedTo?: string;
  notes?: string;
}

interface EmployeeOnboardingChecklist {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  startDate: string;
  items: OnboardingChecklistItem[];
  status: "not-started" | "in-progress" | "completed";
  completedPercentage: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

interface ShiftSchedule {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  shiftType: "morning" | "afternoon" | "evening" | "night" | "custom";
  startTime: string; // HH:MM format
  endTime: string; // HH:MM format
  date: string; // ISO date string
  location: string;
  notes?: string;
  status: "scheduled" | "confirmed" | "completed" | "cancelled";
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  swapRequestId?: string;
}

interface ShiftSwapRequest {
  id: string;
  requesterId: string;
  requesterName: string;
  requesterShiftId: string;
  targetEmployeeId?: string;
  targetEmployeeName?: string;
  targetShiftId?: string;
  reason: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  requestedAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
}

interface EmployeeDocument {
  id: string;
  employeeId: string;
  employeeName: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  category:
    | "identification"
    | "contract"
    | "certification"
    | "performance"
    | "other";
  uploadedAt: string;
  uploadedBy: string;
  uploadedByName: string;
  expiryDate?: string;
  accessLevel: "employee" | "hr" | "manager";
  version: number;
  previousVersionId?: string;
  description?: string;
  base64Data: string;
}

interface ExpenseReimbursement {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeDepartment: string;
  category:
    | "travel"
    | "meals"
    | "accommodation"
    | "equipment"
    | "training"
    | "supplies"
    | "other";
  amount: number;
  currency: string;
  date: string;
  description: string;
  status: "draft" | "submitted" | "approved" | "rejected" | "reimbursed";
  receiptAttachments: Array<{
    id: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    base64Data: string;
  }>;
  submittedAt?: string;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  reimbursedAt?: string;
  reimbursementMethod?: "bank-transfer" | "check" | "payroll";
  reimbursementReference?: string;
  createdAt: string;
  updatedAt: string;
}

interface WFHRequest {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeDepartment: string;
  startDate: string;
  endDate: string;
  reason: string;
  status: "pending" | "approved" | "rejected";
  requestedAt: string;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  recurring?: boolean;
  recurringDays?: (
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
  )[];
}

interface OffboardingChecklistItem {
  id: string;
  title: string;
  description: string;
  category:
    | "access-revocation"
    | "asset-return"
    | "knowledge-transfer"
    | "exit-interview"
    | "clearance"
    | "documentation";
  required: boolean;
  completedAt?: string;
  completedBy?: string;
  completedByName?: string;
  notes?: string;
}

interface EmployeeOffboarding {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  department: string;
  position: string;
  lastWorkingDay: string;
  resignationDate: string;
  reason:
    | "resignation"
    | "retirement"
    | "termination"
    | "end-of-contract"
    | "other";
  reasonDetails?: string;
  exitInterviewCompleted: boolean;
  exitInterviewDate?: string;
  exitInterviewNotes?: string;
  exitInterviewConductedBy?: string;
  exitInterviewConductedByName?: string;
  checklist: OffboardingChecklistItem[];
  status: "initiated" | "in-progress" | "completed";
  completedPercentage: number;
  initiatedBy: string;
  initiatedByName: string;
  initiatedAt: string;
  completedAt?: string;
  updatedAt: string;
  finalSettlementAmount?: number;
  finalSettlementPaid?: boolean;
  finalSettlementDate?: string;
  accessRevoked: boolean;
  accessRevokedDate?: string;
  assetsReturned: boolean;
  assetsReturnedDate?: string;
}

interface ManagerDelegation {
  id: string;
  managerId: string;
  managerName: string;
  delegateId: string;
  delegateName: string;
  startDate: string;
  endDate: string;
  reason: string;
  createdAt: string;
  createdBy: string;
  status: "active" | "expired" | "cancelled";
  permissions: (
    | "leave-approval"
    | "timesheet-approval"
    | "overtime-approval"
    | "expense-approval"
    | "shift-approval"
  )[];
  actionsLog?: Array<{
    actionId: string;
    actionType: string;
    performedAt: string;
    details: string;
  }>;
}

interface DashboardWidget {
  id: string;
  type:
    | "total-employees"
    | "open-roles"
    | "attendance-rate"
    | "performance-trends"
    | "workforce-diversity"
    | "celebrations"
    | "recent-activities"
    | "pending-tasks";
  title: string;
  visible: boolean;
  size: "small" | "medium" | "large";
  order: number;
}

interface DashboardLayout {
  widgets: DashboardWidget[];
  lastModified: string;
}

// Recognition and Awards System Interfaces
interface RecognitionType {
  id: string;
  name: string;
  points: number;
  description: string;
  icon: string;
}

interface Recognition {
  id: string;
  senderId: string;
  recipientId: string;
  type: string;
  points: number;
  reason: string;
  badge?: string;
  createdAt: string;
  reactions: { userId: string; emoji: string }[];
}

interface PointTransaction {
  id: string;
  userId: string;
  amount: number;
  type: "earned" | "redeemed" | "adjustment";
  description: string;
  referenceId?: string;
  createdAt: string;
}

// Rewards Marketplace Interfaces
interface Reward {
  id: string;
  name: string;
  description: string;
  cost: number;
  image?: string;
  category: string;
  stock: number;
  status: "active" | "inactive" | "archived";
  createdAt: string;
}

interface CartItem {
  rewardId: string;
  quantity: number;
  cost: number;
}

interface RedemptionOrder {
  id: string;
  userId: string;
  items: CartItem[];
  totalPoints: number;
  status: "pending" | "approved" | "processing" | "shipped" | "delivered";
  deliveryMethod: string;
  trackingNumber?: string;
  createdAt: string;
  updatedAt: string;
}

// Surveys Interfaces
interface SurveyQuestion {
  id: string;
  text: string;
  type: "multiple-choice" | "rating" | "text" | "yes-no" | "matrix";
  options?: string[];
  required: boolean;
}

interface Survey {
  id: string;
  title: string;
  description: string;
  questions: SurveyQuestion[];
  status: "draft" | "active" | "closed";
  anonymous: boolean;
  distributedTo: string[];
  deadline: string;
  createdAt: string;
}

interface SurveyResponse {
  id: string;
  surveyId: string;
  responderId?: string;
  answers: { questionId: string; answer: string | string[] }[];
  submittedAt: string;
}

// Learning Management System Interfaces
interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: "beginner" | "intermediate" | "advanced";
  duration: number; // in minutes
  thumbnail?: string;
  instructorId: string;
  status: "draft" | "published" | "archived";
  createdAt: string;
}

interface CourseModule {
  id: string;
  courseId: string;
  title: string;
  order: number;
  contentType: "video" | "pdf" | "quiz" | "link" | "scorm";
  content: string | object;
  duration?: number;
}

interface CourseEnrollment {
  id: string;
  userId: string;
  courseId: string;
  status: "active" | "completed" | "dropped";
  progress: number; // 0-100
  completedModules: string[];
  enrolledAt: string;
  completedAt?: string;
}

interface CourseQuiz {
  id: string;
  courseId: string;
  title: string;
  questions: QuizQuestion[];
  passingScore: number;
}

interface QuizQuestion {
  id: string;
  text: string;
  type: "multiple-choice" | "true-false" | "short-answer";
  options?: string[];
  correctAnswer: string | string[];
  points: number;
}

interface QuizAttempt {
  id: string;
  userId: string;
  quizId: string;
  score: number;
  passed: boolean;
  answers: { questionId: string; answer: string | string[] }[];
  attemptedAt: string;
}

export function LegacyAppContent() {
  const { t, i18n } = useTranslation();
  const {
    isMobile,
    sidebarCollapsed,
    setSidebarCollapsed,
    activeSection,
    setActiveSection,
    theme,
    setTheme,
    preferredLanguage,
    handleLanguageChange,
    isOnline,
    currentUser,
    setCurrentUser,
    isAuthenticated,
    organizations,
    setOrganizations,
    currentOrgId,
    setCurrentOrgId,
    currentOrg,
    orgDepartments,
    setOrgDepartments,
    orgLocations,
    setOrgLocations,
    attendanceRecords,
    setAttendanceRecords,
    leaveRequests,
    setLeaveRequests,
    timesheetEntries,
    setTimesheetEntries,
    overtimeEntries,
    setOvertimeEntries,
    employees,
    setEmployees,
    notifications,
    setNotifications,
    announcements,
    setAnnouncements,
    teamMembers,
    setTeamMembers,
    dashboardLayout,
    setDashboardLayout,
    attendanceRate,
    streak,
    setStreak,
    totalLeaveBalance,
    usedLeaveBalance,
    weeklyGoals,
    setWeeklyGoals,
    isCheckedIn,
    setIsCheckedIn,
    isOnBreak,
    setIsOnBreak,
    breakStartTime,
    setBreakStartTime,
    totalBreakTime,
    setTotalBreakTime,
    geofenceState,
    setGeofenceState,
    geofenceZones,
    setGeofenceZones,
    organizationInvitations,
    setOrganizationInvitations,
    customRoles,
    setCustomRoles,
    roleAssignments,
    setRoleAssignments,
    roleAuditLogs,
    setRoleAuditLogs,
    employeeOnboardingChecklists,
    setEmployeeOnboardingChecklists,
    shiftSchedules,
    setShiftSchedules,
    shiftSwapRequests,
    setShiftSwapRequests,
    employeeDocuments,
    setEmployeeDocuments,
    expenseReimbursements,
    setExpenseReimbursements,
    wfhRequests,
    setWfhRequests,
    employeeOffboardings,
    setEmployeeOffboardings,
    managerDelegations,
    setManagerDelegations,
    holidays,
    setHolidays,
    performanceReviews,
    setPerformanceReviews,
    handleLogin,
    handleLogout,
    handleSwitchOrganization,
    handleExportOrganizationData,
  } = useApp();

  // Manager View States
  const [rejectNotesMap, setRejectNotesMap] = useState<Record<string, string>>(
    {},
  );
  const [rejectDialogWeek, setRejectDialogWeek] = useState<string | null>(null);

  const [billingInvoices, setBillingInvoices] = useKV<BillingInvoice[]>(
    "billing-invoices",
    [],
  );
  const [billingAlerts, setBillingAlerts] = useKV<BillingAlert[]>(
    "billing-alerts",
    [],
  );
  const [orgDeactivations, setOrgDeactivations] = useKV<
    OrganizationDeactivation[]
  >("organization-deactivations", []);

  // Derived user profile for compatibility
  const userProfile: UserProfile = currentUser
    ? {
        name: currentUser.name,
        email: currentUser.email,
        phone: currentUser.phone || "+1 (555) 000-0000",
        department: currentUser.department,
        role:
          currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1),
        startDate: currentUser.joinedAt,
        avatar: currentUser.avatar,
      }
    : {
        name: "Guest",
        email: "",
        phone: "",
        department: "",
        role: "",
        startDate: "",
        avatar: "",
      };

  const [employeeProfiles, setEmployeeProfiles] = useKV<any[]>(
    "employee-profiles",
    [],
  );

  const [notificationPreferences, setNotificationPreferences] =
    useKV<NotificationPreferences>("notification-preferences", {
      email: true,
      push: true,
      inApp: true,
    });
  const [passwordResetTokens, setPasswordResetTokens] = useKV<
    PasswordResetToken[]
  >("password-reset-tokens", []);
  const [onboardingState, setOnboardingState] = useKV<OnboardingState>(
    "onboarding-state",
    {
      isFirstTime: true,
      currentStep: 0,
      steps: [],
      showTooltip: false,
      completedSteps: [],
    },
  );

  // Recognition and Awards System State
  const [recognitions, setRecognitions] = useKV<Recognition[]>(
    "recognitions",
    [],
  );
  const [recognitionTypes, setRecognitionTypes] = useKV<RecognitionType[]>(
    "recognition-types",
    [
      {
        id: "1",
        name: "Peer Recognition",
        points: 5,
        description: "Recognize a colleague",
        icon: "👏",
      },
      {
        id: "2",
        name: "Manager Award",
        points: 10,
        description: "Award from manager",
        icon: "⭐",
      },
      {
        id: "3",
        name: "Quarterly Award",
        points: 50,
        description: "Quarterly recognition",
        icon: "🏆",
      },
      {
        id: "4",
        name: "Annual Award",
        points: 100,
        description: "Annual recognition",
        icon: "👑",
      },
    ],
  );
  const [pointTransactions, setPointTransactions] = useKV<PointTransaction[]>(
    "point-transactions",
    [],
  );
  const [userPoints, setUserPoints] = useKV<Record<string, number>>(
    "user-points",
    {},
  );

  // Rewards Marketplace State
  const [rewards, setRewards] = useKV<Reward[]>("rewards", [
    {
      id: "1",
      name: "Amazon Gift Card",
      description: "$25 Amazon Gift Card",
      cost: 50,
      category: "gift-cards",
      stock: 100,
      status: "active",
      createdAt: new Date().toISOString(),
    },
    {
      id: "2",
      name: "Coffee Maker",
      description: "Premium Coffee Maker",
      cost: 150,
      category: "electronics",
      stock: 25,
      status: "active",
      createdAt: new Date().toISOString(),
    },
  ]);
  const [rewardCart, setRewardCart] = useKV<CartItem[]>("reward-cart", []);
  const [redemptionOrders, setRedemptionOrders] = useKV<RedemptionOrder[]>(
    "redemption-orders",
    [],
  );

  // Surveys State
  const [surveys, setSurveys] = useKV<Survey[]>("surveys", []);
  const [surveyResponses, setSurveyResponses] = useKV<SurveyResponse[]>(
    "survey-responses",
    [],
  );

  // Learning Management System State
  const [courses, setCourses] = useKV<Course[]>("courses", []);
  const [courseModules, setCourseModules] = useKV<CourseModule[]>(
    "course-modules",
    [],
  );
  const [courseEnrollments, setCourseEnrollments] = useKV<CourseEnrollment[]>(
    "course-enrollments",
    [],
  );
  const [courseQuizzes, setCourseQuizzes] = useKV<CourseQuiz[]>(
    "course-quizzes",
    [],
  );
  const [quizAttempts, setQuizAttempts] = useKV<QuizAttempt[]>(
    "quiz-attempts",
    [],
  );

  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [timesheetDialogOpen, setTimesheetDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState<string>("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [leaveType, setLeaveType] = useState<
    "vacation" | "sick" | "personal" | "emergency"
  >("vacation");
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveAttachments, setLeaveAttachments] = useState<
    Array<{
      id: string;
      name: string;
      type: string;
      size: number;
      data: string;
      uploadedAt: string;
    }>
  >([]);

  // Leave Management States - Date Range
  const [leaveStartDate, setLeaveStartDate] = useState<Date>();
  const [leaveEndDate, setLeaveEndDate] = useState<Date>();
  const [isLeaveManagerView, setIsLeaveManagerView] = useState(false);

  // Leave Manager Action States
  const [leaveRejectNotesMap, setLeaveRejectNotesMap] = useState<
    Record<string, string>
  >({});
  const [leaveRejectDialogId, setLeaveRejectDialogId] = useState<string | null>(
    null,
  );

  // Team Calendar States
  const [calendarView, setCalendarView] = useState<"month" | "week" | "day">(
    "month",
  );
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarDepartmentFilter, setCalendarDepartmentFilter] =
    useState<string>("all");
  const [calendarStatusFilter, setCalendarStatusFilter] =
    useState<string>("all");
  const [calendarTypeFilter, setCalendarTypeFilter] = useState<string>("all");

  // Attendance Filter States
  const [attendanceSearch, setAttendanceSearch] = useState<string>("");
  const [attendanceStatusFilter, setAttendanceStatusFilter] =
    useState<string>("all");
  const [attendanceDateFrom, setAttendanceDateFrom] = useState<string>("");
  const [attendanceDateTo, setAttendanceDateTo] = useState<string>("");
  const [attendanceSelectedIds, setAttendanceSelectedIds] = useState<
    Set<string>
  >(new Set());

  const [timesheetProject, setTimesheetProject] = useState("");
  const [timesheetTask, setTimesheetTask] = useState("");
  const [timesheetHours, setTimesheetHours] = useState("");
  const [timesheetDescription, setTimesheetDescription] = useState("");
  const [timesheetDate, setTimesheetDate] = useState<Date>(new Date());
  const [timesheetAttachments, setTimesheetAttachments] = useState<
    Array<{
      id: string;
      name: string;
      type: string;
      size: number;
      data: string;
      uploadedAt: string;
    }>
  >([]);
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    return new Date(today.setDate(diff));
  });
  const [isManagerView, setIsManagerView] = useState(false);
  const [goalTitle, setGoalTitle] = useState("");
  const [goalTarget, setGoalTarget] = useState("");
  const [goalType, setGoalType] = useState<"hours" | "tasks" | "projects">(
    "hours",
  );
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Session Timeout State
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
  const [sessionTimeoutDialogOpen, setSessionTimeoutDialogOpen] =
    useState(false);
  const SESSION_TIMEOUT_MINUTES = 30; // 30 minutes of inactivity
  const SESSION_WARNING_MINUTES = 2; // Show warning 2 minutes before timeout (at 28 minutes)

  // Log Hours Form Reset
  const resetLogHoursForm = () => {
    setTimesheetProject("");
    setTimesheetTask("");
    setTimesheetHours("");
    setTimesheetDescription("");
    setTimesheetDate(new Date());
    setTimesheetAttachments([]);
  };

  // Holiday Management State
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [holidayFormData, setHolidayFormData] = useState({
    name: "",
    date: "",
    type: "public" as "public" | "company" | "optional",
    description: "",
    recurring: false,
  });
  const [deleteHolidayId, setDeleteHolidayId] = useState<string | null>(null);
  const [onboardingDialogOpen, setOnboardingDialogOpen] = useState(false);
  const [showOnboardingTooltip, setShowOnboardingTooltip] = useState(false);
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState(0);

  // Employee Onboarding Checklist States
  const [onboardingChecklistDialogOpen, setOnboardingChecklistDialogOpen] =
    useState(false);
  const [selectedOnboardingChecklistId, setSelectedOnboardingChecklistId] =
    useState<string | null>(null);
  const [createChecklistDialogOpen, setCreateChecklistDialogOpen] =
    useState(false);

  // Manual Attendance Entry States
  const [manualEntryDialogOpen, setManualEntryDialogOpen] = useState(false);
  const [manualEntryDate, setManualEntryDate] = useState<Date>();
  const [manualCheckInTime, setManualCheckInTime] = useState("");
  const [manualCheckOutTime, setManualCheckOutTime] = useState("");
  const [manualLocation, setManualLocation] = useState(
    "Office - Main Building",
  );
  const [manualBreakTime, setManualBreakTime] = useState("");
  const [manualStatus, setManualStatus] = useState<
    "present" | "absent" | "late" | "half-day" | "sick" | "vacation" | "holiday"
  >("present");

  // WFH Request States
  const [wfhDialogOpen, setWfhDialogOpen] = useState(false);
  const [wfhStartDate, setWfhStartDate] = useState("");
  const [wfhEndDate, setWfhEndDate] = useState("");
  const [wfhReason, setWfhReason] = useState("");
  const [wfhRecurring, setWfhRecurring] = useState(false);
  const [wfhRecurringDays, setWfhRecurringDays] = useState<string[]>([]);

  // Invitation System States
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  const [bulkInvitationDialogOpen, setBulkInvitationDialogOpen] =
    useState(false);
  const [invitationEmail, setInvitationEmail] = useState("");
  const [invitationRole, setInvitationRole] =
    useState<OrganizationInvitation["role"]>("employee");
  const [invitationDepartmentId, setInvitationDepartmentId] = useState<
    string | undefined
  >();
  const [invitationLocationId, setInvitationLocationId] = useState<
    string | undefined
  >();
  const [bulkInvitationEmails, setBulkInvitationEmails] = useState("");
  const [bulkInvitationRole, setBulkInvitationRole] =
    useState<OrganizationInvitation["role"]>("employee");
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);
  const [csvImportData, setCsvImportData] = useState("");

  // Custom Roles and Permissions States
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<CustomRole | null>(null);
  const [roleFormData, setRoleFormData] = useState({
    name: "",
    description: "",
    permissions: [] as Permission[],
    inheritsFrom: undefined as string | undefined,
  });
  const [roleAssignmentDialogOpen, setRoleAssignmentDialogOpen] =
    useState(false);
  const [selectedRoleForAssignment, setSelectedRoleForAssignment] =
    useState<string>("");
  const [selectedUsersForRoleAssignment, setSelectedUsersForRoleAssignment] =
    useState<string[]>([]);
  const [roleAuditDialogOpen, setRoleAuditDialogOpen] = useState(false);

  // Manager Delegation States
  const [delegationDialogOpen, setDelegationDialogOpen] = useState(false);
  const [delegateUserId, setDelegateUserId] = useState("");
  const [delegationStartDate, setDelegationStartDate] = useState("");
  const [delegationEndDate, setDelegationEndDate] = useState("");
  const [delegationReason, setDelegationReason] = useState("");
  const [delegationPermissions, setDelegationPermissions] = useState<
    (
      | "leave-approval"
      | "timesheet-approval"
      | "overtime-approval"
      | "expense-approval"
      | "shift-approval"
    )[]
  >(["leave-approval"]);

  // Analytics States
  const [analyticsDateRange, setAnalyticsDateRange] = useState<
    "7d" | "30d" | "90d" | "all"
  >("30d");

  // Geofencing State

  // Dashboard Customization States
  const getDefaultDashboardLayout = (): DashboardLayout => ({
    widgets: [
      {
        id: "total-employees",
        type: "total-employees",
        title: "Total Employees",
        visible: true,
        size: "medium",
        order: 0,
      },
      {
        id: "open-roles",
        type: "open-roles",
        title: "Open Roles",
        visible: true,
        size: "medium",
        order: 1,
      },
      {
        id: "attendance-rate",
        type: "attendance-rate",
        title: "Attendance Rate",
        visible: true,
        size: "medium",
        order: 2,
      },
      {
        id: "performance-trends",
        type: "performance-trends",
        title: "Performance Trends",
        visible: true,
        size: "large",
        order: 3,
      },
      {
        id: "workforce-diversity",
        type: "workforce-diversity",
        title: "Workforce Diversity",
        visible: true,
        size: "large",
        order: 4,
      },
      {
        id: "celebrations",
        type: "celebrations",
        title: "Celebrations",
        visible: true,
        size: "large",
        order: 5,
      },
      {
        id: "recent-activities",
        type: "recent-activities",
        title: "Recent Activities",
        visible: true,
        size: "large",
        order: 6,
      },
      {
        id: "pending-tasks",
        type: "pending-tasks",
        title: "Pending Tasks",
        visible: true,
        size: "large",
        order: 7,
      },
    ],
    lastModified: new Date().toISOString(),
  });

  // Session Timeout Logic
  const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
  const WARNING_THRESHOLD = 60 * 1000; // 1 minute warning
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const lastActivity = useRef(Date.now());
  const logoutRef = useRef(handleLogout);

  useEffect(() => {
    logoutRef.current = handleLogout;
  }, [handleLogout]);

  useEffect(() => {
    if (!currentUser) return;

    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
    ];

    const onUserActivity = () => {
      lastActivity.current = Date.now();
    };

    events.forEach((event) => window.addEventListener(event, onUserActivity));

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity.current;
      const timeLeft = INACTIVITY_LIMIT - timeSinceLastActivity;

      if (timeLeft <= 0) {
        toast.success("Work auto-saved due to inactivity");
        logoutRef.current();
        toast.error("Session timed out due to inactivity");
        setShowTimeoutWarning(false);
      } else if (timeLeft <= WARNING_THRESHOLD) {
        if (!showTimeoutWarning) setShowTimeoutWarning(true);
      } else {
        if (showTimeoutWarning) setShowTimeoutWarning(false);
      }
    }, 1000);

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, onUserActivity),
      );
      clearInterval(interval);
    };
  }, [currentUser, showTimeoutWarning]);

  // Organization Management Handlers
  const handleCreateOrganization = (
    org: Omit<Organization, "id" | "createdAt">,
  ) => {
    const newOrg: Organization = {
      ...org,
      id: `org-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };
    setOrganizations([...organizations, newOrg]);
    setCurrentOrgId(newOrg.id);

    // Update current user to include organization
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        organizationId: newOrg.id,
        organizations: [...(currentUser.organizations || []), newOrg.id],
      });
    }

    toast.success(`Organization "${newOrg.name}" created successfully`);
    return newOrg;
  };

  const handleUpdateOrganization = (
    orgId: string,
    updates: Partial<Organization>,
  ) => {
    setOrganizations(
      organizations.map((org) =>
        org.id === orgId ? { ...org, ...updates } : org,
      ),
    );
    toast.success("Organization updated successfully");
  };

  const handleAddDepartment = (
    dept: Omit<OrganizationDepartment, "id" | "createdAt">,
  ) => {
    if (!currentOrgId) {
      toast.error("No organization selected");
      return;
    }

    const newDept: OrganizationDepartment = {
      ...dept,
      id: `dept-${Date.now()}`,
      organizationId: currentOrgId,
      createdAt: new Date().toISOString(),
    };
    setOrgDepartments([...orgDepartments, newDept]);
    toast.success(`Department "${newDept.name}" added`);
  };

  const handleAddLocation = (
    loc: Omit<OrganizationLocation, "id" | "createdAt">,
  ) => {
    if (!currentOrgId) {
      toast.error("No organization selected");
      return;
    }

    const newLoc: OrganizationLocation = {
      ...loc,
      id: `loc-${Date.now()}`,
      organizationId: currentOrgId,
      createdAt: new Date().toISOString(),
    };
    setOrgLocations([...orgLocations, newLoc]);
    toast.success(`Location "${newLoc.name}" added`);
  };

  // Invitation Management Handlers
  const handleSendInvitation = (
    invitation: Omit<
      OrganizationInvitation,
      "id" | "token" | "invitedAt" | "expiresAt" | "status"
    >,
  ) => {
    if (!currentUser || !currentOrgId) {
      toast.error("You must be logged in to send invitations");
      return;
    }

    const newInvitation: OrganizationInvitation = {
      ...invitation,
      id: crypto.randomUUID(),
      token: crypto.randomUUID(),
      status: "pending",
      invitedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
    };

    setOrganizationInvitations([
      ...(organizationInvitations || []),
      newInvitation,
    ]);
    toast.success(`Invitation sent to ${invitation.email}`);
  };

  const handleBulkSendInvitations = (
    emails: string[],
    role: OrganizationInvitation["role"],
    departmentId?: string,
    locationId?: string,
  ) => {
    if (!currentUser || !currentOrgId) {
      toast.error("You must be logged in to send invitations");
      return;
    }

    const newInvitations = emails.map((email) => ({
      id: crypto.randomUUID(),
      organizationId: currentOrgId,
      email: email.trim(),
      role,
      departmentId,
      locationId,
      token: crypto.randomUUID(),
      status: "pending" as const,
      invitedBy: currentUser.id,
      invitedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    }));

    setOrganizationInvitations([
      ...(organizationInvitations || []),
      ...newInvitations,
    ]);
    toast.success(`${newInvitations.length} invitations sent successfully`);
  };

  const handleResendInvitation = (invitationId: string) => {
    const updated = (organizationInvitations || []).map((inv) =>
      inv.id === invitationId
        ? {
            ...inv,
            token: crypto.randomUUID(), // Generate new token
            invitedAt: new Date().toISOString(),
            expiresAt: new Date(
              Date.now() + 7 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            status: "pending" as const,
          }
        : inv,
    );
    setOrganizationInvitations(updated);
    toast.success("Invitation resent successfully");
  };

  const handleCancelInvitation = (invitationId: string) => {
    const updated = (organizationInvitations || []).map((inv) =>
      inv.id === invitationId
        ? {
            ...inv,
            status: "cancelled" as const,
            cancelledAt: new Date().toISOString(),
          }
        : inv,
    );
    setOrganizationInvitations(updated);
    toast.success("Invitation cancelled");
  };

  const handleAcceptInvitation = (
    token: string,
    userData: Omit<
      User,
      "id" | "organizationId" | "joinedAt" | "role" | "department"
    >,
  ) => {
    const invitation = (organizationInvitations || []).find(
      (inv) => inv.token === token && inv.status === "pending",
    );

    if (!invitation) {
      toast.error("Invalid or expired invitation");
      return;
    }

    if (new Date(invitation.expiresAt) < new Date()) {
      const updated = (organizationInvitations || []).map((inv) =>
        inv.id === invitation.id ? { ...inv, status: "expired" as const } : inv,
      );
      setOrganizationInvitations(updated);
      toast.error("Invitation has expired");
      return;
    }

    // Create new user from invitation
    const department = invitation.departmentId
      ? (orgDepartments || []).find((d) => d.id === invitation.departmentId)
          ?.name || "General"
      : "General";

    const newUser: User = {
      ...userData,
      id: crypto.randomUUID(),
      organizationId: invitation.organizationId,
      role: invitation.role,
      department,
      joinedAt: new Date().toISOString(),
    };

    setEmployees([...(employees || []), newUser]);

    // Mark invitation as accepted
    const updated = (organizationInvitations || []).map((inv) =>
      inv.id === invitation.id
        ? {
            ...inv,
            status: "accepted" as const,
            acceptedAt: new Date().toISOString(),
          }
        : inv,
    );
    setOrganizationInvitations(updated);

    toast.success(`Welcome to the organization!`);
    handleLogin(newUser);
  };

  const handleImportInvitationsFromCSV = (csvData: string) => {
    if (!currentUser || !currentOrgId) {
      toast.error("You must be logged in to import invitations");
      return;
    }

    try {
      const lines = csvData.split("\n").filter((line) => line.trim());
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

      const emailIndex = headers.indexOf("email");
      const roleIndex = headers.indexOf("role");
      const departmentIndex = headers.indexOf("department");

      if (emailIndex === -1 || roleIndex === -1) {
        toast.error("CSV must contain 'email' and 'role' columns");
        return;
      }

      const newInvitations: OrganizationInvitation[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map((v) => v.trim());
        const email = values[emailIndex];
        const role = values[roleIndex] as OrganizationInvitation["role"];
        const departmentName =
          departmentIndex >= 0 ? values[departmentIndex] : undefined;

        if (!email || !role) continue;

        const department = departmentName
          ? (orgDepartments || []).find(
              (d) => d.name.toLowerCase() === departmentName.toLowerCase(),
            )
          : undefined;

        newInvitations.push({
          id: crypto.randomUUID(),
          organizationId: currentOrgId,
          email,
          role,
          departmentId: department?.id,
          token: crypto.randomUUID(),
          status: "pending",
          invitedBy: currentUser.id,
          invitedAt: new Date().toISOString(),
          expiresAt: new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000,
          ).toISOString(),
        });
      }

      if (newInvitations.length === 0) {
        toast.error("No valid invitations found in CSV");
        return;
      }

      setOrganizationInvitations([
        ...(organizationInvitations || []),
        ...newInvitations,
      ]);
      toast.success(
        `${newInvitations.length} invitations imported successfully`,
      );
    } catch (error) {
      console.error("CSV import error:", error);
      toast.error("Failed to import invitations from CSV");
    }
  };

  // Custom Roles and Permissions Management
  const handleCreateCustomRole = (
    role: Omit<CustomRole, "id" | "createdAt" | "isCustom">,
  ) => {
    if (!currentUser || !currentOrgId) {
      toast.error("You must be logged in to create roles");
      return;
    }

    const newRole: CustomRole = {
      ...role,
      id: crypto.randomUUID(),
      isCustom: true,
      createdAt: new Date().toISOString(),
    };

    setCustomRoles([...(customRoles || []), newRole]);

    // Add audit log
    const auditLog: RoleAuditLog = {
      id: crypto.randomUUID(),
      organizationId: currentOrgId,
      action: "role-created",
      roleId: newRole.id,
      roleName: newRole.name,
      performedBy: currentUser.id,
      performedAt: new Date().toISOString(),
    };
    setRoleAuditLogs([...(roleAuditLogs || []), auditLog]);

    toast.success(`Role "${role.name}" created successfully`);
  };

  const handleUpdateCustomRole = (
    roleId: string,
    updates: Partial<CustomRole>,
  ) => {
    if (!currentUser || !currentOrgId) {
      toast.error("You must be logged in to update roles");
      return;
    }

    const oldRole = (customRoles || []).find((r) => r.id === roleId);
    if (!oldRole) {
      toast.error("Role not found");
      return;
    }

    const updated = (customRoles || []).map((role) =>
      role.id === roleId
        ? {
            ...role,
            ...updates,
            updatedAt: new Date().toISOString(),
            updatedBy: currentUser.id,
          }
        : role,
    );
    setCustomRoles(updated);

    // Add audit log
    const auditLog: RoleAuditLog = {
      id: crypto.randomUUID(),
      organizationId: currentOrgId,
      action: "role-updated",
      roleId,
      roleName: oldRole.name,
      performedBy: currentUser.id,
      performedAt: new Date().toISOString(),
      changes: updates,
    };
    setRoleAuditLogs([...(roleAuditLogs || []), auditLog]);

    toast.success(`Role "${oldRole.name}" updated successfully`);
  };

  const handleDeleteCustomRole = (roleId: string) => {
    if (!currentUser || !currentOrgId) {
      toast.error("You must be logged in to delete roles");
      return;
    }

    const role = (customRoles || []).find((r) => r.id === roleId);
    if (!role) {
      toast.error("Role not found");
      return;
    }

    if (!role.isCustom) {
      toast.error("Cannot delete system roles");
      return;
    }

    // Remove role assignments
    const updatedAssignments = (roleAssignments || []).filter(
      (a) => a.roleId !== roleId,
    );
    setRoleAssignments(updatedAssignments);

    // Remove role
    const updatedRoles = (customRoles || []).filter((r) => r.id !== roleId);
    setCustomRoles(updatedRoles);

    // Add audit log
    const auditLog: RoleAuditLog = {
      id: crypto.randomUUID(),
      organizationId: currentOrgId,
      action: "role-deleted",
      roleId,
      roleName: role.name,
      performedBy: currentUser.id,
      performedAt: new Date().toISOString(),
    };
    setRoleAuditLogs([...(roleAuditLogs || []), auditLog]);

    toast.success(`Role "${role.name}" deleted successfully`);
  };

  const handleCloneRole = (roleId: string) => {
    if (!currentUser || !currentOrgId) {
      toast.error("You must be logged in to clone roles");
      return;
    }

    const role = (customRoles || []).find((r) => r.id === roleId);
    if (!role) {
      toast.error("Role not found");
      return;
    }

    const clonedRole: CustomRole = {
      ...role,
      id: crypto.randomUUID(),
      name: `${role.name} (Copy)`,
      isCustom: true,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
      updatedAt: undefined,
      updatedBy: undefined,
    };

    setCustomRoles([...(customRoles || []), clonedRole]);
    toast.success(`Role "${role.name}" cloned successfully`);
  };

  const handleAssignRole = (userId: string, roleId: string) => {
    if (!currentUser || !currentOrgId) {
      toast.error("You must be logged in to assign roles");
      return;
    }

    const role = (customRoles || []).find((r) => r.id === roleId);
    const user = (employees || []).find((u) => u.id === userId);

    if (!role || !user) {
      toast.error("Role or user not found");
      return;
    }

    const newAssignment: RoleAssignment = {
      id: crypto.randomUUID(),
      userId,
      roleId,
      organizationId: currentOrgId,
      assignedAt: new Date().toISOString(),
      assignedBy: currentUser.id,
    };

    setRoleAssignments([...(roleAssignments || []), newAssignment]);

    // Add audit log
    const auditLog: RoleAuditLog = {
      id: crypto.randomUUID(),
      organizationId: currentOrgId,
      action: "role-assigned",
      roleId,
      roleName: role.name,
      userId,
      performedBy: currentUser.id,
      performedAt: new Date().toISOString(),
    };
    setRoleAuditLogs([...(roleAuditLogs || []), auditLog]);

    toast.success(`Role "${role.name}" assigned to ${user.name}`);
  };

  const handleBulkAssignRole = (userIds: string[], roleId: string) => {
    if (!currentUser || !currentOrgId) {
      toast.error("You must be logged in to assign roles");
      return;
    }

    const role = (customRoles || []).find((r) => r.id === roleId);
    if (!role) {
      toast.error("Role not found");
      return;
    }

    const newAssignments: RoleAssignment[] = userIds.map((userId) => ({
      id: crypto.randomUUID(),
      userId,
      roleId,
      organizationId: currentOrgId,
      assignedAt: new Date().toISOString(),
      assignedBy: currentUser.id,
    }));

    setRoleAssignments([...(roleAssignments || []), ...newAssignments]);

    // Add audit logs
    const auditLogs: RoleAuditLog[] = userIds.map((userId) => ({
      id: crypto.randomUUID(),
      organizationId: currentOrgId,
      action: "role-assigned" as const,
      roleId,
      roleName: role.name,
      userId,
      performedBy: currentUser.id,
      performedAt: new Date().toISOString(),
    }));
    setRoleAuditLogs([...(roleAuditLogs || []), ...auditLogs]);

    toast.success(`Role "${role.name}" assigned to ${userIds.length} users`);
  };

  const handleExportRolesMatrix = () => {
    if (!currentOrgId) {
      toast.error("No organization selected");
      return;
    }

    const orgRoles = (customRoles || []).filter(
      (r) => r.organizationId === currentOrgId,
    );

    // Create CSV content
    const allModules: PermissionModule[] = [
      "attendance",
      "leaves",
      "timesheets",
      "employees",
      "reports",
      "settings",
      "shifts",
      "expenses",
      "documents",
      "performance",
      "onboarding",
      "offboarding",
    ];
    const allActions: PermissionAction[] = [
      "view",
      "create",
      "edit",
      "delete",
      "approve",
    ];

    let csvContent =
      "Role," +
      allModules.flatMap((m) => allActions.map((a) => `${m}:${a}`)).join(",") +
      "\n";

    orgRoles.forEach((role) => {
      const row = [role.name];
      allModules.forEach((module) => {
        const permission = role.permissions.find((p) => p.module === module);
        allActions.forEach((action) => {
          row.push(permission?.actions.includes(action) ? "Yes" : "No");
        });
      });
      csvContent += row.join(",") + "\n";
    });

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `roles-permissions-matrix-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Roles and permissions matrix exported");
  };

  // Subscription and Billing Handlers
  const subscriptionPlans: SubscriptionPlan[] = [
    {
      id: "free",
      name: "free",
      displayName: "Free Plan",
      price: 0,
      billingCycle: "monthly",
      features: {
        maxEmployees: 10,
        maxDepartments: 3,
        maxLocations: 1,
        attendanceTracking: true,
        leaveManagement: true,
        timesheets: false,
        performanceReviews: false,
        customRoles: false,
        apiAccess: false,
        advancedReports: false,
        prioritySupport: false,
      },
    },
    {
      id: "basic",
      name: "basic",
      displayName: "Basic Plan",
      price: 99,
      billingCycle: "monthly",
      features: {
        maxEmployees: 50,
        maxDepartments: 10,
        maxLocations: 3,
        attendanceTracking: true,
        leaveManagement: true,
        timesheets: true,
        performanceReviews: true,
        customRoles: false,
        apiAccess: false,
        advancedReports: false,
        prioritySupport: false,
      },
    },
    {
      id: "professional",
      name: "professional",
      displayName: "Professional Plan",
      price: 299,
      billingCycle: "monthly",
      features: {
        maxEmployees: 200,
        maxDepartments: "unlimited",
        maxLocations: "unlimited",
        attendanceTracking: true,
        leaveManagement: true,
        timesheets: true,
        performanceReviews: true,
        customRoles: true,
        apiAccess: true,
        advancedReports: true,
        prioritySupport: false,
      },
    },
    {
      id: "enterprise",
      name: "enterprise",
      displayName: "Enterprise Plan",
      price: 999,
      billingCycle: "monthly",
      features: {
        maxEmployees: "unlimited",
        maxDepartments: "unlimited",
        maxLocations: "unlimited",
        attendanceTracking: true,
        leaveManagement: true,
        timesheets: true,
        performanceReviews: true,
        customRoles: true,
        apiAccess: true,
        advancedReports: true,
        prioritySupport: true,
      },
    },
  ];

  const handleUpgradeSubscription = (
    newPlan: Organization["subscriptionPlan"],
  ) => {
    if (!currentOrgId || !currentOrg) {
      toast.error("No organization selected");
      return;
    }

    const updated = organizations.map((org) =>
      org.id === currentOrgId
        ? {
            ...org,
            subscriptionPlan: newPlan,
            subscriptionExpiresAt: new Date(
              Date.now() + 30 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          }
        : org,
    );
    setOrganizations(updated);

    toast.success(`Subscription upgraded to ${newPlan} plan!`);
  };

  const handleGenerateInvoice = () => {
    if (!currentOrgId || !currentOrg) {
      toast.error("No organization selected");
      return;
    }

    const plan = subscriptionPlans.find(
      (p) => p.name === currentOrg.subscriptionPlan,
    );
    if (!plan) return;

    const invoice: BillingInvoice = {
      id: crypto.randomUUID(),
      organizationId: currentOrgId,
      invoiceNumber: `INV-${Date.now()}`,
      amount: plan.price,
      currency: currentOrg.currency,
      status: "issued",
      billingPeriodStart: new Date().toISOString(),
      billingPeriodEnd: new Date(
        Date.now() + 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      items: [
        {
          description: `${plan.displayName} - Monthly Subscription`,
          quantity: 1,
          unitPrice: plan.price,
          total: plan.price,
        },
      ],
      createdAt: new Date().toISOString(),
    };

    setBillingInvoices([...(billingInvoices || []), invoice]);
    toast.success(`Invoice ${invoice.invoiceNumber} generated`);
  };

  const handleMarkInvoicePaid = (invoiceId: string) => {
    const updated = (billingInvoices || []).map((inv) =>
      inv.id === invoiceId
        ? { ...inv, status: "paid" as const, paidAt: new Date().toISOString() }
        : inv,
    );
    setBillingInvoices(updated);
    toast.success("Invoice marked as paid");
  };

  // Organization Deactivation and Data Retention Handlers
  const handleDeactivateOrganization = (reason?: string) => {
    if (!currentOrgId || !currentOrg || !currentUser) {
      toast.error("Cannot deactivate organization");
      return;
    }

    if (currentUser.role !== "superadmin" && currentUser.role !== "admin") {
      toast.error("Only administrators can deactivate organizations");
      return;
    }

    const deactivation: OrganizationDeactivation = {
      id: crypto.randomUUID(),
      organizationId: currentOrgId,
      reason,
      deactivatedBy: currentUser.id,
      deactivatedAt: new Date().toISOString(),
      gracePeriodEndsAt: new Date(
        Date.now() + 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
    };

    setOrgDeactivations([...(orgDeactivations || []), deactivation]);

    const updated = organizations.map((org) =>
      org.id === currentOrgId ? { ...org, status: "suspended" as const } : org,
    );
    setOrganizations(updated);

    // Create alert
    const alert: BillingAlert = {
      id: crypto.randomUUID(),
      organizationId: currentOrgId,
      type: "trial-ending",
      message: `Organization deactivated. Grace period ends on ${new Date(deactivation.gracePeriodEndsAt).toLocaleDateString()}. Data will be permanently deleted after this date.`,
      severity: "error",
      createdAt: new Date().toISOString(),
    };
    setBillingAlerts([...(billingAlerts || []), alert]);

    toast.success("Organization deactivated. 30-day grace period started.");
  };

  const handleReactivateOrganization = () => {
    if (!currentOrgId || !currentOrg || !currentUser) {
      toast.error("Cannot reactivate organization");
      return;
    }

    const deactivation = (orgDeactivations || []).find(
      (d) => d.organizationId === currentOrgId && !d.reactivatedAt,
    );
    if (!deactivation) {
      toast.error("No active deactivation found");
      return;
    }

    // Check if within grace period
    if (new Date() > new Date(deactivation.gracePeriodEndsAt)) {
      toast.error(
        "Grace period has expired. Organization cannot be reactivated.",
      );
      return;
    }

    const updatedDeactivations = (orgDeactivations || []).map((d) =>
      d.id === deactivation.id
        ? { ...d, reactivatedAt: new Date().toISOString() }
        : d,
    );
    setOrgDeactivations(updatedDeactivations);

    const updated = organizations.map((org) =>
      org.id === currentOrgId ? { ...org, status: "active" as const } : org,
    );
    setOrganizations(updated);

    toast.success("Organization reactivated successfully!");
  };

  const handleArchiveOrganizationData = () => {
    if (!currentOrgId || !currentOrg) {
      toast.error("No organization selected");
      return;
    }

    try {
      // Export all data
      const metadata = exportOrganizationData({
        organization: currentOrg,
        employees: (employees || []).filter(
          (e) => e.organizationId === currentOrgId,
        ),
        departments: (orgDepartments || []).filter(
          (d) => d.organizationId === currentOrgId,
        ),
        locations: (orgLocations || []).filter(
          (l) => l.organizationId === currentOrgId,
        ),
        attendance: (attendanceRecords || []).filter((a) => {
          const emp = employees.find((e) => e.id === a.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        leaves: (leaveRequests || []).filter((l) => {
          const emp = employees.find((e) => e.id === l.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        timesheets: (timesheetEntries || []).filter((t) => {
          const emp = employees.find((e) => e.id === t.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        shifts: (shiftSchedules || []).filter((s) => {
          const emp = employees.find((e) => e.id === s.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        holidays: (holidays || []).filter(
          (h) => h.organizationId === currentOrgId,
        ),
        performances: (performanceReviews || []).filter((p) => {
          const emp = employees.find((e) => e.id === p.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        documents: (employeeDocuments || []).filter((d) => {
          const emp = employees.find((e) => e.id === d.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        expenses: (expenseReimbursements || []).filter((e) => {
          const emp = employees.find((emp) => emp.id === e.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        wfhRequests: (wfhRequests || []).filter((w) => {
          const emp = employees.find((e) => e.id === w.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
      });

      toast.success("Organization data archived successfully!", {
        duration: 5000,
      });
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Failed to archive organization data");
    }
  };

  // Initialize default roles
  useEffect(() => {
    if (!currentOrgId || !currentUser) return;

    const orgRoles = (customRoles || []).filter(
      (r) => r.organizationId === currentOrgId,
    );
    if (orgRoles.length > 0) return; // Already initialized

    const allPermissions: Permission[] = [
      {
        module: "attendance",
        actions: ["view", "create", "edit", "delete", "approve"],
      },
      {
        module: "leaves",
        actions: ["view", "create", "edit", "delete", "approve"],
      },
      {
        module: "timesheets",
        actions: ["view", "create", "edit", "delete", "approve"],
      },
      { module: "employees", actions: ["view", "create", "edit", "delete"] },
      { module: "reports", actions: ["view"] },
      { module: "settings", actions: ["view", "edit"] },
      {
        module: "shifts",
        actions: ["view", "create", "edit", "delete", "approve"],
      },
      {
        module: "expenses",
        actions: ["view", "create", "edit", "delete", "approve"],
      },
      { module: "documents", actions: ["view", "create", "edit", "delete"] },
      { module: "performance", actions: ["view", "create", "edit", "delete"] },
      { module: "onboarding", actions: ["view", "create", "edit"] },
      { module: "offboarding", actions: ["view", "create", "edit"] },
    ];

    const defaultRoles: CustomRole[] = [
      {
        id: `default-superadmin-${currentOrgId}`,
        organizationId: currentOrgId,
        name: "Super Admin",
        description: "Full system access with all permissions",
        permissions: allPermissions,
        isCustom: false,
        createdAt: new Date().toISOString(),
        createdBy: "system",
      },
      {
        id: `default-admin-${currentOrgId}`,
        organizationId: currentOrgId,
        name: "Admin",
        description: "Administrative access to most features",
        permissions: allPermissions.filter((p) => p.module !== "settings"),
        isCustom: false,
        createdAt: new Date().toISOString(),
        createdBy: "system",
      },
      {
        id: `default-hr-${currentOrgId}`,
        organizationId: currentOrgId,
        name: "HR",
        description: "Human resources management",
        permissions: [
          { module: "attendance", actions: ["view", "approve"] },
          { module: "leaves", actions: ["view", "approve"] },
          { module: "timesheets", actions: ["view", "approve"] },
          { module: "employees", actions: ["view", "create", "edit"] },
          { module: "reports", actions: ["view"] },
          { module: "onboarding", actions: ["view", "create", "edit"] },
          { module: "offboarding", actions: ["view", "create", "edit"] },
          { module: "documents", actions: ["view", "create", "edit"] },
          { module: "performance", actions: ["view"] },
        ],
        isCustom: false,
        createdAt: new Date().toISOString(),
        createdBy: "system",
      },
      {
        id: `default-manager-${currentOrgId}`,
        organizationId: currentOrgId,
        name: "Manager",
        description: "Team management and approval",
        permissions: [
          { module: "attendance", actions: ["view", "approve"] },
          { module: "leaves", actions: ["view", "approve"] },
          { module: "timesheets", actions: ["view", "approve"] },
          { module: "employees", actions: ["view"] },
          { module: "reports", actions: ["view"] },
          { module: "shifts", actions: ["view", "create", "edit"] },
          { module: "expenses", actions: ["view", "approve"] },
          { module: "performance", actions: ["view", "create", "edit"] },
        ],
        isCustom: false,
        createdAt: new Date().toISOString(),
        createdBy: "system",
      },
      {
        id: `default-employee-${currentOrgId}`,
        organizationId: currentOrgId,
        name: "Employee",
        description: "Basic employee access",
        permissions: [
          { module: "attendance", actions: ["view", "create"] },
          { module: "leaves", actions: ["view", "create"] },
          { module: "timesheets", actions: ["view", "create", "edit"] },
          { module: "expenses", actions: ["view", "create"] },
          { module: "documents", actions: ["view"] },
        ],
        isCustom: false,
        createdAt: new Date().toISOString(),
        createdBy: "system",
      },
    ];

    setCustomRoles([...(customRoles || []), ...defaultRoles]);
  }, [currentOrgId, currentUser, customRoles, setCustomRoles]);

  useEffect(() => {
    if (currentUser) {
      Promise.all([
        api.getAttendance().then(setAttendanceRecords).catch(console.error),
        api.getLeaveRequests().then(setLeaveRequests).catch(console.error),
        api.getTimesheets().then(setTimesheetEntries).catch(console.error),
      ]);
    } else {
      setAttendanceRecords([]);
      setLeaveRequests([]);
      setTimesheetEntries([]);
    }
  }, [currentUser]);

  // Check billing limits periodically
  useEffect(() => {
    if (!currentOrgId || !currentOrg) return;

    // Initial check
    const checkLimits = () => {
      const plan = subscriptionPlans.find(
        (p) => p.name === currentOrg.subscriptionPlan,
      );
      if (!plan) return;

      const employeeCount = (employees || []).filter(
        (e) => e.organizationId === currentOrgId,
      ).length;

      const newAlerts: BillingAlert[] = [];

      // Check employee limit
      if (plan.features.maxEmployees !== "unlimited") {
        const limit = plan.features.maxEmployees as number;
        if (employeeCount >= limit) {
          const existingAlert = (billingAlerts || []).find(
            (a) =>
              a.organizationId === currentOrgId &&
              a.type === "limit-exceeded" &&
              !a.acknowledgedAt,
          );
          if (!existingAlert) {
            newAlerts.push({
              id: crypto.randomUUID(),
              organizationId: currentOrgId,
              type: "limit-exceeded",
              message: `Employee limit exceeded: ${employeeCount}/${limit}. Please upgrade your plan.`,
              severity: "error",
              createdAt: new Date().toISOString(),
            });
          }
        }
      }

      // Check trial expiry
      if (currentOrg.status === "trial" && currentOrg.subscriptionExpiresAt) {
        const daysUntilExpiry = Math.ceil(
          (new Date(currentOrg.subscriptionExpiresAt).getTime() - Date.now()) /
            (24 * 60 * 60 * 1000),
        );
        if (daysUntilExpiry <= 7 && daysUntilExpiry > 0) {
          const existingAlert = (billingAlerts || []).find(
            (a) =>
              a.organizationId === currentOrgId &&
              a.type === "trial-ending" &&
              !a.acknowledgedAt,
          );
          if (!existingAlert) {
            newAlerts.push({
              id: crypto.randomUUID(),
              organizationId: currentOrgId,
              type: "trial-ending",
              message: `Your trial expires in ${daysUntilExpiry} days. Upgrade to continue using all features.`,
              severity: "warning",
              createdAt: new Date().toISOString(),
            });
          }
        }
      }

      if (newAlerts.length > 0) {
        setBillingAlerts([...(billingAlerts || []), ...newAlerts]);
      }
    };

    checkLimits();

    const interval = setInterval(checkLimits, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(interval);
  }, [currentOrgId, currentOrg, employees]);

  // Attendance Filter and Bulk Operations
  const filteredAttendanceRecords = (attendanceRecords || []).filter(
    (record) => {
      // Search filter
      if (
        attendanceSearch &&
        !record.date.toLowerCase().includes(attendanceSearch.toLowerCase())
      ) {
        return false;
      }

      // Status filter
      if (
        attendanceStatusFilter !== "all" &&
        record.status !== attendanceStatusFilter
      ) {
        return false;
      }

      // Date range filter
      if (attendanceDateFrom) {
        const recordDate = new Date(record.date);
        const fromDate = new Date(attendanceDateFrom);
        if (recordDate < fromDate) return false;
      }
      if (attendanceDateTo) {
        const recordDate = new Date(record.date);
        const toDate = new Date(attendanceDateTo);
        if (recordDate > toDate) return false;
      }

      return true;
    },
  );

  const handleSelectAllAttendance = () => {
    if (attendanceSelectedIds.size === filteredAttendanceRecords.length) {
      setAttendanceSelectedIds(new Set());
    } else {
      setAttendanceSelectedIds(
        new Set(filteredAttendanceRecords.map((r) => r.id)),
      );
    }
  };

  const handleToggleAttendanceSelection = (id: string) => {
    const newSelection = new Set(attendanceSelectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setAttendanceSelectedIds(newSelection);
  };

  const handleBulkDeleteAttendance = () => {
    if (attendanceSelectedIds.size === 0) {
      toast.error("No records selected");
      return;
    }

    const remaining = attendanceRecords.filter(
      (r) => !attendanceSelectedIds.has(r.id),
    );
    setAttendanceRecords(remaining);
    setAttendanceSelectedIds(new Set());
    toast.success(`Deleted ${attendanceSelectedIds.size} attendance records`);
  };

  const handleExportFilteredAttendance = () => {
    if (filteredAttendanceRecords.length === 0) {
      toast.error("No records to export");
      return;
    }

    const csvData = filteredAttendanceRecords.map((record) => ({
      Date: record.date,
      "Check In": record.checkIn,
      "Check Out": record.checkOut || "N/A",
      Status: record.status,
      Location: record.location || "N/A",
      "Break Time (mins)": record.breakTime || 0,
    }));

    exportToCSV(csvData, "attendance-filtered");
    toast.success("Attendance records exported successfully");
  };

  // Initialize with sample data for better first-time experience
  useEffect(() => {
    // Initialize onboarding steps
    if (onboardingState?.steps.length === 0) {
      const onboardingSteps: OnboardingStep[] = [
        {
          id: "welcome",
          title: "Welcome to FlowHR! 🎉",
          description:
            "Let's get you started with a quick tour of your new HR management system.",
          completed: false,
        },
        {
          id: "profile-setup",
          title: "Complete Your Profile",
          description:
            "Set up your personal information to personalize your experience.",
          target: "profile-button",
          action: "Open Profile",
          completed: false,
        },
        {
          id: "first-checkin",
          title: "Try Your First Check-in",
          description: "Start tracking your attendance with a simple check-in.",
          target: "checkin-button",
          action: "Check In",
          completed: false,
        },
        {
          id: "explore-tabs",
          title: "Explore Key Features",
          description:
            "Navigate through different sections to see attendance, leaves, timesheet, and reports.",
          target: "tab-navigation",
          completed: false,
        },
        {
          id: "log-first-hours",
          title: "Log Your First Hours",
          description:
            "Record time spent on projects for accurate time tracking.",
          target: "log-hours-button",
          action: "Log Hours",
          completed: false,
        },
        {
          id: "set-weekly-goal",
          title: "Set a Weekly Goal",
          description: "Create your first productivity goal to stay motivated.",
          target: "set-goal-button",
          action: "Set Goal",
          completed: false,
        },
        {
          id: "keyboard-shortcuts",
          title: "Learn Keyboard Shortcuts",
          description: "Discover time-saving shortcuts for power users.",
          target: "keyboard-shortcuts-button",
          action: "View Shortcuts",
          completed: false,
        },
        {
          id: "completion",
          title: "You're All Set! 🚀",
          description:
            "Congratulations! You've completed the onboarding tour. Enjoy using FlowHR!",
          completed: false,
        },
      ];

      setOnboardingState(
        (
          current = {
            isFirstTime: true,
            currentStep: 0,
            steps: [],
            showTooltip: false,
            completedSteps: [],
          },
        ) => ({
          ...current,
          steps: onboardingSteps,
        }),
      );
    }

    // Show onboarding for first-time users
    if (onboardingState?.isFirstTime && onboardingState?.steps.length > 0) {
      setOnboardingDialogOpen(true);
    }

    // Sample attendance data removed in favor of API fetching

    // Sample timesheet data removed in favor of API fetching

    if ((teamMembers || []).length === 0) {
      const sampleTeam: TeamMember[] = [
        {
          id: "1",
          name: "Sarah Chen",
          role: "Product Manager",
          department: "Product",
          email: "sarah.chen@company.com",
          avatar: "👩‍💼",
          status: "online",
        },
        {
          id: "2",
          name: "Marcus Rodriguez",
          role: "UI/UX Designer",
          department: "Design",
          email: "marcus.r@company.com",
          avatar: "🎨",
          status: "busy",
        },
        {
          id: "3",
          name: "Emily Davis",
          role: "Backend Developer",
          department: "Engineering",
          email: "emily.davis@company.com",
          avatar: "⚡",
          status: "online",
        },
        {
          id: "4",
          name: "James Wilson",
          role: "Data Analyst",
          department: "Analytics",
          email: "james.w@company.com",
          avatar: "📊",
          status: "away",
          isOnLeave: true,
        },
      ];
      setTeamMembers(sampleTeam);
    }

    if ((weeklyGoals || []).length === 0) {
      const currentWeek = new Date().toISOString().split("T")[0];
      const sampleGoals: WeeklyGoal[] = [
        {
          id: "1",
          title: "Complete 40 working hours",
          target: 40,
          current: 28,
          type: "hours",
          week: currentWeek,
        },
        {
          id: "2",
          title: "Log time for 3 projects",
          target: 3,
          current: 2,
          type: "projects",
          week: currentWeek,
        },
      ];
      setWeeklyGoals(sampleGoals);
    }

    if ((notifications || []).length === 0) {
      const sampleNotifications: Notification[] = [
        {
          id: "1",
          title: "Leave Request Approved",
          message: "Your vacation request for next Friday has been approved.",
          type: "success",
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          read: false,
        },
        {
          id: "2",
          title: "Team Meeting Reminder",
          message: "Sprint planning meeting starts in 30 minutes.",
          type: "info",
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          read: true,
        },
      ];
      setNotifications(sampleNotifications);
    }

    // Initialize sample holidays
    if ((holidays || []).length === 0) {
      const currentYear = new Date().getFullYear();
      const sampleHolidays: Holiday[] = [
        {
          id: crypto.randomUUID(),
          name: "New Year's Day",
          date: `${currentYear}-01-01`,
          type: "public",
          description: "Public holiday celebrating the first day of the year",
          recurring: true,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.id || "system",
        },
        {
          id: crypto.randomUUID(),
          name: "Independence Day",
          date: `${currentYear}-07-04`,
          type: "public",
          description: "National independence day celebration",
          recurring: true,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.id || "system",
        },
        {
          id: crypto.randomUUID(),
          name: "Thanksgiving",
          date: `${currentYear}-11-28`,
          type: "public",
          description: "Thanksgiving holiday",
          recurring: true,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.id || "system",
        },
        {
          id: crypto.randomUUID(),
          name: "Christmas Day",
          date: `${currentYear}-12-25`,
          type: "public",
          description: "Christmas holiday",
          recurring: true,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.id || "system",
        },
        {
          id: crypto.randomUUID(),
          name: "Company Founding Day",
          date: `${currentYear}-03-15`,
          type: "company",
          description: "Anniversary of company founding",
          recurring: true,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.id || "system",
        },
      ];
      setHolidays(sampleHolidays);
    }

    // Initialize sample announcements
    if ((announcements || []).length === 0) {
      const sampleAnnouncements: Announcement[] = [
        {
          id: crypto.randomUUID(),
          title: "Welcome to FlowHR!",
          content:
            "We're excited to have you onboard. This announcement board will keep you updated on important company news and updates.",
          type: "info",
          createdBy: currentUser?.id || "system",
          createdByName: currentUser?.name || "System",
          createdAt: new Date().toISOString(),
          isPinned: true,
          audience: "all",
          readBy: [],
        },
        {
          id: crypto.randomUUID(),
          title: "Q1 Performance Reviews",
          content:
            "Performance reviews for Q1 will be conducted next week. Please ensure all your timesheets and project updates are complete.",
          type: "warning",
          createdBy: currentUser?.id || "system",
          createdByName: currentUser?.name || "HR Team",
          createdAt: new Date(
            Date.now() - 2 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          expiresAt: new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          isPinned: false,
          audience: "all",
          readBy: [],
        },
        {
          id: crypto.randomUUID(),
          title: "Team Building Event This Friday",
          content:
            "Join us for a team building event this Friday at 4 PM in the main conference room. Snacks and refreshments will be provided!",
          type: "success",
          createdBy: currentUser?.id || "system",
          createdByName: currentUser?.name || "People Ops",
          createdAt: new Date(
            Date.now() - 1 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          isPinned: false,
          audience: "all",
          readBy: [],
        },
      ];
      setAnnouncements(sampleAnnouncements);
    }

    // Initialize sample employee onboarding checklists
    if (
      (employeeOnboardingChecklists || []).length === 0 &&
      teamMembers &&
      teamMembers.length > 0
    ) {
      const sampleChecklists: EmployeeOnboardingChecklist[] = [];

      // Create checklist for first team member
      const firstEmployee = teamMembers[0];
      if (firstEmployee) {
        const checklist: EmployeeOnboardingChecklist = {
          id: crypto.randomUUID(),
          employeeId: firstEmployee.id,
          employeeName: firstEmployee.name,
          department: firstEmployee.department,
          startDate: new Date().toISOString().split("T")[0],
          status: "in-progress",
          completedPercentage: 25,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.id || "system",
          updatedAt: new Date().toISOString(),
          items: [
            {
              id: crypto.randomUUID(),
              title: "Complete Employment Forms",
              description:
                "Fill out and submit all required employment documentation including tax forms, direct deposit, and emergency contacts",
              category: "documentation",
              required: true,
              dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              completedAt: new Date().toISOString(),
              completedBy: currentUser?.id || "system",
              assignedTo: firstEmployee.id,
            },
            {
              id: crypto.randomUUID(),
              title: "IT Equipment Setup",
              description:
                "Receive laptop, phone, access cards, and other necessary equipment",
              category: "equipment",
              required: true,
              dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              assignedTo: "IT Department",
            },
            {
              id: crypto.randomUUID(),
              title: "System Access Provisioning",
              description:
                "Get credentials and access to email, HRMS, project management tools, and other systems",
              category: "access",
              required: true,
              dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              assignedTo: "IT Department",
            },
            {
              id: crypto.randomUUID(),
              title: "Company Orientation",
              description:
                "Attend company orientation session covering history, culture, values, and policies",
              category: "orientation",
              required: true,
              dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              assignedTo: "HR Team",
            },
            {
              id: crypto.randomUUID(),
              title: "Security & Compliance Training",
              description:
                "Complete mandatory security awareness and compliance training modules",
              category: "compliance",
              required: true,
              dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              assignedTo: firstEmployee.id,
            },
            {
              id: crypto.randomUUID(),
              title: "Department Introduction",
              description:
                "Meet team members, understand department structure, and review responsibilities",
              category: "orientation",
              required: true,
              dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              assignedTo: firstEmployee.department,
            },
            {
              id: crypto.randomUUID(),
              title: "Role-Specific Training",
              description:
                "Complete job-specific training and certification requirements",
              category: "training",
              required: true,
              dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              assignedTo: firstEmployee.id,
            },
            {
              id: crypto.randomUUID(),
              title: "Benefits Enrollment",
              description:
                "Review and enroll in health insurance, retirement plans, and other benefits",
              category: "documentation",
              required: false,
              dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              assignedTo: "HR Team",
            },
          ],
        };
        sampleChecklists.push(checklist);
      }

      setEmployeeOnboardingChecklists(sampleChecklists);
    }

    // Initialize sample shift schedules
    if (
      (shiftSchedules || []).length === 0 &&
      teamMembers &&
      teamMembers.length > 0
    ) {
      const today = new Date();
      const sampleShifts: ShiftSchedule[] = [];

      // Create shifts for next 7 days for team members
      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const shiftDate = new Date(today);
        shiftDate.setDate(today.getDate() + dayOffset);
        const dateStr = shiftDate.toISOString().split("T")[0];

        teamMembers.slice(0, 4).forEach((member, idx) => {
          const shiftTypes: Array<{
            type: "morning" | "afternoon" | "evening" | "night";
            start: string;
            end: string;
          }> = [
            { type: "morning", start: "08:00", end: "16:00" },
            { type: "afternoon", start: "12:00", end: "20:00" },
            { type: "evening", start: "16:00", end: "00:00" },
            { type: "night", start: "00:00", end: "08:00" },
          ];

          const shiftType = shiftTypes[idx % shiftTypes.length];

          sampleShifts.push({
            id: crypto.randomUUID(),
            employeeId: member.id,
            employeeName: member.name,
            department: member.department,
            shiftType: shiftType.type,
            startTime: shiftType.start,
            endTime: shiftType.end,
            date: dateStr,
            location: "Main Office",
            status: dayOffset === 0 ? "confirmed" : "scheduled",
            createdAt: new Date().toISOString(),
            createdBy: currentUser?.id || "system",
            updatedAt: new Date().toISOString(),
          });
        });
      }

      setShiftSchedules(sampleShifts);
    }
  }, []);

  // Live clock update
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(
        new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Automatic checkout after shift hours
  useEffect(() => {
    const checkAutoCheckout = () => {
      const today = new Date().toISOString().split("T")[0];
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinutes;

      // Get today's attendance records that are still checked in
      const todayRecords = (attendanceRecords || []).filter(
        (record) => record.date === today && !record.checkOut,
      );

      if (todayRecords.length === 0) return;

      // Check each employee's shift schedule
      todayRecords.forEach((record) => {
        // Find the employee's shift schedule for today
        const employeeShift = (shiftSchedules || []).find(
          (shift) =>
            shift.employeeId === currentUser?.id &&
            shift.date === today &&
            shift.status !== "cancelled",
        );

        if (employeeShift) {
          // Parse shift end time
          const [endHour, endMinutes] = employeeShift.endTime
            .split(":")
            .map(Number);
          const shiftEndTimeInMinutes = endHour * 60 + endMinutes;

          // Check if current time is past shift end time (with 30 min grace period)
          const gracePeriodMinutes = 30;
          if (
            currentTimeInMinutes >=
            shiftEndTimeInMinutes + gracePeriodMinutes
          ) {
            // Auto checkout
            const checkoutTime = new Date().toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });

            const updatedRecords = (attendanceRecords || []).map((r) =>
              r.id === record.id ? { ...r, checkOut: checkoutTime } : r,
            );

            setAttendanceRecords(updatedRecords);

            toast.info(
              `🕐 Auto checked-out at ${checkoutTime} (shift ended at ${employeeShift.endTime})`,
              { duration: 5000 },
            );
          }
        } else {
          // No shift schedule found, use default 8-hour shift (9 AM - 5 PM)
          // Auto checkout after 5:30 PM (17:30 + 30 min grace)
          const defaultShiftEndMinutes = 17 * 60 + 30; // 5:30 PM

          if (currentTimeInMinutes >= defaultShiftEndMinutes) {
            const checkoutTime = new Date().toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });

            const updatedRecords = (attendanceRecords || []).map((r) =>
              r.id === record.id ? { ...r, checkOut: checkoutTime } : r,
            );

            setAttendanceRecords(updatedRecords);

            toast.info(
              `🕐 Auto checked-out at ${checkoutTime} (default shift ended)`,
              { duration: 5000 },
            );
          }
        }
      });
    };

    // Check every hour
    const interval = setInterval(checkAutoCheckout, 60 * 60 * 1000);

    // Also check immediately on mount
    checkAutoCheckout();

    return () => clearInterval(interval);
  }, [attendanceRecords, shiftSchedules, currentUser]);

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Multi-channel notification function

  // ==================== Geofencing Utility Functions ====================

  // Calculate distance between two coordinates using Haversine formula

  // Check if a position is within any geofence zone

  // Handle automatic check-in when entering a geofence zone

  // Handle automatic check-out when leaving a geofence zone

  // Check location and trigger geofence actions

  // Request location permission

  // Toggle geofencing on/off

  // ==================== End Geofencing Utility Functions ====================

  // ==================== Holiday Management Functions ====================

  const handleAddHoliday = () => {
    setEditingHoliday(null);
    setHolidayFormData({
      name: "",
      date: "",
      type: "public",
      description: "",
      recurring: false,
    });
    setHolidayDialogOpen(true);
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setHolidayFormData({
      name: holiday.name,
      date: holiday.date,
      type: holiday.type,
      description: holiday.description || "",
      recurring: holiday.recurring,
    });
    setHolidayDialogOpen(true);
  };

  const handleSaveHoliday = () => {
    if (!holidayFormData.name || !holidayFormData.date) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (editingHoliday) {
      // Update existing holiday
      const updatedHolidays = holidays.map((h) =>
        h.id === editingHoliday.id
          ? {
              ...h,
              name: holidayFormData.name,
              date: holidayFormData.date,
              type: holidayFormData.type,
              description: holidayFormData.description,
              recurring: holidayFormData.recurring,
              updatedAt: new Date().toISOString(),
            }
          : h,
      );
      setHolidays(updatedHolidays);
      toast.success("Holiday updated successfully");
    } else {
      // Add new holiday
      const newHoliday: Holiday = {
        id: crypto.randomUUID(),
        name: holidayFormData.name,
        date: holidayFormData.date,
        type: holidayFormData.type,
        description: holidayFormData.description,
        recurring: holidayFormData.recurring,
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.id || "system",
      };
      setHolidays([...holidays, newHoliday]);
      toast.success("Holiday added successfully");
    }

    setHolidayDialogOpen(false);
    setEditingHoliday(null);
  };

  const handleDeleteHoliday = (id: string) => {
    setDeleteHolidayId(id);
  };

  const confirmDeleteHoliday = () => {
    if (deleteHolidayId) {
      setHolidays(holidays.filter((h) => h.id !== deleteHolidayId));
      toast.success("Holiday deleted successfully");
      setDeleteHolidayId(null);
    }
  };

  // ==================== End Holiday Management Functions ====================

  // ==================== Shift Management Functions ====================

  const [selectedShiftDate, setSelectedShiftDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [showNewShiftDialog, setShowNewShiftDialog] = useState(false);
  const [showShiftDetailsDialog, setShowShiftDetailsDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState<ShiftSchedule | null>(
    null,
  );
  const [newShift, setNewShift] = useState<Partial<ShiftSchedule>>({
    shiftType: "morning",
    startTime: "08:00",
    endTime: "16:00",
    location: "Main Office",
    status: "scheduled",
  });
  const [showSwapRequestDialog, setShowSwapRequestDialog] = useState(false);
  const [swapRequest, setSwapRequest] = useState<Partial<ShiftSwapRequest>>({});

  const handleCreateShift = () => {
    if (
      !newShift.employeeId ||
      !newShift.date ||
      !newShift.startTime ||
      !newShift.endTime
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const employee = teamMembers.find((m) => m.id === newShift.employeeId);
    if (!employee) {
      toast.error("Employee not found");
      return;
    }

    const shift: ShiftSchedule = {
      id: crypto.randomUUID(),
      employeeId: newShift.employeeId,
      employeeName: employee.name,
      department: employee.department,
      shiftType: newShift.shiftType || "custom",
      startTime: newShift.startTime || "",
      endTime: newShift.endTime || "",
      date: newShift.date || "",
      location: newShift.location || "Main Office",
      notes: newShift.notes,
      status: "scheduled",
      createdAt: new Date().toISOString(),
      createdBy: currentUser?.id || "system",
      updatedAt: new Date().toISOString(),
    };

    setShiftSchedules([...shiftSchedules, shift]);
    toast.success(`Shift created for ${employee.name}`);
    setShowNewShiftDialog(false);
    setNewShift({
      shiftType: "morning",
      startTime: "08:00",
      endTime: "16:00",
      location: "Main Office",
      status: "scheduled",
    });
  };

  const handleUpdateShiftStatus = (
    shiftId: string,
    status: ShiftSchedule["status"],
  ) => {
    const updated = shiftSchedules.map((shift) =>
      shift.id === shiftId
        ? { ...shift, status, updatedAt: new Date().toISOString() }
        : shift,
    );
    setShiftSchedules(updated);
    toast.success(`Shift ${status}`);
  };

  const handleDeleteShift = (shiftId: string) => {
    const shift = shiftSchedules.find((s) => s.id === shiftId);
    if (shift) {
      setShiftSchedules(shiftSchedules.filter((s) => s.id !== shiftId));
      toast.success(`Shift deleted for ${shift.employeeName}`);
    }
  };

  const handleRequestShiftSwap = () => {
    if (
      !swapRequest.requesterId ||
      !swapRequest.requesterShiftId ||
      !swapRequest.reason
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const request: ShiftSwapRequest = {
      id: crypto.randomUUID(),
      requesterId: swapRequest.requesterId,
      requesterName: swapRequest.requesterName || "",
      requesterShiftId: swapRequest.requesterShiftId,
      targetEmployeeId: swapRequest.targetEmployeeId,
      targetEmployeeName: swapRequest.targetEmployeeName,
      targetShiftId: swapRequest.targetShiftId,
      reason: swapRequest.reason,
      status: "pending",
      requestedAt: new Date().toISOString(),
    };

    setShiftSwapRequests([...shiftSwapRequests, request]);
    toast.success("Shift swap request submitted");
    setShowSwapRequestDialog(false);
    setSwapRequest({});
  };

  const handleApproveSwapRequest = (requestId: string) => {
    const request = shiftSwapRequests.find((r) => r.id === requestId);
    if (!request) return;

    // Update request status
    const updatedRequests = shiftSwapRequests.map((r) =>
      r.id === requestId
        ? {
            ...r,
            status: "approved" as const,
            reviewedBy: currentUser?.id,
            reviewedAt: new Date().toISOString(),
          }
        : r,
    );
    setShiftSwapRequests(updatedRequests);

    // Swap the shifts if target shift is specified
    if (request.targetShiftId) {
      const updatedShifts = shiftSchedules.map((shift) => {
        if (shift.id === request.requesterShiftId) {
          return { ...shift, swapRequestId: requestId };
        }
        if (shift.id === request.targetShiftId) {
          return { ...shift, swapRequestId: requestId };
        }
        return shift;
      });
      setShiftSchedules(updatedShifts);
    }

    toast.success("Shift swap approved");
  };

  const handleRejectSwapRequest = (requestId: string, notes: string) => {
    const updatedRequests = shiftSwapRequests.map((r) =>
      r.id === requestId
        ? {
            ...r,
            status: "rejected" as const,
            reviewedBy: currentUser?.id,
            reviewedAt: new Date().toISOString(),
            reviewNotes: notes,
          }
        : r,
    );
    setShiftSwapRequests(updatedRequests);
    toast.success("Shift swap request rejected");
  };

  // ==================== End Shift Management Functions ====================

  // ==================== Employee Offboarding Functions ====================

  const createDefaultOffboardingChecklist = (): OffboardingChecklistItem[] => [
    {
      id: crypto.randomUUID(),
      title: "Revoke System Access",
      description:
        "Disable all system accounts, email, VPN, and application access",
      category: "access-revocation",
      required: true,
    },
    {
      id: crypto.randomUUID(),
      title: "Revoke Building Access",
      description:
        "Deactivate key cards, badges, and physical access permissions",
      category: "access-revocation",
      required: true,
    },
    {
      id: crypto.randomUUID(),
      title: "Collect Company Assets",
      description:
        "Collect laptop, phone, keys, ID badge, and other company property",
      category: "asset-return",
      required: true,
    },
    {
      id: crypto.randomUUID(),
      title: "Knowledge Transfer Sessions",
      description: "Document and transfer critical knowledge to team members",
      category: "knowledge-transfer",
      required: true,
    },
    {
      id: crypto.randomUUID(),
      title: "Conduct Exit Interview",
      description: "Schedule and complete exit interview with HR",
      category: "exit-interview",
      required: true,
    },
    {
      id: crypto.randomUUID(),
      title: "Process Final Settlement",
      description: "Calculate and process final salary, benefits, and dues",
      category: "clearance",
      required: true,
    },
    {
      id: crypto.randomUUID(),
      title: "Obtain Clearance Certificate",
      description: "Get all department clearances and signed documents",
      category: "clearance",
      required: true,
    },
    {
      id: crypto.randomUUID(),
      title: "Archive Employee Records",
      description:
        "Archive all employee documents and records per compliance requirements",
      category: "documentation",
      required: true,
    },
  ];

  const handleInitiateOffboarding = (
    user: User,
    lastWorkingDay: string,
    reason: string,
    reasonDetails?: string,
  ) => {
    if (!currentUser) return;

    const newOffboarding: EmployeeOffboarding = {
      id: crypto.randomUUID(),
      employeeId: user.id,
      employeeName: user.name,
      employeeEmail: user.email,
      department: user.department,
      position: user.role,
      lastWorkingDay,
      resignationDate: new Date().toISOString().split("T")[0],
      reason: reason as any,
      reasonDetails,
      exitInterviewCompleted: false,
      checklist: createDefaultOffboardingChecklist(),
      status: "initiated",
      completedPercentage: 0,
      initiatedBy: currentUser.id,
      initiatedByName: currentUser.name,
      initiatedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      accessRevoked: false,
      assetsReturned: false,
    };

    setEmployeeOffboardings([...employeeOffboardings, newOffboarding]);
    toast.success(`Offboarding initiated for ${user.name}`);
  };

  const handleUpdateOffboardingChecklistItem = (
    offboardingId: string,
    itemId: string,
    completed: boolean,
    notes?: string,
  ) => {
    if (!currentUser) return;

    const updatedOffboardings = employeeOffboardings.map((offboarding) => {
      if (offboarding.id === offboardingId) {
        const updatedChecklist = offboarding.checklist.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              completedAt: completed ? new Date().toISOString() : undefined,
              completedBy: completed ? currentUser.id : undefined,
              completedByName: completed ? currentUser.name : undefined,
              notes,
            };
          }
          return item;
        });

        const completedCount = updatedChecklist.filter(
          (item) => item.completedAt,
        ).length;
        const completedPercentage = Math.round(
          (completedCount / updatedChecklist.length) * 100,
        );
        const allCompleted = completedCount === updatedChecklist.length;

        return {
          ...offboarding,
          checklist: updatedChecklist,
          completedPercentage,
          status: allCompleted
            ? "completed"
            : offboarding.status === "initiated"
              ? "in-progress"
              : offboarding.status,
          completedAt: allCompleted
            ? new Date().toISOString()
            : offboarding.completedAt,
          updatedAt: new Date().toISOString(),
        };
      }
      return offboarding;
    });

    setEmployeeOffboardings(updatedOffboardings);
    toast.success(
      completed ? "Task marked as completed" : "Task marked as incomplete",
    );
  };

  const handleCompleteExitInterview = (
    offboardingId: string,
    exitInterviewNotes: string,
  ) => {
    if (!currentUser) return;

    const updatedOffboardings = employeeOffboardings.map((offboarding) => {
      if (offboarding.id === offboardingId) {
        return {
          ...offboarding,
          exitInterviewCompleted: true,
          exitInterviewDate: new Date().toISOString().split("T")[0],
          exitInterviewNotes,
          exitInterviewConductedBy: currentUser.id,
          exitInterviewConductedByName: currentUser.name,
          updatedAt: new Date().toISOString(),
        };
      }
      return offboarding;
    });

    setEmployeeOffboardings(updatedOffboardings);
    toast.success("Exit interview recorded");
  };

  const handleRevokeAccess = (offboardingId: string) => {
    const updatedOffboardings = employeeOffboardings.map((offboarding) => {
      if (offboarding.id === offboardingId) {
        return {
          ...offboarding,
          accessRevoked: true,
          accessRevokedDate: new Date().toISOString().split("T")[0],
          updatedAt: new Date().toISOString(),
        };
      }
      return offboarding;
    });

    setEmployeeOffboardings(updatedOffboardings);
    toast.success("Employee access revoked");
  };

  const handleMarkAssetsReturned = (offboardingId: string) => {
    const updatedOffboardings = employeeOffboardings.map((offboarding) => {
      if (offboarding.id === offboardingId) {
        return {
          ...offboarding,
          assetsReturned: true,
          assetsReturnedDate: new Date().toISOString().split("T")[0],
          updatedAt: new Date().toISOString(),
        };
      }
      return offboarding;
    });

    setEmployeeOffboardings(updatedOffboardings);
    toast.success("Assets marked as returned");
  };

  const handleProcessFinalSettlement = (
    offboardingId: string,
    amount: number,
  ) => {
    const updatedOffboardings = employeeOffboardings.map((offboarding) => {
      if (offboarding.id === offboardingId) {
        return {
          ...offboarding,
          finalSettlementAmount: amount,
          finalSettlementPaid: true,
          finalSettlementDate: new Date().toISOString().split("T")[0],
          updatedAt: new Date().toISOString(),
        };
      }
      return offboarding;
    });

    setEmployeeOffboardings(updatedOffboardings);
    toast.success("Final settlement processed");
  };

  // ==================== End Employee Offboarding Functions ====================

  // ==================== Work From Home (WFH) Functions ====================

  const handleSubmitWFHRequest = (
    startDate: string,
    endDate: string,
    reason: string,
    recurring?: boolean,
    recurringDays?: string[],
  ) => {
    if (!currentUser) return;

    const newRequest: WFHRequest = {
      id: crypto.randomUUID(),
      employeeId: currentUser.id,
      employeeName: currentUser.name,
      employeeDepartment: currentUser.department,
      startDate,
      endDate,
      reason,
      status: "pending",
      requestedAt: new Date().toISOString(),
      recurring,
      recurringDays: recurringDays as any,
    };

    setWfhRequests([...wfhRequests, newRequest]);
    toast.success("WFH request submitted successfully");
  };

  const handleApproveWFHRequest = (requestId: string, notes?: string) => {
    if (!currentUser) return;

    const updatedRequests = wfhRequests.map((request) => {
      if (request.id === requestId) {
        return {
          ...request,
          status: "approved" as const,
          reviewedBy: currentUser.id,
          reviewedByName: currentUser.name,
          reviewedAt: new Date().toISOString(),
          reviewNotes: notes,
        };
      }
      return request;
    });

    setWfhRequests(updatedRequests);
    toast.success("WFH request approved");
  };

  const handleRejectWFHRequest = (requestId: string, notes: string) => {
    if (!currentUser) return;

    const updatedRequests = wfhRequests.map((request) => {
      if (request.id === requestId) {
        return {
          ...request,
          status: "rejected" as const,
          reviewedBy: currentUser.id,
          reviewedByName: currentUser.name,
          reviewedAt: new Date().toISOString(),
          reviewNotes: notes,
        };
      }
      return request;
    });

    setWfhRequests(updatedRequests);
    toast.success("WFH request rejected");
  };

  const handleCancelWFHRequest = (requestId: string) => {
    const updatedRequests = wfhRequests.filter(
      (request) => request.id !== requestId,
    );
    setWfhRequests(updatedRequests);
    toast.success("WFH request cancelled");
  };

  // ==================== End Work From Home (WFH) Functions ====================

  // ==================== Dashboard Customization Functions ====================

  // ==================== End Dashboard Customization Functions ====================

  // ==================== Analytics Data Processing Functions ====================

  // Filter data by date range
  const getDateRangeFilter = () => {
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;

    switch (analyticsDateRange) {
      case "7d":
        return new Date(now.getTime() - 7 * dayMs);
      case "30d":
        return new Date(now.getTime() - 30 * dayMs);
      case "90d":
        return new Date(now.getTime() - 90 * dayMs);
      case "all":
        return new Date(0);
      default:
        return new Date(now.getTime() - 30 * dayMs);
    }
  };

  // Process attendance data for chart
  const getAttendanceChartData = () => {
    const startDate = getDateRangeFilter();
    const filteredRecords = (attendanceRecords || []).filter(
      (record) => new Date(record.date) >= startDate,
    );

    // Group by date
    const dataByDate: Record<
      string,
      { present: number; absent: number; late: number; halfDay: number }
    > = {};

    filteredRecords.forEach((record) => {
      const date = new Date(record.date).toLocaleDateString();
      if (!dataByDate[date]) {
        dataByDate[date] = { present: 0, absent: 0, late: 0, halfDay: 0 };
      }

      if (record.status === "present") dataByDate[date].present += 1;
      else if (record.status === "absent") dataByDate[date].absent += 1;
      else if (record.status === "late") dataByDate[date].late += 1;
      else if (record.status === "half-day") dataByDate[date].halfDay += 1;
    });

    return Object.entries(dataByDate)
      .map(([date, counts]) => ({
        date,
        Present: counts.present,
        Late: counts.late,
        "Half-Day": counts.halfDay,
        Absent: counts.absent,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-30); // Last 30 days max for readability
  };

  // Process leave data for pie chart
  const getLeaveTypeChartData = () => {
    const startDate = getDateRangeFilter();
    const approvedLeaves = (leaveRequests || []).filter(
      (req) =>
        req.status === "approved" && new Date(req.startDate) >= startDate,
    );

    const leaveByType: Record<string, number> = {};
    approvedLeaves.forEach((req) => {
      const type = req.type || "Other";
      leaveByType[type] = (leaveByType[type] || 0) + 1;
    });

    return Object.entries(leaveByType).map(([name, value]) => ({
      name,
      value,
    }));
  };

  // Process timesheet data for hours breakdown
  const getTimesheetHoursData = () => {
    const startDate = getDateRangeFilter();
    const filteredEntries = (timesheetEntries || []).filter(
      (entry) => new Date(entry.date) >= startDate,
    );

    const hoursByProject: Record<string, number> = {};
    filteredEntries.forEach((entry) => {
      const project = entry.project || "Unassigned";
      hoursByProject[project] = (hoursByProject[project] || 0) + entry.hours;
    });

    return Object.entries(hoursByProject)
      .map(([name, hours]) => ({
        name,
        hours: Math.round(hours * 10) / 10,
      }))
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 10); // Top 10 projects
  };

  // Process department comparison data
  const getDepartmentComparisonData = () => {
    const startDate = getDateRangeFilter();
    const filteredAttendance = (attendanceRecords || []).filter(
      (record) => new Date(record.date) >= startDate,
    );

    const deptData: Record<string, { total: number; present: number }> = {};

    filteredAttendance.forEach((record) => {
      const emp = teamMembers.find((m) => m.id === record.employeeId);
      const dept = emp?.department || "Unknown";

      if (!deptData[dept]) {
        deptData[dept] = { total: 0, present: 0 };
      }

      deptData[dept].total += 1;
      if (record.status === "present") {
        deptData[dept].present += 1;
      }
    });

    return Object.entries(deptData).map(([department, data]) => ({
      department,
      attendanceRate:
        data.total > 0 ? Math.round((data.present / data.total) * 100) : 0,
      total: data.total,
    }));
  };

  // Process overtime trend data
  const getOvertimeTrendData = () => {
    const startDate = getDateRangeFilter();
    const filteredOvertime = (overtimeEntries || []).filter(
      (entry) => new Date(entry.date) >= startDate,
    );

    const overtimeByDate: Record<string, number> = {};
    filteredOvertime.forEach((entry) => {
      const date = new Date(entry.date).toLocaleDateString();
      overtimeByDate[date] = (overtimeByDate[date] || 0) + (entry.hours || 0);
    });

    return Object.entries(overtimeByDate)
      .map(([date, hours]) => ({
        date,
        hours: Math.round(hours * 10) / 10,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-30);
  };

  // ==================== End Analytics Functions ====================

  // ==================== Manager Delegation Functions ====================

  // Check if user has active delegation authority
  const getActiveDelegations = () => {
    const now = new Date();
    return (managerDelegations || []).filter((delegation) => {
      if (delegation.status !== "active") return false;
      const start = new Date(delegation.startDate);
      const end = new Date(delegation.endDate);
      return now >= start && now <= end;
    });
  };

  // Check if current user is delegated to approve
  const canApproveAsDelegate = (
    managerId: string,
    permission: string,
  ): boolean => {
    if (!currentUser) return false;
    const activeDelegations = getActiveDelegations();
    return activeDelegations.some(
      (d) =>
        d.managerId === managerId &&
        d.delegateId === currentUser.id &&
        d.permissions.includes(permission as any),
    );
  };

  // Get managers that current user is delegated for
  const getDelegatedManagers = (): string[] => {
    if (!currentUser) return [];
    const activeDelegations = getActiveDelegations();
    return activeDelegations
      .filter((d) => d.delegateId === currentUser.id)
      .map((d) => d.managerId);
  };

  const handleDelegationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !delegateUserId ||
      !delegationStartDate ||
      !delegationEndDate ||
      !delegationReason
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const startDate = new Date(delegationStartDate);
    const endDate = new Date(delegationEndDate);

    if (endDate < startDate) {
      toast.error("End date must be after start date");
      return;
    }

    const delegateUser = teamMembers.find((m) => m.id === delegateUserId);
    if (!delegateUser) {
      toast.error("Selected delegate not found");
      return;
    }

    setIsLoading(true);
    try {
      const newDelegation: ManagerDelegation = {
        id: `delegation-${Date.now()}`,
        managerId: currentUser?.id || "",
        managerName: currentUser?.name || "",
        delegateId: delegateUserId,
        delegateName: delegateUser.name,
        startDate: delegationStartDate,
        endDate: delegationEndDate,
        reason: delegationReason,
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.id || "",
        status: "active",
        permissions: delegationPermissions,
        actionsLog: [],
      };

      setManagerDelegations([newDelegation, ...(managerDelegations || [])]);

      // Create notification for delegate
      const delegateNotification: Notification = {
        id: `notif-${Date.now()}`,
        type: "delegation",
        title: "New Delegation Authority",
        message: `${currentUser?.name} has delegated approval authority to you from ${new Date(delegationStartDate).toLocaleDateString()} to ${new Date(delegationEndDate).toLocaleDateString()}. Permissions: ${delegationPermissions.join(", ")}`,
        timestamp: new Date().toISOString(),
        read: false,
        userId: delegateUserId,
      };
      setNotifications([delegateNotification, ...(notifications || [])]);

      setDelegationDialogOpen(false);
      toast.success(`Delegation created successfully for ${delegateUser.name}`);

      // Reset form
      setDelegateUserId("");
      setDelegationStartDate("");
      setDelegationEndDate("");
      setDelegationReason("");
      setDelegationPermissions(["leave-approval"]);
    } catch (err: any) {
      toast.error(err.message || "Failed to create delegation");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDelegation = (delegationId: string) => {
    const updated = (managerDelegations || []).map((d) =>
      d.id === delegationId ? { ...d, status: "cancelled" as const } : d,
    );
    setManagerDelegations(updated);
    toast.success("Delegation cancelled");
  };

  // Log delegation action
  const logDelegationAction = (
    delegationId: string,
    actionType: string,
    details: string,
  ) => {
    const updated = (managerDelegations || []).map((d) => {
      if (d.id === delegationId) {
        const newAction = {
          actionId: `action-${Date.now()}`,
          actionType,
          performedAt: new Date().toISOString(),
          details,
        };
        return {
          ...d,
          actionsLog: [...(d.actionsLog || []), newAction],
        };
      }
      return d;
    });
    setManagerDelegations(updated);
  };

  // ==================== End Manager Delegation Functions ====================

  const handleCheckIn = async () => {
    setIsLoading(true);

    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const currentTime = new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      if (isCheckedIn) {
        // Check out
        await api.checkOut(today);
        const updatedRecords = (attendanceRecords || []).map((record) => {
          if (record.date === today && !record.checkOut) {
            return { ...record, checkOut: currentTime };
          }
          return record;
        });
        setAttendanceRecords(updatedRecords);
        setIsCheckedIn(false);
        setTotalBreakTime(0); // Reset break time for next day
        toast.success("🏁 Checked out successfully! Have a great day!");
      } else {
        // Check in
        const location =
          geofenceState.isEnabled && geofenceState.currentZoneId
            ? "Geofence Zone"
            : "Office - Main Building";

        await api.checkIn(location, today);

        const newRecord: AttendanceRecord = {
          id: `att-${Date.now()}`,
          date: today,
          checkIn: currentTime,
          status: "present",
          location,
          breakTime: 0,
        };

        setAttendanceRecords([newRecord, ...(attendanceRecords || [])]);
        setIsCheckedIn(true);
        setStreak((current = 0) => current + 1);
        completeOnboardingStep("first-checkin");
        toast.success(
          "🎉 Checked in successfully! Ready to make today amazing!",
        );
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to update attendance");
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== File Attachment Functions ====================

  const handleFileUpload = async (
    file: File,
    setAttachments: React.Dispatch<
      React.SetStateAction<
        Array<{
          id: string;
          name: string;
          type: string;
          size: number;
          data: string;
          uploadedAt: string;
        }>
      >
    >,
    currentAttachments: Array<{
      id: string;
      name: string;
      type: string;
      size: number;
      data: string;
      uploadedAt: string;
    }>,
  ): Promise<void> => {
    // Validate file size (2MB limit)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      toast.error(`File size exceeds 2MB limit. Please choose a smaller file.`);
      return;
    }

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, PNG, and PDF files are allowed");
      return;
    }

    // Check total attachments limit
    if (currentAttachments.length >= 3) {
      toast.error("Maximum 3 attachments allowed per request");
      return;
    }

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1]); // Remove data:image/png;base64, prefix
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const newAttachment = {
        id: `att-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64,
        uploadedAt: new Date().toISOString(),
      };

      setAttachments([...currentAttachments, newAttachment]);
      toast.success(`File "${file.name}" uploaded successfully`);
    } catch (error) {
      toast.error("Failed to upload file. Please try again.");
    }
  };

  const handleRemoveAttachment = (
    attachmentId: string,
    setAttachments: React.Dispatch<
      React.SetStateAction<
        Array<{
          id: string;
          name: string;
          type: string;
          size: number;
          data: string;
          uploadedAt: string;
        }>
      >
    >,
    currentAttachments: Array<{
      id: string;
      name: string;
      type: string;
      size: number;
      data: string;
      uploadedAt: string;
    }>,
  ) => {
    setAttachments(currentAttachments.filter((att) => att.id !== attachmentId));
    toast.success("Attachment removed");
  };

  const handleDownloadAttachment = (attachment: {
    name: string;
    type: string;
    data: string;
  }) => {
    try {
      const link = document.createElement("a");
      link.href = `data:${attachment.type};base64,${attachment.data}`;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download attachment");
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // ==================== End File Attachment Functions ====================

  const handleLeaveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (!leaveStartDate) {
      toast.error("Please select a start date");
      return;
    }
    if (!leaveEndDate) {
      toast.error("Please select an end date");
      return;
    }
    if (leaveEndDate < leaveStartDate) {
      toast.error("End date must be after start date");
      return;
    }
    if (!leaveType) {
      toast.error("Please select a leave type");
      return;
    }
    if (!leaveReason || leaveReason.trim().length < 5) {
      toast.error("Please provide a reason (minimum 5 characters)");
      return;
    }

    setIsLoading(true);
    try {
      // Check for duplicate leave requests
      const startDateStr = leaveStartDate?.toISOString().split("T")[0] || "";
      const endDateStr = leaveEndDate?.toISOString().split("T")[0] || "";

      const existingLeave = (leaveRequests || []).find((lr) => {
        if (lr.employeeName !== userProfile.name) return false;

        // Check for date overlap
        const existingStart = new Date(lr.startDate);
        const existingEnd = new Date(lr.endDate);
        const newStart = new Date(startDateStr);
        const newEnd = new Date(endDateStr);

        // Check if date ranges overlap
        return !(newEnd < existingStart || newStart > existingEnd);
      });

      if (existingLeave) {
        toast.error("You already have a leave request for these dates");
        setIsLoading(false);
        return;
      }

      const newLeave: LeaveRequest = {
        id: `leave-${Date.now()}`,
        employeeName: userProfile.name,
        employeeId: currentUser?.id || "user-1",
        startDate: leaveStartDate?.toISOString().split("T")[0] || "",
        endDate: leaveEndDate?.toISOString().split("T")[0] || "",
        type: leaveType as "vacation" | "sick" | "personal" | "emergency",
        reason: leaveReason,
        status: "pending",
        requestedAt: new Date().toISOString(),
        totalDays:
          leaveStartDate && leaveEndDate
            ? Math.ceil(
                (leaveEndDate.getTime() - leaveStartDate.getTime()) /
                  (1000 * 60 * 60 * 24),
              ) + 1
            : 1,
        attachments: leaveAttachments.length > 0 ? leaveAttachments : undefined,
      };
      setLeaveRequests([newLeave, ...(leaveRequests || [])]);
      setLeaveDialogOpen(false);
      toast.success("Leave request submitted successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to submit leave request");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimesheetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Calculate week start date
      const entryDate = new Date(timesheetDate);
      const day = entryDate.getDay();
      const diff = entryDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
      const weekStart = new Date(entryDate);
      weekStart.setDate(diff);

      const newEntry: TimesheetEntry = {
        id: `ts-${Date.now()}`,
        date: timesheetDate.toISOString().split("T")[0],
        project: timesheetProject,
        task: timesheetTask,
        hours: parseFloat(timesheetHours),
        description: timesheetDescription,
        status: "draft",
        weekStartDate: weekStart.toISOString().split("T")[0],
        attachments:
          timesheetAttachments.length > 0 ? timesheetAttachments : undefined,
      };
      setTimesheetEntries([newEntry, ...(timesheetEntries || [])]);
      resetLogHoursForm();
      setTimesheetDialogOpen(false);
      toast.success("Timesheet entry added successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to add timesheet entry");
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualAttendanceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (!manualEntryDate) {
        toast.error("Please select a date");
        return;
      }

      const formattedDate = manualEntryDate.toISOString().split("T")[0];

      // Check if attendance already exists for this date
      const existingIndex = (attendanceRecords || []).findIndex(
        (r) => r.date === formattedDate,
      );

      const newRecord: AttendanceRecord = {
        id:
          existingIndex >= 0
            ? attendanceRecords[existingIndex].id
            : `att-${Date.now()}`,
        date: formattedDate,
        checkIn: manualCheckInTime,
        checkOut: manualCheckOutTime || undefined,
        status: manualStatus,
        location: manualLocation,
        breakTime: 0,
      };

      let updatedRecords: AttendanceRecord[];
      if (existingIndex >= 0) {
        // Update existing record
        updatedRecords = [...attendanceRecords];
        updatedRecords[existingIndex] = newRecord;
        toast.success("Attendance record updated successfully");
      } else {
        // Add new record
        updatedRecords = [newRecord, ...(attendanceRecords || [])].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        toast.success("Manual attendance entry added successfully");
      }

      setAttendanceRecords(updatedRecords);

      // Create corresponding leave request if status is absence-related
      if (["sick", "vacation", "absent", "half-day"].includes(manualStatus)) {
        const existingLeave = (leaveRequests || []).find(
          (lr) =>
            lr.employeeId === (currentUser?.id || "unknown") &&
            lr.startDate === formattedDate &&
            lr.endDate === formattedDate,
        );

        if (!existingLeave) {
          const newLeaveRequest: LeaveRequest = {
            id: `leave-${Date.now()}`,
            employeeId: currentUser?.id || "unknown",
            employeeName: userProfile?.name || "Unknown",
            type:
              manualStatus === "sick"
                ? "sick"
                : manualStatus === "vacation"
                  ? "vacation"
                  : "personal", // Map half-day and absent to personal
            startDate: formattedDate,
            endDate: formattedDate,
            reason: `Auto-created from attendance marking (${manualStatus})`,
            status: "approved",
            reviewedBy: "System",
            reviewedAt: new Date().toISOString().split("T")[0],
            requestedAt: new Date().toISOString(),
            totalDays: 1,
          };

          const updatedLeaves = [...(leaveRequests || []), newLeaveRequest];
          setLeaveRequests(updatedLeaves);
        }
      }

      setManualEntryDialogOpen(false);

      // Reset form
      setManualEntryDate(undefined);
      setManualCheckInTime("");
      setManualCheckOutTime("");
      setManualStatus("present");
      setManualLocation("Office - Main Building");
    } catch (err: any) {
      toast.error(err.message || "Failed to add manual attendance");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to calculate days between two dates (inclusive)

  // Helper function to check for leave conflicts

  // Helper function to calculate remaining leave balance

  // Handle leave approval by manager

  // Handle leave rejection by manager

  // Handle leave cancellation by employee

  // Helper function to get week start date (Monday)

  // Helper function to get all days in a week

  // Helper function to get week range string

  // Submit timesheet for a week

  // Manager: Approve timesheet

  // Manager: Reject timesheet

  // Delete a timesheet entry (only drafts and rejected)

  const handleBreakToggle = () => {
    if (isOnBreak) {
      // End break
      if (breakStartTime) {
        const breakDuration = Math.floor(
          (Date.now() - new Date(breakStartTime).getTime()) / (1000 * 60),
        );
        setTotalBreakTime((current = 0) => current + breakDuration);
        setBreakStartTime(null);
        toast.success(
          `🏃‍♂️ Break ended! You took a ${breakDuration} minute break.`,
        );
      }
      setIsOnBreak(false);
    } else {
      // Start break
      setBreakStartTime(new Date().toISOString());
      setIsOnBreak(true);
      toast.success("☕ Break started! Take your time.");
    }
  };

  const completeOnboardingStep = (stepId: string) => {
    setOnboardingState(
      (
        current = {
          isFirstTime: true,
          currentStep: 0,
          steps: [],
          showTooltip: false,
          completedSteps: [],
        },
      ) => ({
        ...current,
        completedSteps: [...current.completedSteps, stepId],
        steps: current.steps.map((step) =>
          step.id === stepId ? { ...step, completed: true } : step,
        ),
      }),
    );
  };

  const nextOnboardingStep = () => {
    setOnboardingState(
      (
        current = {
          isFirstTime: true,
          currentStep: 0,
          steps: [],
          showTooltip: false,
          completedSteps: [],
        },
      ) => ({
        ...current,
        currentStep: Math.min(
          current.currentStep + 1,
          current.steps.length - 1,
        ),
      }),
    );
  };

  const skipOnboarding = () => {
    setOnboardingState(
      (
        current = {
          isFirstTime: true,
          currentStep: 0,
          steps: [],
          showTooltip: false,
          completedSteps: [],
        },
      ) => ({
        ...current,
        isFirstTime: false,
        currentStep: current.steps.length - 1,
      }),
    );
    setOnboardingDialogOpen(false);
    toast.success(
      "You can access the onboarding tour anytime from the help menu!",
    );
  };

  const restartOnboarding = () => {
    setOnboardingState({
      isFirstTime: true,
      currentStep: 0,
      steps: onboardingState?.steps || [],
      showTooltip: false,
      completedSteps: [],
    });
    setOnboardingDialogOpen(true);
  };

  const openTimesheetDialog = () => {
    // Reset form state BEFORE opening dialog
    resetLogHoursForm();

    // Then open the dialog
    setTimesheetDialogOpen(true);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only trigger if not typing in an input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        // Allow Cmd/Ctrl+K even in inputs to open search
        if ((event.metaKey || event.ctrlKey) && event.key === "k") {
          event.preventDefault();
          setSearchOpen(true);
          return;
        }
        return;
      }

      // Cmd/Ctrl + K for search
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        setSearchOpen(true);
        return;
      }

      // Alt/Option + key combinations
      if (event.altKey) {
        switch (event.key.toLowerCase()) {
          case "c":
            event.preventDefault();
            handleCheckIn();
            break;
          case "l":
            event.preventDefault();
            setLeaveDialogOpen(true);
            break;
          case "t":
            event.preventDefault();
            openTimesheetDialog();
            break;
          case "g":
            event.preventDefault();
            setGoalDialogOpen(true);
            break;
          case "b":
            event.preventDefault();
            if (isCheckedIn) {
              handleBreakToggle();
            }
            break;
          case "n":
            event.preventDefault();
            setNotificationDialogOpen(true);
            break;
          case "p":
            event.preventDefault();
            setProfileDialogOpen(true);
            break;
          case "h":
            event.preventDefault();
            restartOnboarding();
            break;
        }
      }

      // Number keys for tab navigation
      if (event.key >= "1" && event.key <= "6" && event.altKey) {
        event.preventDefault();
        const tabs = [
          "dashboard",
          "attendance",
          "leaves",
          "timesheet",
          "employees",
          "reports",
        ];
        const tabIndex = parseInt(event.key) - 1;
        if (tabs[tabIndex]) {
          setActiveSection(tabs[tabIndex]);
        }
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [
    isCheckedIn,
    handleCheckIn,
    handleBreakToggle,
    openTimesheetDialog,
    setLeaveDialogOpen,
    setGoalDialogOpen,
    setNotificationDialogOpen,
    setProfileDialogOpen,
    restartOnboarding,
    setActiveSection,
  ]);

  // Session Timeout Logic - Auto logout after inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    // Update last activity on user interactions
    const updateActivity = () => {
      setLastActivityTime(Date.now());
      setSessionTimeoutDialogOpen(false);
    };

    // Events that reset the activity timer
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    activityEvents.forEach((event) => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Check for timeout every minute
    const timeoutCheck = setInterval(() => {
      const now = Date.now();
      const inactiveMinutes = (now - lastActivityTime) / (1000 * 60);

      // Show warning dialog before timeout
      if (
        inactiveMinutes >= SESSION_TIMEOUT_MINUTES - SESSION_WARNING_MINUTES &&
        !sessionTimeoutDialogOpen
      ) {
        setSessionTimeoutDialogOpen(true);
      }

      // Auto logout after timeout
      if (inactiveMinutes >= SESSION_TIMEOUT_MINUTES) {
        toast.info("Session expired due to inactivity. Please log in again.");
        handleLogout();
      }
    }, 60000); // Check every minute

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, updateActivity);
      });
      clearInterval(timeoutCheck);
    };
  }, [isAuthenticated, lastActivityTime, handleLogout]);

  // US2 Fix: Sync leave requests to localStorage for persistence across page refreshes
  useEffect(() => {
    if (leaveRequests && leaveRequests.length > 0) {
      try {
        localStorage.setItem(
          "leaveRequests_backup",
          JSON.stringify(leaveRequests),
        );
      } catch (err) {
        console.warn("Failed to sync leave requests to localStorage", err);
      }
    }
  }, [leaveRequests]);

  // US2 Fix: Restore leave requests from localStorage on app init if they exist
  useEffect(() => {
    if (isAuthenticated && (!leaveRequests || leaveRequests.length === 0)) {
      try {
        const backup = localStorage.getItem("leaveRequests_backup");
        if (backup) {
          const parsed = JSON.parse(backup);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setLeaveRequests(parsed);
          }
        }
      } catch (err) {
        console.warn("Failed to restore leave requests from localStorage", err);
      }
    }
  }, [isAuthenticated]);

  // US2 Fix: Reset timesheet form BEFORE opening dialog (prevents flash of stale data)

  const totalHoursThisWeek = (timesheetEntries || [])
    .filter((entry) => {
      const entryDate = new Date(entry.date);
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      return entryDate >= weekStart;
    })
    .reduce((sum, entry) => sum + entry.hours, 0);

  const unreadNotifications = (notifications || []).filter(
    (notif) => !notif.read,
  ).length;
  const currentWeekGoals = (weeklyGoals || []).filter((goal) => {
    const goalWeek = new Date(goal.week);
    const currentWeek = new Date();
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay());
    return goalWeek >= currentWeek;
  });

  // Calculate attendance rate (percentage of days checked in this month)

  // Check for reset token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("resetToken");
    if (token) {
      setResetToken(token);
      setShowResetPassword(true);
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Render logic
  const renderAuthScreen = () => {
    // Show reset password if token is present
    if (showResetPassword && resetToken) {
      return (
        <ResetPassword
          token={resetToken}
          users={employeeProfiles.map((emp) => ({
            id: emp.id,
            name: emp.name,
            email: emp.email,
            password: emp.password,
            role: emp.role,
            department: emp.department,
            avatar: emp.avatar,
            joinedAt: emp.createdAt,
            phone: emp.phone,
          }))}
          resetTokens={passwordResetTokens}
          onPasswordReset={(updatedUsers, updatedTokens) => {
            setEmployeeProfiles(
              updatedUsers.map((user) => ({
                id: user.id,
                name: user.name,
                email: user.email,
                password: user.password,
                role: user.role,
                department: user.department,
                avatar: user.avatar,
                phone: user.phone || "",
                createdAt: user.joinedAt,
                skills: [],
                projects: [],
              })),
            );
            setPasswordResetTokens(updatedTokens);
            setShowResetPassword(false);
            setResetToken("");
          }}
          onBackToLogin={() => {
            setShowResetPassword(false);
            setResetToken("");
          }}
        />
      );
    }

    // Show forgot password
    if (showForgotPassword) {
      return (
        <ForgotPassword
          users={employeeProfiles.map((emp) => ({
            id: emp.id,
            email: emp.email,
            name: emp.name,
          }))}
          resetTokens={passwordResetTokens}
          onResetTokenCreate={setPasswordResetTokens}
          onBackToLogin={() => setShowForgotPassword(false)}
        />
      );
    }

    // Default: show login
    return (
      <Login
        onLogin={handleLogin}
        onForgotPasswordClick={() => setShowForgotPassword(true)}
      />
    );
  };

  // Memoized search filters for performance optimization (US1 - Search Fix)
  // Must run before the auth early-return below: hooks can't be called conditionally.
  const filteredMembers = useMemo(
    () =>
      (teamMembers || []).filter((member) => {
        const search = searchValue.trim().toLowerCase();
        if (!search) return true; // Show all if empty
        return (
          member.name.toLowerCase().includes(search) ||
          member.email.toLowerCase().includes(search) ||
          member.department.toLowerCase().includes(search)
        );
      }),
    [teamMembers, searchValue],
  );

  const filteredLeaves = useMemo(
    () =>
      (leaveRequests || []).filter((leave) => {
        const search = searchValue.trim().toLowerCase();
        if (!search) return true;
        return (
          leave.employeeName.toLowerCase().includes(search) ||
          leave.type.toLowerCase().includes(search) ||
          leave.status.toLowerCase().includes(search) ||
          leave.startDate.includes(search) ||
          leave.endDate.includes(search)
        );
      }),
    [leaveRequests, searchValue],
  );

  const filteredTimesheets = useMemo(
    () =>
      (timesheetEntries || []).filter((entry) => {
        const search = searchValue.trim().toLowerCase();
        if (!search) return true;
        return (
          entry.project.toLowerCase().includes(search) ||
          entry.task.toLowerCase().includes(search) ||
          entry.description.toLowerCase().includes(search) ||
          entry.date.includes(search)
        );
      }),
    [timesheetEntries, searchValue],
  );

  if (!isAuthenticated) {
    return renderAuthScreen();
  }

  return (
    <>
      {activeSection === "dashboard" && <DashboardView />}

      {/* Core HR Section */}
      {activeSection === "core-hr" && <CoreHRDashboard />}

      {/* LMS Section */}
      {activeSection === "lms" && <LMSDashboard />}

      {/* Performance Section */}
      {activeSection === "performance" && <PerformanceDashboard />}

      {/* Recruitment Section */}
      {activeSection === "recruitment" && <RecruitmentDashboard />}

      {/* Lifecycle Section */}
      {activeSection === "lifecycle" && <LifecycleDashboard />}

      {/* Payroll Section */}
      {activeSection === "payroll" && <PayrollDashboard />}

      {/* Analytics Section */}
      {activeSection === "analytics" && <AnalyticsDashboard />}

      {/* Announcements Section */}
      {activeSection === "announcements" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5" />
                    Announcements & Notice Board
                  </CardTitle>
                  <CardDescription>
                    Stay updated with important company announcements
                  </CardDescription>
                </div>
                {(currentUser?.role === "admin" ||
                  currentUser?.role === "superadmin" ||
                  currentUser?.role === "hr") && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="w-4 h-4 mr-2" />
                        New Announcement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Create Announcement</DialogTitle>
                        <DialogDescription>
                          Post a new announcement to the notice board
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const formData = new FormData(e.currentTarget);
                          const newAnnouncement: Announcement = {
                            id: crypto.randomUUID(),
                            title: formData.get("title") as string,
                            content: formData.get("content") as string,
                            type: formData.get("type") as
                              | "urgent"
                              | "info"
                              | "warning"
                              | "success",
                            createdBy: currentUser?.id || "system",
                            createdByName: currentUser?.name || "Admin",
                            createdAt: new Date().toISOString(),
                            expiresAt: formData.get("expiresAt")
                              ? new Date(
                                  formData.get("expiresAt") as string,
                                ).toISOString()
                              : undefined,
                            isPinned: formData.get("isPinned") === "on",
                            audience: formData.get("audience") as
                              | "all"
                              | "managers"
                              | "hr"
                              | "employees",
                            readBy: [],
                          };
                          setAnnouncements([newAnnouncement, ...announcements]);
                          toast.success("Announcement posted successfully! 📢");
                          (e.target as HTMLFormElement).reset();
                        }}
                        className="space-y-4"
                      >
                        <div className="space-y-2">
                          <label
                            htmlFor="title"
                            className="text-sm font-medium"
                          >
                            Title
                          </label>
                          <Input
                            id="title"
                            name="title"
                            placeholder="Announcement title"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label
                            htmlFor="content"
                            className="text-sm font-medium"
                          >
                            Content
                          </label>
                          <textarea
                            id="content"
                            name="content"
                            className="w-full min-h-[120px] p-3 border rounded-lg resize-none"
                            placeholder="Write your announcement..."
                            required
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label
                              htmlFor="type"
                              className="text-sm font-medium"
                            >
                              Type
                            </label>
                            <select
                              id="type"
                              name="type"
                              className="w-full p-2 border rounded-lg"
                              required
                            >
                              <option value="info">Info</option>
                              <option value="success">Success</option>
                              <option value="warning">Warning</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label
                              htmlFor="audience"
                              className="text-sm font-medium"
                            >
                              Audience
                            </label>
                            <select
                              id="audience"
                              name="audience"
                              className="w-full p-2 border rounded-lg"
                              required
                            >
                              <option value="all">All Employees</option>
                              <option value="managers">Managers Only</option>
                              <option value="hr">HR Only</option>
                              <option value="employees">Employees Only</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label
                              htmlFor="expiresAt"
                              className="text-sm font-medium"
                            >
                              Expires At (Optional)
                            </label>
                            <Input
                              id="expiresAt"
                              name="expiresAt"
                              type="date"
                            />
                          </div>
                          <div className="flex items-center space-x-2 pt-6">
                            <input
                              type="checkbox"
                              id="isPinned"
                              name="isPinned"
                              className="w-4 h-4"
                            />
                            <label
                              htmlFor="isPinned"
                              className="text-sm font-medium"
                            >
                              Pin to top
                            </label>
                          </div>
                        </div>
                        <div className="flex gap-2 pt-4">
                          <Button type="submit" className="flex-1">
                            Post Announcement
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <div className="text-center py-12">
                  <Megaphone className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No announcements yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {announcements
                    .filter((ann) => {
                      // Filter by audience
                      if (ann.audience === "all") return true;
                      if (
                        ann.audience === "managers" &&
                        (currentUser?.role === "manager" ||
                          currentUser?.role === "admin" ||
                          currentUser?.role === "superadmin")
                      )
                        return true;
                      if (
                        ann.audience === "hr" &&
                        (currentUser?.role === "hr" ||
                          currentUser?.role === "admin" ||
                          currentUser?.role === "superadmin")
                      )
                        return true;
                      if (
                        ann.audience === "employees" &&
                        currentUser?.role === "employee"
                      )
                        return true;
                      return false;
                    })
                    .filter((ann) => {
                      // Filter expired announcements
                      if (!ann.expiresAt) return true;
                      return new Date(ann.expiresAt) > new Date();
                    })
                    .sort((a, b) => {
                      // Sort: pinned first, then by date
                      if (a.isPinned && !b.isPinned) return -1;
                      if (!a.isPinned && b.isPinned) return 1;
                      return (
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime()
                      );
                    })
                    .map((announcement) => {
                      const typeColors = {
                        urgent:
                          "border-[var(--destructive)] bg-[var(--destructive)_/_0.05]",
                        warning:
                          "border-[var(--warning)] bg-[var(--warning)_/_0.05]",
                        success:
                          "border-[var(--success)] bg-[var(--success)_/_0.05]",
                        info: "border-[var(--info)] bg-[var(--info)_/_0.05]",
                      };
                      const typeBadgeColors = {
                        urgent:
                          "bg-[var(--destructive)_/_0.1] text-[var(--destructive)]",
                        warning:
                          "bg-[var(--warning)_/_0.1] text-[var(--warning)]",
                        success:
                          "bg-[var(--success)_/_0.1] text-[var(--success)]",
                        info: "bg-[var(--info)_/_0.1] text-[var(--info)]",
                      };
                      return (
                        <Card
                          key={announcement.id}
                          className={`${typeColors[announcement.type]} border-2`}
                        >
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  {announcement.isPinned && (
                                    <Star
                                      className="w-4 h-4 text-amber-600"
                                      weight="fill"
                                    />
                                  )}
                                  <CardTitle className="text-lg">
                                    {announcement.title}
                                  </CardTitle>
                                  <Badge
                                    className={
                                      typeBadgeColors[announcement.type]
                                    }
                                  >
                                    {announcement.type}
                                  </Badge>
                                </div>
                                <CardDescription>
                                  Posted by {announcement.createdByName} •{" "}
                                  {new Date(
                                    announcement.createdAt,
                                  ).toLocaleDateString()}
                                  {announcement.expiresAt && (
                                    <>
                                      {" "}
                                      • Expires{" "}
                                      {new Date(
                                        announcement.expiresAt,
                                      ).toLocaleDateString()}
                                    </>
                                  )}
                                </CardDescription>
                              </div>
                              {(currentUser?.role === "admin" ||
                                currentUser?.role === "superadmin" ||
                                currentUser?.role === "hr") && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (confirm("Delete this announcement?")) {
                                      setAnnouncements(
                                        announcements.filter(
                                          (a) => a.id !== announcement.id,
                                        ),
                                      );
                                      toast.success("Announcement deleted");
                                    }
                                  }}
                                >
                                  <Trash className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent>
                            <p className="text-slate-700 whitespace-pre-wrap">
                              {announcement.content}
                            </p>
                            {!announcement.readBy.includes(
                              currentUser?.id || "",
                            ) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-3"
                                onClick={() => {
                                  const updatedAnnouncements =
                                    announcements.map((a) =>
                                      a.id === announcement.id
                                        ? {
                                            ...a,
                                            readBy: [
                                              ...a.readBy,
                                              currentUser?.id || "",
                                            ],
                                          }
                                        : a,
                                    );
                                  setAnnouncements(updatedAnnouncements);
                                  toast.success("Marked as read");
                                }}
                              >
                                <Check className="w-4 h-4 mr-2" />
                                Mark as Read
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Employee Onboarding Checklist Section */}
      {activeSection === "onboarding" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-6 h-6 text-primary" />
                    Employee Onboarding
                  </CardTitle>
                  <CardDescription>
                    Track and manage new employee onboarding tasks
                  </CardDescription>
                </div>
                {(currentUser?.role === "hr" ||
                  currentUser?.role === "admin" ||
                  currentUser?.role === "superadmin") && (
                  <Button
                    onClick={() => setCreateChecklistDialogOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Checklist
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {employeeOnboardingChecklists.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    No Onboarding Checklists
                  </h3>
                  <p className="text-slate-500 mb-4">
                    Create your first onboarding checklist to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {employeeOnboardingChecklists.map((checklist) => {
                    const completedCount = checklist.items.filter(
                      (item) => item.completedAt,
                    ).length;
                    const totalCount = checklist.items.length;
                    const percentage = Math.round(
                      (completedCount / totalCount) * 100,
                    );
                    const statusColors = {
                      "not-started": "bg-slate-100 text-slate-700",
                      "in-progress":
                        "bg-[var(--info)_/_0.1] text-[var(--info)]",
                      completed:
                        "bg-[var(--success)_/_0.1] text-[var(--success)]",
                    };

                    return (
                      <Card
                        key={checklist.id}
                        className="hover:shadow-md transition-shadow cursor-pointer"
                      >
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <CardTitle>{checklist.employeeName}</CardTitle>
                                <Badge
                                  className={statusColors[checklist.status]}
                                >
                                  {checklist.status.replace("-", " ")}
                                </Badge>
                              </div>
                              <CardDescription>
                                {checklist.department} • Started{" "}
                                {new Date(
                                  checklist.startDate,
                                ).toLocaleDateString()}
                              </CardDescription>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-primary">
                                {percentage}%
                              </div>
                              <div className="text-xs text-slate-500">
                                {completedCount}/{totalCount} tasks
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {/* Progress Bar */}
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div
                                className="bg-primary h-2 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>

                            {/* Task Categories Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                              {[
                                "documentation",
                                "equipment",
                                "access",
                                "training",
                                "orientation",
                                "compliance",
                              ].map((category) => {
                                const categoryTasks = checklist.items.filter(
                                  (item) => item.category === category,
                                );
                                const completedCategory = categoryTasks.filter(
                                  (item) => item.completedAt,
                                ).length;
                                if (categoryTasks.length === 0) return null;

                                return (
                                  <div
                                    key={category}
                                    className="flex items-center gap-2 text-slate-600"
                                  >
                                    <span className="capitalize">
                                      {category}:
                                    </span>
                                    <span className="font-medium">
                                      {completedCategory}/{categoryTasks.length}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>

                            {/* View Details Button */}
                            <Button
                              variant="outline"
                              className="w-full mt-3"
                              onClick={() => {
                                setSelectedOnboardingChecklistId(checklist.id);
                                setOnboardingChecklistDialogOpen(true);
                              }}
                            >
                              <List className="w-4 h-4 mr-2" />
                              View Details
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Employee Offboarding Section */}
      {activeSection === "offboarding" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <SignOut className="w-6 h-6 text-primary" />
                    Employee Offboarding
                  </CardTitle>
                  <CardDescription>
                    Manage employee exit processes and offboarding tasks
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {employeeOffboardings.length === 0 ? (
                <div className="text-center py-12">
                  <SignOut className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    No Offboarding Records
                  </h3>
                  <p className="text-slate-500 mb-4">
                    Initiate offboarding from the Team section when an employee
                    leaves
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {employeeOffboardings
                    .sort(
                      (a, b) =>
                        new Date(b.initiatedAt).getTime() -
                        new Date(a.initiatedAt).getTime(),
                    )
                    .map((offboarding) => {
                      const completedCount = offboarding.checklist.filter(
                        (item) => item.completedAt,
                      ).length;
                      const totalCount = offboarding.checklist.length;
                      const percentage = Math.round(
                        (completedCount / totalCount) * 100,
                      );
                      const statusColors = {
                        initiated: "bg-slate-100 text-slate-700",
                        "in-progress":
                          "bg-[var(--info)_/_0.1] text-[var(--info)]",
                        completed:
                          "bg-[var(--success)_/_0.1] text-[var(--success)]",
                      };
                      const reasonLabels = {
                        resignation: "Resignation",
                        retirement: "Retirement",
                        termination: "Termination",
                        "end-of-contract": "End of Contract",
                        other: "Other",
                      };

                      const daysUntilLastDay = Math.ceil(
                        (new Date(offboarding.lastWorkingDay).getTime() -
                          new Date().getTime()) /
                          (1000 * 60 * 60 * 24),
                      );

                      return (
                        <Card
                          key={offboarding.id}
                          className="hover:shadow-md transition-shadow"
                        >
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <CardTitle>
                                    {offboarding.employeeName}
                                  </CardTitle>
                                  <Badge
                                    className={statusColors[offboarding.status]}
                                  >
                                    {offboarding.status.replace("-", " ")}
                                  </Badge>
                                  {daysUntilLastDay > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="bg-orange-50 text-orange-700 border-orange-200"
                                    >
                                      {daysUntilLastDay} days remaining
                                    </Badge>
                                  )}
                                  {daysUntilLastDay === 0 && (
                                    <Badge
                                      variant="outline"
                                      className="bg-[var(--destructive)_/_0.1] text-[var(--destructive)] border-[var(--destructive)]"
                                    >
                                      Last day today
                                    </Badge>
                                  )}
                                </div>
                                <CardDescription>
                                  {offboarding.department} •{" "}
                                  {offboarding.position} •{" "}
                                  {reasonLabels[offboarding.reason]}
                                </CardDescription>
                                <div className="text-sm text-slate-600 mt-2">
                                  Last Working Day:{" "}
                                  {new Date(
                                    offboarding.lastWorkingDay,
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-primary">
                                  {percentage}%
                                </div>
                                <div className="text-xs text-slate-500">
                                  {completedCount}/{totalCount} tasks
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              {/* Progress Bar */}
                              <div className="w-full bg-slate-200 rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>

                              {/* Key Status Indicators */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                  {offboarding.accessRevoked ? (
                                    <Check className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <Warning className="w-5 h-5 text-orange-500" />
                                  )}
                                  <span className="text-sm font-medium">
                                    Access{" "}
                                    {offboarding.accessRevoked
                                      ? "Revoked"
                                      : "Active"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {offboarding.assetsReturned ? (
                                    <Check className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <Warning className="w-5 h-5 text-orange-500" />
                                  )}
                                  <span className="text-sm font-medium">
                                    Assets{" "}
                                    {offboarding.assetsReturned
                                      ? "Returned"
                                      : "Pending"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {offboarding.exitInterviewCompleted ? (
                                    <Check className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <Warning className="w-5 h-5 text-orange-500" />
                                  )}
                                  <span className="text-sm font-medium">
                                    Exit Interview{" "}
                                    {offboarding.exitInterviewCompleted
                                      ? "Done"
                                      : "Pending"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {offboarding.finalSettlementPaid ? (
                                    <Check className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <Warning className="w-5 h-5 text-orange-500" />
                                  )}
                                  <span className="text-sm font-medium">
                                    Settlement{" "}
                                    {offboarding.finalSettlementPaid
                                      ? "Paid"
                                      : "Pending"}
                                  </span>
                                </div>
                              </div>

                              {/* Checklist Items */}
                              <div className="space-y-2">
                                <h4 className="font-medium text-sm text-slate-900 mb-3">
                                  Offboarding Checklist
                                </h4>
                                {offboarding.checklist.map((item) => (
                                  <div
                                    key={item.id}
                                    className="flex items-start gap-3 p-3 bg-white border rounded-lg hover:border-primary/50 transition-colors"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!!item.completedAt}
                                      onChange={(e) =>
                                        handleUpdateOffboardingChecklistItem(
                                          offboarding.id,
                                          item.id,
                                          e.target.checked,
                                        )
                                      }
                                      className="mt-1 h-4 w-4 rounded border-slate-300"
                                      disabled={
                                        offboarding.status === "completed"
                                      }
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p
                                          className={`text-sm font-medium ${item.completedAt ? "line-through text-slate-500" : "text-slate-900"}`}
                                        >
                                          {item.title}
                                        </p>
                                        {item.required && (
                                          <Badge
                                            variant="outline"
                                            className="text-xs"
                                          >
                                            Required
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-slate-500 mt-1">
                                        {item.description}
                                      </p>
                                      {item.completedAt && (
                                        <div className="text-xs text-green-600 mt-2">
                                          ✓ Completed by {item.completedByName}{" "}
                                          on{" "}
                                          {new Date(
                                            item.completedAt,
                                          ).toLocaleDateString()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Quick Actions */}
                              {offboarding.status !== "completed" && (
                                <div className="flex flex-wrap gap-2 pt-4 border-t">
                                  {!offboarding.accessRevoked && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        handleRevokeAccess(offboarding.id)
                                      }
                                    >
                                      <Lock className="w-4 h-4 mr-2" />
                                      Revoke Access
                                    </Button>
                                  )}
                                  {!offboarding.assetsReturned && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        handleMarkAssetsReturned(offboarding.id)
                                      }
                                    >
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                      Mark Assets Returned
                                    </Button>
                                  )}
                                  {!offboarding.exitInterviewCompleted && (
                                    <Dialog>
                                      <DialogTrigger asChild>
                                        <Button variant="outline" size="sm">
                                          <FileText className="w-4 h-4 mr-2" />
                                          Record Exit Interview
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent>
                                        <DialogHeader>
                                          <DialogTitle>
                                            Exit Interview -{" "}
                                            {offboarding.employeeName}
                                          </DialogTitle>
                                          <DialogDescription>
                                            Record notes from the exit interview
                                          </DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-4 py-4">
                                          <div className="space-y-2">
                                            <Label htmlFor="exit-notes">
                                              Interview Notes
                                            </Label>
                                            <textarea
                                              id="exit-notes"
                                              className="w-full min-h-[150px] rounded-md border border-slate-300 p-3"
                                              placeholder="Enter feedback, concerns, and suggestions shared during the exit interview..."
                                              onBlur={(e) => {
                                                if (e.target.value.trim()) {
                                                  handleCompleteExitInterview(
                                                    offboarding.id,
                                                    e.target.value,
                                                  );
                                                }
                                              }}
                                            />
                                          </div>
                                        </div>
                                      </DialogContent>
                                    </Dialog>
                                  )}
                                  {!offboarding.finalSettlementPaid && (
                                    <Dialog>
                                      <DialogTrigger asChild>
                                        <Button variant="outline" size="sm">
                                          <CurrencyDollar className="w-4 h-4 mr-2" />
                                          Process Settlement
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent>
                                        <DialogHeader>
                                          <DialogTitle>
                                            Final Settlement -{" "}
                                            {offboarding.employeeName}
                                          </DialogTitle>
                                          <DialogDescription>
                                            Process the final settlement payment
                                          </DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-4 py-4">
                                          <div className="space-y-2">
                                            <Label htmlFor="settlement-amount">
                                              Settlement Amount
                                            </Label>
                                            <Input
                                              id="settlement-amount"
                                              type="number"
                                              placeholder="Enter amount"
                                              onBlur={(e) => {
                                                const amount = parseFloat(
                                                  e.target.value,
                                                );
                                                if (amount > 0) {
                                                  handleProcessFinalSettlement(
                                                    offboarding.id,
                                                    amount,
                                                  );
                                                }
                                              }}
                                            />
                                          </div>
                                        </div>
                                      </DialogContent>
                                    </Dialog>
                                  )}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Attendance Section */}
      {activeSection === "attendance" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarCheck className="w-5 h-5" />
                    Attendance Tracking
                  </CardTitle>
                  <CardDescription>
                    View and manage your attendance records
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setManualEntryDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Manual Entry
                  </Button>
                  <Button onClick={() => setLeaveDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Request Leave
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Attendance Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                  <div className="text-2xl font-bold text-green-600">
                    {
                      (attendanceRecords || []).filter(
                        (r) => r.status === "present",
                      ).length
                    }
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Present Days
                  </div>
                </div>
                <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                  <div className="text-2xl font-bold text-red-600">
                    {
                      (attendanceRecords || []).filter(
                        (r) => r.status === "absent",
                      ).length
                    }
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Absent Days
                  </div>
                </div>
                <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                  <div className="text-2xl font-bold text-amber-600">
                    {
                      (attendanceRecords || []).filter(
                        (r) => r.status === "late",
                      ).length
                    }
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Late Arrivals
                  </div>
                </div>
                <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                  <div className="text-2xl font-bold text-blue-600">
                    {streak}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Day Streak
                  </div>
                </div>
              </div>

              {/* Calendar and List View Tabs */}
              <Tabs defaultValue="calendar" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="calendar">Calendar View</TabsTrigger>
                  <TabsTrigger value="list">List View</TabsTrigger>
                </TabsList>

                {/* Calendar Tab */}
                <TabsContent value="calendar" className="space-y-4">
                  <AttendanceCalendar
                    attendanceData={Object.fromEntries(
                      (attendanceRecords || []).map((record) => {
                        const date = new Date(record.date);
                        const key = record.date;
                        return [
                          key,
                          {
                            date,
                            checkInTime: record.checkIn,
                            checkOutTime: record.checkOut,
                            status: record.status as any,
                            totalHours: record.duration,
                            notes: record.notes,
                          },
                        ];
                      }),
                    )}
                    onDateSelect={(date) => {
                      console.log("Date selected:", date);
                      toast.info(`Selected ${date.toDateString()}`);
                    }}
                  />
                </TabsContent>

                {/* List Tab */}
                <TabsContent value="list" className="space-y-4">
                  {/* Filters and Bulk Operations */}
                  <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">
                        Filters & Bulk Actions
                      </h3>
                      {attendanceSelectedIds.size > 0 && (
                        <div className="flex gap-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleBulkDeleteAttendance}
                          >
                            <Trash className="w-4 h-4 mr-2" />
                            Delete Selected ({attendanceSelectedIds.size})
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="text-xs font-medium mb-1 block">
                          Search Date
                        </label>
                        <Input
                          placeholder="Search by date..."
                          value={attendanceSearch}
                          onChange={(e) => setAttendanceSearch(e.target.value)}
                          className="h-9"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-medium mb-1 block">
                          Status
                        </label>
                        <select
                          value={attendanceStatusFilter}
                          onChange={(e) =>
                            setAttendanceStatusFilter(e.target.value)
                          }
                          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        >
                          <option value="all">All Status</option>
                          <option value="present">Present</option>
                          <option value="absent">Absent</option>
                          <option value="late">Late</option>
                          <option value="half-day">Half Day</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs font-medium mb-1 block">
                          From Date
                        </label>
                        <Input
                          type="date"
                          value={attendanceDateFrom}
                          onChange={(e) =>
                            setAttendanceDateFrom(e.target.value)
                          }
                          className="h-9"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-medium mb-1 block">
                          To Date
                        </label>
                        <Input
                          type="date"
                          value={attendanceDateTo}
                          onChange={(e) => setAttendanceDateTo(e.target.value)}
                          className="h-9"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setAttendanceSearch("");
                          setAttendanceStatusFilter("all");
                          setAttendanceDateFrom("");
                          setAttendanceDateTo("");
                          setAttendanceSelectedIds(new Set());
                          toast.success("Filters cleared");
                        }}
                      >
                        Clear Filters
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportFilteredAttendance}
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Export Filtered
                      </Button>
                    </div>

                    {filteredAttendanceRecords.length > 0 && (
                      <div className="text-sm text-muted-foreground">
                        Showing {filteredAttendanceRecords.length} of{" "}
                        {attendanceRecords.length} records
                      </div>
                    )}
                  </div>

                  {/* Filtered Attendance Records */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">
                        Attendance Records
                      </h3>
                      {filteredAttendanceRecords.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSelectAllAttendance}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          {attendanceSelectedIds.size ===
                          filteredAttendanceRecords.length
                            ? "Deselect All"
                            : "Select All"}
                        </Button>
                      )}
                    </div>
                    {filteredAttendanceRecords.length === 0 ? (
                      <div className="text-center py-12 border rounded-lg bg-muted/30">
                        {(attendanceRecords || []).length === 0 ? (
                          <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                              <CalendarCheck className="w-8 h-8 text-blue-600" />
                            </div>
                            <h4 className="text-base font-semibold mb-2">
                              Start tracking your attendance
                            </h4>
                            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                              Check in from the dashboard or add a manual entry
                              to begin tracking your work hours.
                            </p>
                            <div className="flex gap-3 justify-center">
                              <Button onClick={handleCheckIn} variant="default">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Check In Now
                              </Button>
                              <Button
                                onClick={() => setManualEntryDialogOpen(true)}
                                variant="outline"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Manual Entry
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                              <Warning className="w-8 h-8 text-amber-600" />
                            </div>
                            <h4 className="text-base font-semibold mb-2">
                              No matching records
                            </h4>
                            <p className="text-sm text-muted-foreground mb-6">
                              Try adjusting your filters to see more results.
                            </p>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setAttendanceSearch("");
                                setAttendanceStatusFilter("all");
                                setAttendanceDateFrom("");
                                setAttendanceDateTo("");
                              }}
                            >
                              Clear Filters
                            </Button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredAttendanceRecords.map((record) => (
                          <div
                            key={record.id}
                            className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={attendanceSelectedIds.has(record.id)}
                              onChange={() =>
                                handleToggleAttendanceSelection(record.id)
                              }
                              className="w-4 h-4 rounded border-gray-300"
                            />
                            <div
                              className={`w-2 h-2 rounded-full ${
                                record.status === "present"
                                  ? "bg-green-500"
                                  : record.status === "late"
                                    ? "bg-amber-500"
                                    : record.status === "absent"
                                      ? "bg-red-500"
                                      : "bg-blue-500"
                              }`}
                            />
                            <div className="flex-1">
                              <div className="font-medium">
                                {new Date(record.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    weekday: "short",
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  },
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {record.checkIn}{" "}
                                {record.checkOut
                                  ? `- ${record.checkOut}`
                                  : "(In Progress)"}
                                {record.location && ` • ${record.location}`}
                              </div>
                            </div>
                            <Badge
                              variant={
                                record.status === "present"
                                  ? "default"
                                  : "secondary"
                              }
                            >
                              {record.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leaves Section */}
      {activeSection === "leaves" && (
        <div
          className="space-y-6"
          role="tabpanel"
          id="leaves-panel"
          aria-labelledby="leaves-tab"
        >
          <div>
            <h1 className="text-3xl font-bold">Leave Management</h1>
            <p className="text-muted-foreground">
              Manage your leave requests and balance
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Annual Leave</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">12 days</div>
                <p className="text-sm text-muted-foreground">Available</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Sick Leave</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">5 days</div>
                <p className="text-sm text-muted-foreground">Available</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Casual Leave</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">3 days</div>
                <p className="text-sm text-muted-foreground">Available</p>
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Leave Requests</CardTitle>
                  <CardDescription>
                    View and manage your leave applications
                  </CardDescription>
                </div>
                <Button onClick={() => setLeaveDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Request Leave
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(leaveRequests || []).length === 0 ? (
                <div className="text-center py-12">
                  <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <CalendarDots className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    No leave requests yet
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                    Plan your time off by submitting a leave request. We'll
                    notify your manager for approval.
                  </p>
                  <Button onClick={() => setLeaveDialogOpen(true)} size="lg">
                    <Plus className="w-4 h-4 mr-2" />
                    Request Your First Leave
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {(leaveRequests || []).map((request) => (
                    <div
                      key={request.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            request.status === "approved"
                              ? "bg-green-100"
                              : request.status === "rejected"
                                ? "bg-red-100"
                                : "bg-amber-100"
                          }`}
                        >
                          <CalendarDots
                            className={`w-5 h-5 ${
                              request.status === "approved"
                                ? "text-green-600"
                                : request.status === "rejected"
                                  ? "text-red-600"
                                  : "text-amber-600"
                            }`}
                          />
                        </div>
                        <div>
                          <p className="font-medium">{request.type}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(request.startDate).toLocaleDateString()} -{" "}
                            {new Date(request.endDate).toLocaleDateString()}
                            {" • "}
                            {request.totalDays}{" "}
                            {request.totalDays === 1 ? "day" : "days"}
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant={
                          request.status === "approved"
                            ? "default"
                            : request.status === "rejected"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {request.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Holidays Section */}
      {activeSection === "holidays" && (
        <div
          className="space-y-6"
          role="tabpanel"
          id="holidays-panel"
          aria-labelledby="holidays-tab"
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Company Holidays</h1>
              <p className="text-muted-foreground">
                Manage public and company holidays
              </p>
            </div>
            {(currentUser?.role === "admin" ||
              currentUser?.role === "superadmin" ||
              currentUser?.role === "hr") && (
              <Button onClick={handleAddHoliday}>
                <Plus className="h-4 w-4 mr-2" />
                Add Holiday
              </Button>
            )}
          </div>

          {/* Upcoming Holidays Card */}
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Holidays</CardTitle>
              <CardDescription>
                {holidays.filter((h) => new Date(h.date) >= new Date()).length}{" "}
                holidays scheduled
              </CardDescription>
            </CardHeader>
            <CardContent>
              {holidays.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No holidays configured yet
                </p>
              ) : (
                <div className="space-y-4">
                  {holidays
                    .sort(
                      (a, b) =>
                        new Date(a.date).getTime() - new Date(b.date).getTime(),
                    )
                    .map((holiday) => (
                      <div
                        key={holiday.id}
                        className="flex items-center justify-between p-4 rounded-lg border"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className={`p-3 rounded-full ${
                              holiday.type === "public"
                                ? "bg-[var(--info)_/_0.1] text-[var(--info)]"
                                : holiday.type === "company"
                                  ? "bg-[var(--success)_/_0.1] text-[var(--success)]"
                                  : "bg-[var(--warning)_/_0.1] text-[var(--warning)]"
                            }`}
                          >
                            <Gift className="h-5 w-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{holiday.name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {new Date(holiday.date).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                },
                              )}
                            </p>
                            {holiday.description && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {holiday.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              holiday.type === "public"
                                ? "default"
                                : holiday.type === "company"
                                  ? "secondary"
                                  : "outline"
                            }
                          >
                            {holiday.type}
                          </Badge>
                          {holiday.recurring && (
                            <Badge variant="outline">Recurring</Badge>
                          )}
                          {(currentUser?.role === "admin" ||
                            currentUser?.role === "superadmin" ||
                            currentUser?.role === "hr") && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditHoliday(holiday)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteHoliday(holiday.id)}
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Holiday Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Holidays
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{holidays.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Public Holidays
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {holidays.filter((h) => h.type === "public").length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Company Holidays
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {holidays.filter((h) => h.type === "company").length}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Add/Edit Holiday Dialog */}
          <Dialog open={holidayDialogOpen} onOpenChange={setHolidayDialogOpen}>
            <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingHoliday ? "Edit Holiday" : "Add New Holiday"}
                </DialogTitle>
                <DialogDescription>
                  {editingHoliday
                    ? "Update holiday information"
                    : "Create a new company holiday"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Holiday Name *</label>
                  <Input
                    placeholder="e.g., New Year's Day"
                    value={holidayFormData.name}
                    onChange={(e) =>
                      setHolidayFormData({
                        ...holidayFormData,
                        name: e.target.value,
                      })
                    }
                    className="text-base"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Date *</label>
                  <Input
                    type="date"
                    value={holidayFormData.date}
                    onChange={(e) =>
                      setHolidayFormData({
                        ...holidayFormData,
                        date: e.target.value,
                      })
                    }
                    className="text-base"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Holiday Type *</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={holidayFormData.type}
                    onChange={(e) =>
                      setHolidayFormData({
                        ...holidayFormData,
                        type: e.target.value as
                          | "public"
                          | "company"
                          | "optional",
                      })
                    }
                  >
                    <option value="public">Public Holiday</option>
                    <option value="company">Company Holiday</option>
                    <option value="optional">Optional Holiday</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Input
                    placeholder="Optional description"
                    value={holidayFormData.description}
                    onChange={(e) =>
                      setHolidayFormData({
                        ...holidayFormData,
                        description: e.target.value,
                      })
                    }
                    className="text-base"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="recurring"
                    checked={holidayFormData.recurring}
                    onChange={(e) =>
                      setHolidayFormData({
                        ...holidayFormData,
                        recurring: e.target.checked,
                      })
                    }
                    className="h-4 w-4"
                  />
                  <label htmlFor="recurring" className="text-sm font-medium">
                    Recurring annual holiday
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setHolidayDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveHoliday}>
                  {editingHoliday ? "Update" : "Add"} Holiday
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={!!deleteHolidayId}
            onOpenChange={() => setDeleteHolidayId(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Holiday</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this holiday? This action
                  cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setDeleteHolidayId(null)}
                >
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDeleteHoliday}>
                  Delete
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Shift Schedules Section */}
      {activeSection === "shifts" &&
        (currentUser?.role === "manager" ||
          currentUser?.role === "hr" ||
          currentUser?.role === "admin" ||
          currentUser?.role === "superadmin") && (
          <div
            className="space-y-6"
            role="tabpanel"
            id="shifts-panel"
            aria-labelledby="shifts-tab"
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold">Shift Schedules</h1>
                <p className="text-muted-foreground">
                  Manage employee shift schedules and swap requests
                </p>
              </div>
              <Button onClick={() => setShowNewShiftDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Shift
              </Button>
            </div>

            {/* Date Selector */}
            <Card>
              <CardHeader>
                <CardTitle>Filter by Date</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  type="date"
                  value={selectedShiftDate}
                  onChange={(e) => setSelectedShiftDate(e.target.value)}
                  className="max-w-xs"
                />
              </CardContent>
            </Card>

            {/* Shift Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Total Shifts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{shiftSchedules.length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Scheduled
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {
                      shiftSchedules.filter((s) => s.status === "scheduled")
                        .length
                    }
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Confirmed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {
                      shiftSchedules.filter((s) => s.status === "confirmed")
                        .length
                    }
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Pending Swaps
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {
                      shiftSwapRequests.filter((r) => r.status === "pending")
                        .length
                    }
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Shifts for Selected Date */}
            <Card>
              <CardHeader>
                <CardTitle>
                  Shifts -{" "}
                  {new Date(selectedShiftDate).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {shiftSchedules.filter((s) => s.date === selectedShiftDate)
                  .length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No shifts scheduled for this date
                  </p>
                ) : (
                  <div className="space-y-3">
                    {shiftSchedules
                      .filter((s) => s.date === selectedShiftDate)
                      .map((shift) => (
                        <Card key={shift.id} className="border">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <div>
                                    <p className="font-semibold">
                                      {shift.employeeName}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                      {shift.department}
                                    </p>
                                  </div>
                                </div>
                                <div className="mt-2 flex items-center gap-4 text-sm">
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    <span>
                                      {shift.startTime} - {shift.endTime}
                                    </span>
                                  </div>
                                  <Badge
                                    variant={
                                      shift.shiftType === "morning"
                                        ? "default"
                                        : shift.shiftType === "afternoon"
                                          ? "secondary"
                                          : shift.shiftType === "evening"
                                            ? "outline"
                                            : "default"
                                    }
                                  >
                                    {shift.shiftType.charAt(0).toUpperCase() +
                                      shift.shiftType.slice(1)}
                                  </Badge>
                                  <Badge
                                    variant={
                                      shift.status === "confirmed"
                                        ? "default"
                                        : shift.status === "scheduled"
                                          ? "secondary"
                                          : shift.status === "completed"
                                            ? "outline"
                                            : "destructive"
                                    }
                                  >
                                    {shift.status.charAt(0).toUpperCase() +
                                      shift.status.slice(1)}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                  📍 {shift.location}
                                </p>
                                {shift.notes && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    📝 {shift.notes}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                {shift.status === "scheduled" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleUpdateShiftStatus(
                                        shift.id,
                                        "confirmed",
                                      )
                                    }
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Confirm
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedShift(shift);
                                    setShowShiftDetailsDialog(true);
                                  }}
                                >
                                  View
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteShift(shift.id)}
                                >
                                  <Trash className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Shift Swap Requests */}
            {shiftSwapRequests.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Shift Swap Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {shiftSwapRequests
                      .filter((r) => r.status === "pending")
                      .map((request) => {
                        const requesterShift = shiftSchedules.find(
                          (s) => s.id === request.requesterShiftId,
                        );
                        const targetShift = request.targetShiftId
                          ? shiftSchedules.find(
                              (s) => s.id === request.targetShiftId,
                            )
                          : null;

                        return (
                          <Card key={request.id} className="border">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-semibold">
                                    {request.requesterName}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    Wants to swap{" "}
                                    {requesterShift
                                      ? `${requesterShift.date} (${requesterShift.startTime} - ${requesterShift.endTime})`
                                      : "shift"}
                                    {targetShift &&
                                      ` with ${request.targetEmployeeName}'s ${targetShift.date} shift`}
                                  </p>
                                  <p className="text-sm mt-1">
                                    Reason: {request.reason}
                                  </p>
                                  <Badge variant="secondary" className="mt-2">
                                    {request.status}
                                  </Badge>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      handleApproveSwapRequest(request.id)
                                    }
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() =>
                                      handleRejectSwapRequest(
                                        request.id,
                                        "Not approved",
                                      )
                                    }
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Create Shift Dialog */}
            <Dialog
              open={showNewShiftDialog}
              onOpenChange={setShowNewShiftDialog}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Shift</DialogTitle>
                  <DialogDescription>
                    Schedule a new shift for an employee
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Employee</label>
                    <select
                      className="w-full mt-1 p-2 border rounded-md"
                      value={newShift.employeeId || ""}
                      onChange={(e) =>
                        setNewShift({
                          ...newShift,
                          employeeId: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Employee</option>
                      {teamMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name} - {member.department}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Date</label>
                    <Input
                      type="date"
                      value={newShift.date || ""}
                      onChange={(e) =>
                        setNewShift({ ...newShift, date: e.target.value })
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Shift Type</label>
                    <select
                      className="w-full mt-1 p-2 border rounded-md"
                      value={newShift.shiftType || "morning"}
                      onChange={(e) => {
                        const type = e.target.value as
                          | "morning"
                          | "afternoon"
                          | "evening"
                          | "night"
                          | "custom";
                        const times = {
                          morning: { start: "08:00", end: "16:00" },
                          afternoon: { start: "12:00", end: "20:00" },
                          evening: { start: "16:00", end: "00:00" },
                          night: { start: "00:00", end: "08:00" },
                          custom: {
                            start: newShift.startTime || "08:00",
                            end: newShift.endTime || "16:00",
                          },
                        };
                        setNewShift({
                          ...newShift,
                          shiftType: type,
                          startTime: times[type].start,
                          endTime: times[type].end,
                        });
                      }}
                    >
                      <option value="morning">
                        Morning (8:00 AM - 4:00 PM)
                      </option>
                      <option value="afternoon">
                        Afternoon (12:00 PM - 8:00 PM)
                      </option>
                      <option value="evening">
                        Evening (4:00 PM - 12:00 AM)
                      </option>
                      <option value="night">Night (12:00 AM - 8:00 AM)</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Start Time</label>
                      <Input
                        type="time"
                        value={newShift.startTime || "08:00"}
                        onChange={(e) =>
                          setNewShift({
                            ...newShift,
                            startTime: e.target.value,
                          })
                        }
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">End Time</label>
                      <Input
                        type="time"
                        value={newShift.endTime || "16:00"}
                        onChange={(e) =>
                          setNewShift({
                            ...newShift,
                            endTime: e.target.value,
                          })
                        }
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Location</label>
                    <Input
                      value={newShift.location || "Main Office"}
                      onChange={(e) =>
                        setNewShift({
                          ...newShift,
                          location: e.target.value,
                        })
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      Notes (Optional)
                    </label>
                    <Input
                      value={newShift.notes || ""}
                      onChange={(e) =>
                        setNewShift({ ...newShift, notes: e.target.value })
                      }
                      placeholder="Any additional notes..."
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowNewShiftDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleCreateShift}>Create Shift</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Shift Details Dialog */}
            <Dialog
              open={showShiftDetailsDialog}
              onOpenChange={setShowShiftDetailsDialog}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Shift Details</DialogTitle>
                </DialogHeader>
                {selectedShift && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium">Employee</p>
                      <p className="text-lg">{selectedShift.employeeName}</p>
                      <p className="text-sm text-muted-foreground">
                        {selectedShift.department}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Date</p>
                      <p>
                        {new Date(selectedShift.date).toLocaleDateString(
                          "en-US",
                          {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          },
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Shift Time</p>
                      <p>
                        {selectedShift.startTime} - {selectedShift.endTime}
                      </p>
                      <Badge className="mt-1">{selectedShift.shiftType}</Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Location</p>
                      <p>{selectedShift.location}</p>
                    </div>
                    {selectedShift.notes && (
                      <div>
                        <p className="text-sm font-medium">Notes</p>
                        <p className="text-sm text-muted-foreground">
                          {selectedShift.notes}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium">Status</p>
                      <Badge
                        variant={
                          selectedShift.status === "confirmed"
                            ? "default"
                            : selectedShift.status === "scheduled"
                              ? "secondary"
                              : selectedShift.status === "completed"
                                ? "outline"
                                : "destructive"
                        }
                      >
                        {selectedShift.status}
                      </Badge>
                    </div>
                  </div>
                )}
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowShiftDetailsDialog(false)}
                  >
                    Close
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

      {/* Reports Section */}
      {activeSection === "reports" && (
        <div
          className="space-y-6"
          role="tabpanel"
          id="reports-panel"
          aria-labelledby="reports-tab"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold">Reports & Analytics</h1>
              <p className="text-muted-foreground">
                View comprehensive reports and insights
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground hidden sm:inline">
                Time Range:
              </span>
              <div className="flex gap-1 border rounded-lg p-1">
                {(["7d", "30d", "90d", "all"] as const).map((range) => (
                  <Button
                    key={range}
                    variant={analyticsDateRange === range ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setAnalyticsDateRange(range)}
                    className="text-xs"
                  >
                    {range === "all" ? "All Time" : range.toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Analytics Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Attendance Trends Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Attendance Trends</CardTitle>
                <CardDescription>
                  Daily attendance status breakdown
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={getAttendanceChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Present"
                      stackId="1"
                      stroke="#22c55e"
                      fill="#22c55e"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="Late"
                      stackId="1"
                      stroke="#f59e0b"
                      fill="#f59e0b"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="Half-Day"
                      stackId="1"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="Absent"
                      stackId="1"
                      stroke="#ef4444"
                      fill="#ef4444"
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Leave Type Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Leave Type Distribution</CardTitle>
                <CardDescription>Approved leaves by type</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getLeaveTypeChartData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getLeaveTypeChartData().map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            [
                              "#22c55e",
                              "#3b82f6",
                              "#f59e0b",
                              "#ef4444",
                              "#8b5cf6",
                              "#ec4899",
                            ][index % 6]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Timesheet Hours by Project */}
            <Card>
              <CardHeader>
                <CardTitle>Timesheet Hours by Project</CardTitle>
                <CardDescription>
                  Total hours logged per project
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getTimesheetHoursData()} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 12 }}
                      width={100}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="hours" fill="#3b82f6" name="Hours" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Department Attendance Rate */}
            <Card>
              <CardHeader>
                <CardTitle>Department Attendance Rate</CardTitle>
                <CardDescription>
                  Attendance percentage by department
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getDepartmentComparisonData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="department"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="attendanceRate"
                      fill="#22c55e"
                      name="Attendance Rate (%)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Overtime Trends */}
            {(overtimeEntries || []).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Overtime Trends</CardTitle>
                  <CardDescription>Overtime hours over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={getOvertimeTrendData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="hours"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        name="Overtime Hours"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Export Reports Section */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Export Reports</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Attendance Report */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Attendance Report</CardTitle>
                  <CardDescription>
                    Export all attendance records
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        try {
                          const formattedData = formatAttendanceReport(
                            attendanceRecords || [],
                          );
                          exportToCSV(formattedData, "attendance_report");
                          toast.success(
                            "Attendance report exported successfully",
                          );
                        } catch (err: any) {
                          toast.error(err.message || "Failed to export report");
                        }
                      }}
                    >
                      <UploadSimple className="mr-2 h-4 w-4" />
                      Export to CSV
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      {(attendanceRecords || []).length} records
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Leave Report */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Leave Report</CardTitle>
                  <CardDescription>Export all leave requests</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        try {
                          const formattedData = formatLeaveReport(
                            leaveRequests || [],
                          );
                          exportToCSV(formattedData, "leave_report");
                          toast.success("Leave report exported successfully");
                        } catch (err: any) {
                          toast.error(err.message || "Failed to export report");
                        }
                      }}
                    >
                      <UploadSimple className="mr-2 h-4 w-4" />
                      Export to CSV
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      {(leaveRequests || []).length} requests
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Timesheet Report */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Timesheet Report</CardTitle>
                  <CardDescription>
                    Export all timesheet entries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        try {
                          const formattedData = formatTimesheetReport(
                            timesheetEntries || [],
                          );
                          exportToCSV(formattedData, "timesheet_report");
                          toast.success(
                            "Timesheet report exported successfully",
                          );
                        } catch (err: any) {
                          toast.error(err.message || "Failed to export report");
                        }
                      }}
                    >
                      <UploadSimple className="mr-2 h-4 w-4" />
                      Export to CSV
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      {(timesheetEntries || []).length} entries
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Employee Report */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Employee Report</CardTitle>
                  <CardDescription>Export employee directory</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        try {
                          const formattedData = formatEmployeeReport(
                            teamMembers || [],
                          );
                          exportToCSV(formattedData, "employee_report");
                          toast.success(
                            "Employee report exported successfully",
                          );
                        } catch (err: any) {
                          toast.error(err.message || "Failed to export report");
                        }
                      }}
                    >
                      <UploadSimple className="mr-2 h-4 w-4" />
                      Export to CSV
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      {(teamMembers || []).length} employees
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Employees Section */}
      {activeSection === "employees" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Team Directory
                  </CardTitle>
                  <CardDescription>
                    View all team members and their details
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search employees..."
                      className="pl-9 pr-4 py-2 border rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(teamMembers || []).map((member) => (
                  <Card
                    key={member.id}
                    className="hover:shadow-lg transition-shadow"
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3">
                        <div className="text-3xl">{member.avatar}</div>
                        <div className="flex-1">
                          <div className="font-semibold">{member.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {member.role}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {member.department}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge
                              variant={
                                member.status === "online"
                                  ? "default"
                                  : "secondary"
                              }
                            >
                              {member.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Organization Chart Section */}
      {activeSection === "org-chart" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <UsersThree className="w-5 h-5" />
                    Organization Chart
                  </CardTitle>
                  <CardDescription>
                    Visual representation of company hierarchy
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    <TrendUp className="w-4 h-4 mr-2" />
                    Export as Image
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* View Options */}
              <div className="flex gap-2 mb-6">
                <Button variant="outline" size="sm">
                  By Department
                </Button>
                <Button variant="outline" size="sm">
                  By Location
                </Button>
                <Button variant="outline" size="sm">
                  Full Hierarchy
                </Button>
              </div>

              {/* Search in org chart */}
              <div className="mb-6">
                <div className="relative max-w-md">
                  <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search for an employee..."
                    className="pl-9 pr-4 py-2 border rounded-lg text-sm w-full"
                  />
                </div>
              </div>

              {/* Organization Chart Visualization */}
              <div className="border rounded-lg p-6 bg-muted/30">
                {(() => {
                  // Build hierarchy from teamMembers
                  const ceo = teamMembers.find(
                    (m) =>
                      m.role.toLowerCase().includes("ceo") ||
                      m.role.toLowerCase().includes("founder"),
                  );
                  const departments: Record<string, typeof teamMembers> = {};

                  teamMembers.forEach((member) => {
                    if (!departments[member.department]) {
                      departments[member.department] = [];
                    }
                    departments[member.department].push(member);
                  });

                  return (
                    <div className="space-y-8">
                      {/* CEO Level */}
                      {ceo && (
                        <div className="flex justify-center">
                          <Card className="w-64 bg-primary/10 border-primary">
                            <CardContent className="pt-4">
                              <div className="flex flex-col items-center text-center">
                                <div className="text-4xl mb-2">
                                  {ceo.avatar}
                                </div>
                                <div className="font-semibold text-lg">
                                  {ceo.name}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {ceo.role}
                                </div>
                                <Badge
                                  className="mt-2"
                                  variant={
                                    ceo.status === "online"
                                      ? "default"
                                      : "secondary"
                                  }
                                >
                                  {ceo.status}
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {/* Department Heads and Teams */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Object.entries(departments).map(
                          ([deptName, members]) => {
                            const head = members.find(
                              (m) =>
                                m.role.toLowerCase().includes("head") ||
                                m.role.toLowerCase().includes("director") ||
                                m.role.toLowerCase().includes("manager"),
                            );
                            const otherMembers = members.filter(
                              (m) => m.id !== head?.id && m.id !== ceo?.id,
                            );

                            return (
                              <div key={deptName} className="space-y-4">
                                {/* Department Header */}
                                <div className="text-center">
                                  <h3 className="font-semibold text-lg border-b pb-2">
                                    {deptName}
                                  </h3>
                                </div>

                                {/* Department Head */}
                                {head && head.id !== ceo?.id && (
                                  <div className="flex justify-center">
                                    <Card className="w-full bg-secondary/30">
                                      <CardContent className="pt-4">
                                        <div className="flex items-start gap-3">
                                          <div className="text-2xl">
                                            {head.avatar}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">
                                              {head.name}
                                            </div>
                                            <div className="text-xs text-muted-foreground truncate">
                                              {head.role}
                                            </div>
                                            <div className="text-xs text-muted-foreground truncate">
                                              {head.email}
                                            </div>
                                            <Badge
                                              className="mt-1"
                                              variant="outline"
                                            >
                                              {head.status}
                                            </Badge>
                                          </div>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  </div>
                                )}

                                {/* Team Members */}
                                {otherMembers.length > 0 && (
                                  <div className="space-y-2 pl-4 border-l-2 ml-8">
                                    {otherMembers.map((member) => (
                                      <Card
                                        key={member.id}
                                        className="hover:shadow-md transition-shadow"
                                      >
                                        <CardContent className="p-3">
                                          <div className="flex items-start gap-2">
                                            <div className="text-xl">
                                              {member.avatar}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="font-medium text-sm truncate">
                                                {member.name}
                                              </div>
                                              <div className="text-xs text-muted-foreground truncate">
                                                {member.role}
                                              </div>
                                              <Badge
                                                className="mt-1"
                                                variant="outline"
                                              >
                                                {member.status}
                                              </Badge>
                                            </div>
                                          </div>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>

                      {/* Statistics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t">
                        <Card>
                          <CardContent className="pt-4">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-primary">
                                {teamMembers.length}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Total Employees
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-4">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-green-600">
                                {Object.keys(departments).length}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Departments
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-4">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-blue-600">
                                {
                                  teamMembers.filter(
                                    (m) => m.status === "online",
                                  ).length
                                }
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Online Now
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-4">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-orange-600">
                                {teamMembers.filter((m) => m.isOnLeave).length}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                On Leave
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance Section */}
      {activeSection === "performance" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ChartLine className="w-5 h-5" />
                Performance Analytics
              </CardTitle>
              <CardDescription>
                Track your performance metrics and goals
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-2">
                    Attendance Rate
                  </div>
                  <div className="text-2xl font-bold">{attendanceRate}%</div>
                  <div className="text-sm text-green-600 flex items-center gap-1 mt-1">
                    <TrendUp className="w-3 h-3" />
                    +2.1% this month
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-2">
                    Tasks Completed
                  </div>
                  <div className="text-2xl font-bold">
                    {timesheetEntries.length}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    This month
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-2">
                    Total Hours
                  </div>
                  <div className="text-2xl font-bold">
                    {timesheetEntries.reduce(
                      (sum, entry) => sum + entry.hours,
                      0,
                    )}
                    h
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Logged this month
                  </div>
                </div>
              </div>

              {/* Goals Progress */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Weekly Goals</h3>
                  <Button
                    onClick={() => setGoalDialogOpen(true)}
                    variant="outline"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Goal
                  </Button>
                </div>
                <div className="space-y-3">
                  {(weeklyGoals || []).map((goal) => (
                    <div key={goal.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium">{goal.title}</div>
                        <Badge>
                          {Math.round((goal.current / goal.target) * 100)}%
                        </Badge>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{
                            width: `${Math.round((goal.current / goal.target) * 100)}%`,
                          }}
                        />
                      </div>
                      <div className="text-sm text-muted-foreground mt-2">
                        {goal.current} / {goal.target}{" "}
                        {goal.type === "hours" ? "hrs" : goal.type}
                      </div>
                    </div>
                  ))}
                  {weeklyGoals.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No active goals. Create one to track your progress!</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Calendar Section - For Managers, HR, and Admins */}
      {activeSection === "team-calendar" && (
        <div
          className="space-y-6"
          role="tabpanel"
          id="team-calendar-panel"
          aria-labelledby="team-calendar-tab"
        >
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold">Team Leave Calendar</h1>
              <p className="text-muted-foreground">
                View team members' leave schedule and availability
              </p>
            </div>
          </div>

          {/* Calendar Controls */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const newDate = new Date(calendarDate);
                      if (calendarView === "month") {
                        newDate.setMonth(newDate.getMonth() - 1);
                      } else if (calendarView === "week") {
                        newDate.setDate(newDate.getDate() - 7);
                      } else {
                        newDate.setDate(newDate.getDate() - 1);
                      }
                      setCalendarDate(newDate);
                    }}
                    aria-label="Previous"
                  >
                    <CaretLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCalendarDate(new Date())}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const newDate = new Date(calendarDate);
                      if (calendarView === "month") {
                        newDate.setMonth(newDate.getMonth() + 1);
                      } else if (calendarView === "week") {
                        newDate.setDate(newDate.getDate() + 7);
                      } else {
                        newDate.setDate(newDate.getDate() + 1);
                      }
                      setCalendarDate(newDate);
                    }}
                    aria-label="Next"
                  >
                    <CaretRight className="w-4 h-4" />
                  </Button>
                  <div className="text-lg font-semibold ml-2">
                    {calendarView === "month" &&
                      calendarDate.toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    {calendarView === "week" &&
                      `Week of ${calendarDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
                    {calendarView === "day" &&
                      calendarDate.toLocaleDateString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={calendarView}
                    onChange={(e) =>
                      setCalendarView(
                        e.target.value as "month" | "week" | "day",
                      )
                    }
                  >
                    <option value="month">Month</option>
                    <option value="week">Week</option>
                    <option value="day">Day</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b">
                <select
                  className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={calendarDepartmentFilter}
                  onChange={(e) => setCalendarDepartmentFilter(e.target.value)}
                >
                  <option value="all">All Departments</option>
                  <option value="Engineering">Engineering</option>
                  <option value="HR">HR</option>
                  <option value="Sales">Sales</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Operations">Operations</option>
                </select>
                <select
                  className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={calendarStatusFilter}
                  onChange={(e) => setCalendarStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                </select>
                <select
                  className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={calendarTypeFilter}
                  onChange={(e) => setCalendarTypeFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal</option>
                  <option value="emergency">Emergency</option>
                </select>
              </div>

              {/* Calendar View */}
              {calendarView === "month" &&
                (() => {
                  // Get all leave requests filtered by current selections
                  const filteredLeaves = (leaveRequests || []).filter(
                    (leave) => {
                      // Department filter (for managers - only their team)
                      if (currentUser?.role === "manager") {
                        const employee = (teamMembers || []).find(
                          (emp) => emp.id === leave.employeeId,
                        );
                        if (
                          employee &&
                          employee.department !== currentUser.department
                        ) {
                          return false;
                        }
                      }
                      if (calendarDepartmentFilter !== "all") {
                        const employee = (teamMembers || []).find(
                          (emp) => emp.id === leave.employeeId,
                        );
                        if (
                          !employee ||
                          employee.department !== calendarDepartmentFilter
                        ) {
                          return false;
                        }
                      }
                      // Status filter
                      if (
                        calendarStatusFilter !== "all" &&
                        leave.status !== calendarStatusFilter
                      ) {
                        return false;
                      }
                      // Type filter
                      if (
                        calendarTypeFilter !== "all" &&
                        leave.type !== calendarTypeFilter
                      ) {
                        return false;
                      }
                      return true;
                    },
                  );

                  // Calculate calendar grid
                  const year = calendarDate.getFullYear();
                  const month = calendarDate.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  const startDay = firstDay.getDay(); // 0 = Sunday
                  const daysInMonth = lastDay.getDate();

                  const days: (number | null)[] = [];
                  // Add empty cells for days before the first of the month
                  for (let i = 0; i < startDay; i++) {
                    days.push(null);
                  }
                  // Add all days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    days.push(day);
                  }

                  // Calculate team availability for each day
                  const getAvailabilityForDay = (day: number | null) => {
                    if (!day)
                      return {
                        percentage: 100,
                        onLeaveCount: 0,
                        status: "normal" as const,
                      };

                    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    const approvedLeavesOnDay = filteredLeaves.filter(
                      (leave) => {
                        if (leave.status !== "approved") return false;
                        const leaveStart = new Date(leave.startDate);
                        const leaveEnd = new Date(leave.endDate);
                        const currentDay = new Date(dateStr);
                        return (
                          currentDay >= leaveStart && currentDay <= leaveEnd
                        );
                      },
                    );

                    // Get total team size
                    let totalTeam = teamMembers.length;
                    if (currentUser?.role === "manager") {
                      totalTeam = (teamMembers || []).filter(
                        (emp) => emp.department === currentUser.department,
                      ).length;
                    } else if (calendarDepartmentFilter !== "all") {
                      totalTeam = (teamMembers || []).filter(
                        (emp) => emp.department === calendarDepartmentFilter,
                      ).length;
                    }

                    const onLeaveCount = approvedLeavesOnDay.length;
                    const percentage =
                      totalTeam > 0
                        ? Math.round(
                            ((totalTeam - onLeaveCount) / totalTeam) * 100,
                          )
                        : 100;

                    let status: "normal" | "low" | "critical" = "normal";
                    if (percentage < 50) status = "critical";
                    else if (percentage < 70) status = "low";

                    return { percentage, onLeaveCount, status };
                  };

                  // Get leaves for a specific day
                  const getLeavesForDay = (day: number | null) => {
                    if (!day) return [];
                    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    return filteredLeaves.filter((leave) => {
                      const leaveStart = new Date(leave.startDate);
                      const leaveEnd = new Date(leave.endDate);
                      const currentDay = new Date(dateStr);
                      return currentDay >= leaveStart && currentDay <= leaveEnd;
                    });
                  };

                  return (
                    <div className="space-y-4">
                      {/* Calendar Grid */}
                      <div className="grid grid-cols-7 gap-1">
                        {/* Day headers */}
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                          (day) => (
                            <div
                              key={day}
                              className="text-center text-sm font-semibold py-2 text-muted-foreground"
                            >
                              {day}
                            </div>
                          ),
                        )}
                        {/* Calendar days */}
                        {days.map((day, index) => {
                          if (!day) {
                            return (
                              <div
                                key={`empty-${index}`}
                                className="min-h-24 p-1 border rounded-md bg-slate-50"
                              ></div>
                            );
                          }

                          const availability = getAvailabilityForDay(day);
                          const leavesOnDay = getLeavesForDay(day);
                          const isToday =
                            day === new Date().getDate() &&
                            month === new Date().getMonth() &&
                            year === new Date().getFullYear();

                          return (
                            <div
                              key={day}
                              className={`min-h-24 p-1 border rounded-md ${
                                isToday
                                  ? "border-primary border-2 bg-primary/5"
                                  : "bg-white"
                              } ${
                                availability.status === "critical"
                                  ? "bg-[var(--status-absent-bg)]"
                                  : availability.status === "low"
                                    ? "bg-[var(--status-late-bg)]"
                                    : ""
                              }`}
                            >
                              <div className="flex justify-between items-start mb-1">
                                <span
                                  className={`text-sm font-medium ${isToday ? "text-primary font-bold" : ""}`}
                                >
                                  {day}
                                </span>
                                {availability.onLeaveCount > 0 && (
                                  <div
                                    className={`text-xs px-1 rounded ${
                                      availability.status === "critical"
                                        ? "bg-[var(--status-absent-bg)] text-[var(--status-absent)]"
                                        : availability.status === "low"
                                          ? "bg-[var(--status-late-bg)] text-[var(--status-late)]"
                                          : "bg-slate-100 text-slate-700"
                                    }`}
                                  >
                                    {availability.percentage}%
                                  </div>
                                )}
                              </div>
                              <div className="space-y-0.5">
                                {leavesOnDay.slice(0, 3).map((leave) => {
                                  const employee = (teamMembers || []).find(
                                    (emp) => emp.id === leave.employeeId,
                                  );
                                  return (
                                    <div
                                      key={leave.id}
                                      className={`text-xs px-1 py-0.5 rounded truncate ${
                                        leave.type === "vacation"
                                          ? "bg-[var(--status-leave-bg)] text-[var(--status-leave)]"
                                          : leave.type === "sick"
                                            ? "bg-[var(--status-sick-bg)] text-[var(--status-sick)]"
                                            : leave.type === "personal"
                                              ? "bg-[var(--status-late-bg)] text-[var(--status-late)]"
                                              : "bg-slate-100 text-slate-700"
                                      } ${
                                        leave.status === "pending"
                                          ? "border border-dashed"
                                          : ""
                                      }`}
                                      title={`${leave.employeeName} - ${leave.type} (${leave.status})`}
                                    >
                                      {employee?.name.split(" ")[0] ||
                                        leave.employeeName.split(" ")[0]}
                                    </div>
                                  );
                                })}
                                {leavesOnDay.length > 3 && (
                                  <div className="text-xs text-muted-foreground px-1">
                                    +{leavesOnDay.length - 3} more
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Legend */}
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded"></div>
                          <span>Vacation</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-red-100 border border-red-200 rounded"></div>
                          <span>Sick Leave</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded"></div>
                          <span>Personal</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-white border border-dashed border-slate-400 rounded"></div>
                          <span>Pending</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Warning className="w-4 h-4 text-yellow-600" />
                          <span>&lt; 70% availability</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Warning className="w-4 h-4 text-red-600" />
                          <span>&lt; 50% availability (Critical)</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {/* Week View */}
              {calendarView === "week" &&
                (() => {
                  // Get all leave requests filtered by current selections
                  const filteredLeaves = (leaveRequests || []).filter(
                    (leave) => {
                      // Department filter (for managers - only their team)
                      if (currentUser?.role === "manager") {
                        const employee = (teamMembers || []).find(
                          (emp) => emp.id === leave.employeeId,
                        );
                        if (
                          employee &&
                          employee.department !== currentUser.department
                        ) {
                          return false;
                        }
                      }
                      if (calendarDepartmentFilter !== "all") {
                        const employee = (teamMembers || []).find(
                          (emp) => emp.id === leave.employeeId,
                        );
                        if (
                          !employee ||
                          employee.department !== calendarDepartmentFilter
                        ) {
                          return false;
                        }
                      }
                      // Status filter
                      if (
                        calendarStatusFilter !== "all" &&
                        leave.status !== calendarStatusFilter
                      ) {
                        return false;
                      }
                      // Type filter
                      if (
                        calendarTypeFilter !== "all" &&
                        leave.type !== calendarTypeFilter
                      ) {
                        return false;
                      }
                      return true;
                    },
                  );

                  // Calculate week start (Sunday) and end (Saturday)
                  const currentDate = new Date(calendarDate);
                  const dayOfWeek = currentDate.getDay(); // 0 = Sunday
                  const weekStart = new Date(currentDate);
                  weekStart.setDate(currentDate.getDate() - dayOfWeek);

                  const weekDays: Date[] = [];
                  for (let i = 0; i < 7; i++) {
                    const day = new Date(weekStart);
                    day.setDate(weekStart.getDate() + i);
                    weekDays.push(day);
                  }

                  // Get leaves for a specific day
                  const getLeavesForDay = (date: Date) => {
                    const dateStr = date.toISOString().split("T")[0];
                    return filteredLeaves.filter((leave) => {
                      const leaveStart = new Date(leave.startDate);
                      const leaveEnd = new Date(leave.endDate);
                      const currentDay = new Date(dateStr);
                      return currentDay >= leaveStart && currentDay <= leaveEnd;
                    });
                  };

                  // Calculate availability for a day
                  const getAvailabilityForDay = (date: Date) => {
                    const leavesOnDay = getLeavesForDay(date);

                    // Get total team size
                    let totalTeam = teamMembers.length;
                    if (currentUser?.role === "manager") {
                      totalTeam = (teamMembers || []).filter(
                        (emp) => emp.department === currentUser.department,
                      ).length;
                    } else if (calendarDepartmentFilter !== "all") {
                      totalTeam = (teamMembers || []).filter(
                        (emp) => emp.department === calendarDepartmentFilter,
                      ).length;
                    }

                    const onLeaveCount = leavesOnDay.filter(
                      (l) => l.status === "approved",
                    ).length;
                    const percentage =
                      totalTeam > 0
                        ? Math.round(
                            ((totalTeam - onLeaveCount) / totalTeam) * 100,
                          )
                        : 100;

                    let status: "normal" | "low" | "critical" = "normal";
                    if (percentage < 50) status = "critical";
                    else if (percentage < 70) status = "low";

                    return { percentage, onLeaveCount, status };
                  };

                  return (
                    <div className="space-y-4">
                      {/* Week Grid */}
                      <div className="grid grid-cols-7 gap-2">
                        {/* Day headers */}
                        {weekDays.map((day) => {
                          const isToday =
                            day.toDateString() === new Date().toDateString();
                          return (
                            <div
                              key={day.toISOString()}
                              className="text-center"
                            >
                              <div
                                className={`text-sm font-semibold py-1 ${isToday ? "text-primary" : "text-muted-foreground"}`}
                              >
                                {day.toLocaleDateString("en-US", {
                                  weekday: "short",
                                })}
                              </div>
                              <div
                                className={`text-lg font-bold ${isToday ? "text-primary" : ""}`}
                              >
                                {day.getDate()}
                              </div>
                            </div>
                          );
                        })}

                        {/* Day columns */}
                        {weekDays.map((day) => {
                          const isToday =
                            day.toDateString() === new Date().toDateString();
                          const availability = getAvailabilityForDay(day);
                          const leavesOnDay = getLeavesForDay(day);

                          return (
                            <div
                              key={day.toISOString()}
                              className={`min-h-[400px] p-2 border rounded-lg ${
                                isToday
                                  ? "border-primary border-2 bg-primary/5"
                                  : "bg-white"
                              } ${
                                availability.status === "critical"
                                  ? "bg-red-50"
                                  : availability.status === "low"
                                    ? "bg-yellow-50"
                                    : ""
                              }`}
                            >
                              {/* Availability Badge */}
                              {availability.onLeaveCount > 0 && (
                                <div
                                  className={`text-xs px-2 py-1 rounded mb-2 text-center ${
                                    availability.status === "critical"
                                      ? "bg-red-100 text-red-700"
                                      : availability.status === "low"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : "bg-slate-100 text-slate-700"
                                  }`}
                                >
                                  {availability.percentage}% available
                                </div>
                              )}

                              {/* Leaves on this day */}
                              <div className="space-y-2">
                                {leavesOnDay.length === 0 ? (
                                  <div className="text-xs text-muted-foreground text-center py-4">
                                    No leaves
                                  </div>
                                ) : (
                                  leavesOnDay.map((leave) => {
                                    const employee = (teamMembers || []).find(
                                      (emp) => emp.id === leave.employeeId,
                                    );
                                    return (
                                      <div
                                        key={leave.id}
                                        className={`text-xs p-2 rounded border ${
                                          leave.type === "vacation"
                                            ? "bg-blue-50 border-blue-200"
                                            : leave.type === "sick"
                                              ? "bg-red-50 border-red-200"
                                              : leave.type === "personal"
                                                ? "bg-yellow-50 border-yellow-200"
                                                : "bg-slate-50 border-slate-200"
                                        } ${
                                          leave.status === "pending"
                                            ? "border-dashed"
                                            : ""
                                        }`}
                                      >
                                        <div className="font-medium truncate">
                                          {employee?.name || leave.employeeName}
                                        </div>
                                        <div className="text-muted-foreground capitalize">
                                          {leave.type}
                                        </div>
                                        {leave.status === "pending" && (
                                          <div className="text-orange-600 mt-1">
                                            Pending
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Legend */}
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded"></div>
                          <span>Vacation</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-[var(--status-sick)_/_0.2] border border-[var(--status-sick)] rounded"></div>
                          <span>Sick Leave</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-[var(--status-late)_/_0.2] border border-[var(--status-late)] rounded"></div>
                          <span>Personal</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-white border border-dashed border-slate-400 rounded"></div>
                          <span>Pending</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Warning className="w-4 h-4 text-yellow-600" />
                          <span>&lt; 70% availability</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Warning className="w-4 h-4 text-red-600" />
                          <span>&lt; 50% availability (Critical)</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {/* Day View */}
              {calendarView === "day" &&
                (() => {
                  // Get all leave requests filtered by current selections
                  const filteredLeaves = (leaveRequests || []).filter(
                    (leave) => {
                      // Department filter (for managers - only their team)
                      if (currentUser?.role === "manager") {
                        const employee = (teamMembers || []).find(
                          (emp) => emp.id === leave.employeeId,
                        );
                        if (
                          employee &&
                          employee.department !== currentUser.department
                        ) {
                          return false;
                        }
                      }
                      if (calendarDepartmentFilter !== "all") {
                        const employee = (teamMembers || []).find(
                          (emp) => emp.id === leave.employeeId,
                        );
                        if (
                          !employee ||
                          employee.department !== calendarDepartmentFilter
                        ) {
                          return false;
                        }
                      }
                      // Status filter
                      if (
                        calendarStatusFilter !== "all" &&
                        leave.status !== calendarStatusFilter
                      ) {
                        return false;
                      }
                      // Type filter
                      if (
                        calendarTypeFilter !== "all" &&
                        leave.type !== calendarTypeFilter
                      ) {
                        return false;
                      }
                      return true;
                    },
                  );

                  const currentDate = new Date(calendarDate);
                  const dateStr = currentDate.toISOString().split("T")[0];

                  // Get leaves for this day
                  const leavesOnDay = filteredLeaves.filter((leave) => {
                    const leaveStart = new Date(leave.startDate);
                    const leaveEnd = new Date(leave.endDate);
                    const dayDate = new Date(dateStr);
                    return dayDate >= leaveStart && dayDate <= leaveEnd;
                  });

                  // Get total team size
                  let totalTeam = teamMembers.length;
                  if (currentUser?.role === "manager") {
                    totalTeam = (teamMembers || []).filter(
                      (emp) => emp.department === currentUser.department,
                    ).length;
                  } else if (calendarDepartmentFilter !== "all") {
                    totalTeam = (teamMembers || []).filter(
                      (emp) => emp.department === calendarDepartmentFilter,
                    ).length;
                  }

                  const approvedLeavesCount = leavesOnDay.filter(
                    (l) => l.status === "approved",
                  ).length;
                  const availableCount = totalTeam - approvedLeavesCount;
                  const availabilityPercentage =
                    totalTeam > 0
                      ? Math.round((availableCount / totalTeam) * 100)
                      : 100;

                  let availabilityStatus: "normal" | "low" | "critical" =
                    "normal";
                  if (availabilityPercentage < 50)
                    availabilityStatus = "critical";
                  else if (availabilityPercentage < 70)
                    availabilityStatus = "low";

                  // Group leaves by type
                  const leavesByType = {
                    vacation: leavesOnDay.filter((l) => l.type === "vacation"),
                    sick: leavesOnDay.filter((l) => l.type === "sick"),
                    personal: leavesOnDay.filter((l) => l.type === "personal"),
                    other: leavesOnDay.filter(
                      (l) => !["vacation", "sick", "personal"].includes(l.type),
                    ),
                  };

                  return (
                    <div className="space-y-6">
                      {/* Day Overview Stats */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-center">
                              <div className="text-2xl font-bold">
                                {totalTeam}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Total Team
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-green-600">
                                {availableCount}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Available
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-orange-600">
                                {approvedLeavesCount}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                On Leave
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-center">
                              <div
                                className={`text-2xl font-bold ${
                                  availabilityStatus === "critical"
                                    ? "text-red-600"
                                    : availabilityStatus === "low"
                                      ? "text-yellow-600"
                                      : "text-green-600"
                                }`}
                              >
                                {availabilityPercentage}%
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Availability
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Leaves Details */}
                      <div className="space-y-4">
                        {leavesOnDay.length === 0 ? (
                          <Card>
                            <CardContent className="pt-6">
                              <div className="text-center py-8 text-muted-foreground">
                                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                                <p className="font-medium">
                                  Full Team Available
                                </p>
                                <p className="text-sm mt-1">
                                  No team members on leave today
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        ) : (
                          <>
                            {/* Vacation Leaves */}
                            {leavesByType.vacation.length > 0 && (
                              <Card>
                                <CardHeader>
                                  <CardTitle className="text-base flex items-center gap-2">
                                    <div className="w-3 h-3 bg-blue-500 rounded"></div>
                                    Vacation Leaves (
                                    {leavesByType.vacation.length})
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-2">
                                    {leavesByType.vacation.map((leave) => {
                                      const employee = (teamMembers || []).find(
                                        (emp) => emp.id === leave.employeeId,
                                      );
                                      return (
                                        <div
                                          key={leave.id}
                                          className="flex items-center justify-between p-3 border rounded-lg bg-blue-50 border-blue-200"
                                        >
                                          <div className="flex items-center gap-3">
                                            <User className="w-5 h-5 text-blue-600" />
                                            <div>
                                              <div className="font-medium">
                                                {employee?.name ||
                                                  leave.employeeName}
                                              </div>
                                              <div className="text-sm text-muted-foreground">
                                                {new Date(
                                                  leave.startDate,
                                                ).toLocaleDateString()}{" "}
                                                -{" "}
                                                {new Date(
                                                  leave.endDate,
                                                ).toLocaleDateString()}
                                              </div>
                                            </div>
                                          </div>
                                          <Badge
                                            variant={
                                              leave.status === "approved"
                                                ? "default"
                                                : "secondary"
                                            }
                                          >
                                            {leave.status}
                                          </Badge>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            {/* Sick Leaves */}
                            {leavesByType.sick.length > 0 && (
                              <Card>
                                <CardHeader>
                                  <CardTitle className="text-base flex items-center gap-2">
                                    <div className="w-3 h-3 bg-[var(--status-sick)] rounded"></div>
                                    Sick Leaves ({leavesByType.sick.length})
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-2">
                                    {leavesByType.sick.map((leave) => {
                                      const employee = (teamMembers || []).find(
                                        (emp) => emp.id === leave.employeeId,
                                      );
                                      return (
                                        <div
                                          key={leave.id}
                                          className="flex items-center justify-between p-3 border rounded-lg bg-red-50 border-red-200"
                                        >
                                          <div className="flex items-center gap-3">
                                            <User className="w-5 h-5 text-red-600" />
                                            <div>
                                              <div className="font-medium">
                                                {employee?.name ||
                                                  leave.employeeName}
                                              </div>
                                              <div className="text-sm text-muted-foreground">
                                                {new Date(
                                                  leave.startDate,
                                                ).toLocaleDateString()}{" "}
                                                -{" "}
                                                {new Date(
                                                  leave.endDate,
                                                ).toLocaleDateString()}
                                              </div>
                                            </div>
                                          </div>
                                          <Badge
                                            variant={
                                              leave.status === "approved"
                                                ? "default"
                                                : "secondary"
                                            }
                                          >
                                            {leave.status}
                                          </Badge>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            {/* Personal Leaves */}
                            {leavesByType.personal.length > 0 && (
                              <Card>
                                <CardHeader>
                                  <CardTitle className="text-base flex items-center gap-2">
                                    <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                                    Personal Leaves (
                                    {leavesByType.personal.length})
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-2">
                                    {leavesByType.personal.map((leave) => {
                                      const employee = (teamMembers || []).find(
                                        (emp) => emp.id === leave.employeeId,
                                      );
                                      return (
                                        <div
                                          key={leave.id}
                                          className="flex items-center justify-between p-3 border rounded-lg bg-yellow-50 border-yellow-200"
                                        >
                                          <div className="flex items-center gap-3">
                                            <User className="w-5 h-5 text-yellow-600" />
                                            <div>
                                              <div className="font-medium">
                                                {employee?.name ||
                                                  leave.employeeName}
                                              </div>
                                              <div className="text-sm text-muted-foreground">
                                                {new Date(
                                                  leave.startDate,
                                                ).toLocaleDateString()}{" "}
                                                -{" "}
                                                {new Date(
                                                  leave.endDate,
                                                ).toLocaleDateString()}
                                              </div>
                                            </div>
                                          </div>
                                          <Badge
                                            variant={
                                              leave.status === "approved"
                                                ? "default"
                                                : "secondary"
                                            }
                                          >
                                            {leave.status}
                                          </Badge>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            {/* Other Leaves */}
                            {leavesByType.other.length > 0 && (
                              <Card>
                                <CardHeader>
                                  <CardTitle className="text-base flex items-center gap-2">
                                    <div className="w-3 h-3 bg-slate-500 rounded"></div>
                                    Other Leaves ({leavesByType.other.length})
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-2">
                                    {leavesByType.other.map((leave) => {
                                      const employee = (teamMembers || []).find(
                                        (emp) => emp.id === leave.employeeId,
                                      );
                                      return (
                                        <div
                                          key={leave.id}
                                          className="flex items-center justify-between p-3 border rounded-lg bg-slate-50 border-slate-200"
                                        >
                                          <div className="flex items-center gap-3">
                                            <User className="w-5 h-5 text-slate-600" />
                                            <div>
                                              <div className="font-medium">
                                                {employee?.name ||
                                                  leave.employeeName}
                                              </div>
                                              <div className="text-sm text-muted-foreground capitalize">
                                                {leave.type}
                                              </div>
                                              <div className="text-sm text-muted-foreground">
                                                {new Date(
                                                  leave.startDate,
                                                ).toLocaleDateString()}{" "}
                                                -{" "}
                                                {new Date(
                                                  leave.endDate,
                                                ).toLocaleDateString()}
                                              </div>
                                            </div>
                                          </div>
                                          <Badge
                                            variant={
                                              leave.status === "approved"
                                                ? "default"
                                                : "secondary"
                                            }
                                          >
                                            {leave.status}
                                          </Badge>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
            </CardContent>
          </Card>

          {/* Team Availability Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Team Size
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {currentUser?.role === "manager"
                    ? (teamMembers || []).filter(
                        (emp) => emp.department === currentUser.department,
                      ).length
                    : calendarDepartmentFilter !== "all"
                      ? (teamMembers || []).filter(
                          (emp) => emp.department === calendarDepartmentFilter,
                        ).length
                      : teamMembers.length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  On Leave Today
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {
                    (leaveRequests || []).filter((leave) => {
                      if (leave.status !== "approved") return false;
                      const today = new Date().toISOString().split("T")[0];
                      const leaveStart = new Date(leave.startDate)
                        .toISOString()
                        .split("T")[0];
                      const leaveEnd = new Date(leave.endDate)
                        .toISOString()
                        .split("T")[0];
                      return today >= leaveStart && today <= leaveEnd;
                    }).length
                  }
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Pending Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {
                    (leaveRequests || []).filter(
                      (leave) => leave.status === "pending",
                    ).length
                  }
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Team Attendance Section - For Managers, HR, and Admins */}
      {activeSection === "team-attendance" && (
        <TeamAttendanceSection
          currentUser={currentUser}
          teamMembers={teamMembers || []}
        />
      )}

      {/* Timesheet Section */}
      {activeSection === "timesheet" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Timesheet Management
                  </CardTitle>
                  <CardDescription>
                    Log and track your work hours
                  </CardDescription>
                </div>
                <Button onClick={() => openTimesheetDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Log Hours
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Weekly Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">
                    {timesheetEntries.reduce(
                      (sum, entry) => sum + entry.hours,
                      0,
                    )}
                    h
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Total Hours
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">
                    {timesheetEntries
                      .reduce(
                        (sum, entry) => sum + (entry.overtimeHours || 0),
                        0,
                      )
                      .toFixed(1)}
                    h
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Overtime Hours
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">
                    {overtimeEntries
                      .filter((ot) => ot.status === "approved")
                      .reduce((sum, ot) => sum + (ot.overtimeHours || 0), 0)
                      .toFixed(1)}
                    h
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Approved Overtime
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">
                    {
                      overtimeEntries.filter((ot) => ot.status === "pending")
                        .length
                    }
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Pending Approval
                  </div>
                </div>
              </div>

              {/* Recent Entries */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Entries</h3>
                <div className="space-y-2">
                  {timesheetEntries.slice(0, 10).map((entry) => {
                    const overtimeHours = entry.hours > 8 ? entry.hours - 8 : 0;
                    const hasOvertime = overtimeHours > 0;
                    const overtimeEntry = overtimeEntries.find(
                      (ot) => ot.timesheetEntryId === entry.id,
                    );

                    return (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium">{entry.project}</div>
                            <Badge variant="secondary">{entry.hours}h</Badge>
                            {hasOvertime && (
                              <Badge
                                variant="outline"
                                className="bg-orange-50 text-orange-700 border-orange-200"
                              >
                                +{overtimeHours.toFixed(1)}h OT
                              </Badge>
                            )}
                            {overtimeEntry && (
                              <Badge
                                variant={
                                  overtimeEntry.status === "approved"
                                    ? "default"
                                    : overtimeEntry.status === "rejected"
                                      ? "destructive"
                                      : "secondary"
                                }
                              >
                                OT {overtimeEntry.status}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {entry.task}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {entry.date}
                          </div>
                        </div>
                        {hasOvertime &&
                          !overtimeEntry &&
                          entry.status === "submitted" && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="outline">
                                  Submit OT
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>
                                    Submit Overtime Request
                                  </DialogTitle>
                                  <DialogDescription>
                                    Request approval for{" "}
                                    {overtimeHours.toFixed(1)} overtime hours
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                                    <div>
                                      <div className="text-sm text-muted-foreground">
                                        Date
                                      </div>
                                      <div className="font-medium">
                                        {entry.date}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-muted-foreground">
                                        Project
                                      </div>
                                      <div className="font-medium">
                                        {entry.project}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-muted-foreground">
                                        Total Hours
                                      </div>
                                      <div className="font-medium">
                                        {entry.hours}h
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-muted-foreground">
                                        Overtime Hours
                                      </div>
                                      <div className="font-medium text-orange-600">
                                        {overtimeHours.toFixed(1)}h
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium">
                                      Reason for Overtime
                                    </label>
                                    <textarea
                                      id={`overtime-reason-${entry.id}`}
                                      placeholder="Explain why overtime was necessary..."
                                      className="w-full px-3 py-2 border rounded-md text-base min-h-[80px] mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium">
                                      Compensation Type
                                    </label>
                                    <select
                                      id={`overtime-comp-${entry.id}`}
                                      className="w-full px-3 py-2 border rounded-md text-base mt-1"
                                      defaultValue="pay"
                                    >
                                      <option value="pay">
                                        Overtime Pay (1.5x)
                                      </option>
                                      <option value="compTime">
                                        Compensatory Time Off
                                      </option>
                                    </select>
                                  </div>
                                  <Button
                                    className="w-full"
                                    onClick={() => {
                                      const reason = (
                                        document.getElementById(
                                          `overtime-reason-${entry.id}`,
                                        ) as HTMLTextAreaElement
                                      )?.value;
                                      const compType = (
                                        document.getElementById(
                                          `overtime-comp-${entry.id}`,
                                        ) as HTMLSelectElement
                                      )?.value as "pay" | "compTime";

                                      if (
                                        !reason ||
                                        reason.trim().length < 10
                                      ) {
                                        toast.error(
                                          "Please provide a detailed reason (at least 10 characters)",
                                        );
                                        return;
                                      }

                                      const overtimeEntry: OvertimeEntry = {
                                        id: `ot-${Date.now()}`,
                                        employeeId: currentUser?.id || "",
                                        employeeName: currentUser?.name || "",
                                        date: entry.date,
                                        regularHours: 8,
                                        overtimeHours: overtimeHours,
                                        reason: reason,
                                        status: "pending",
                                        timesheetEntryId: entry.id,
                                        project: entry.project,
                                        compensationType: compType,
                                        createdAt: new Date().toISOString(),
                                      };

                                      setOvertimeEntries([
                                        ...overtimeEntries,
                                        overtimeEntry,
                                      ]);

                                      // Update timesheet entry to mark overtime as submitted
                                      const updatedEntries =
                                        timesheetEntries.map((e) =>
                                          e.id === entry.id
                                            ? {
                                                ...e,
                                                overtimeSubmitted: true,
                                              }
                                            : e,
                                        );
                                      setTimesheetEntries(updatedEntries);

                                      toast.success(
                                        "Overtime request submitted for approval",
                                      );
                                      document
                                        .getElementById(
                                          `overtime-reason-${entry.id}`,
                                        )!
                                        .closest("form")
                                        ?.reset();
                                    }}
                                  >
                                    Submit Overtime Request
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                      </div>
                    );
                  })}
                  {timesheetEntries.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No timesheet entries yet. Start logging your hours!</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Learning Section */}
      {activeSection === "learning" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                Learning & Development
              </CardTitle>
              <CardDescription>
                Access training materials and track your progress
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Learning Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">0</div>
                  <div className="text-sm text-muted-foreground">
                    Courses Completed
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">0h</div>
                  <div className="text-sm text-muted-foreground">
                    Learning Hours
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold">0</div>
                  <div className="text-sm text-muted-foreground">
                    Certifications
                  </div>
                </div>
              </div>

              {/* Available Courses */}
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  Recommended Courses
                </h3>
                <div className="text-center py-12 text-muted-foreground">
                  <GraduationCap className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">
                    Learning Content Coming Soon
                  </p>
                  <p className="text-sm">
                    Training courses and materials will be available here
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Approvals Section - For Managers, HR, and Admins */}
      {activeSection === "approvals" &&
        (currentUser?.role === "manager" ||
          currentUser?.role === "hr" ||
          currentUser?.role === "admin" ||
          currentUser?.role === "superadmin") && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Approvals Dashboard
                </CardTitle>
                <CardDescription>
                  Review and approve overtime requests from your team
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Approval Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      {
                        overtimeEntries.filter((ot) => ot.status === "pending")
                          .length
                      }
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Pending Approvals
                    </div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {
                        overtimeEntries.filter((ot) => ot.status === "approved")
                          .length
                      }
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Approved Today
                    </div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-2xl font-bold">
                      {overtimeEntries
                        .filter((ot) => ot.status === "pending")
                        .reduce((sum, ot) => sum + (ot.overtimeHours || 0), 0)
                        .toFixed(1)}
                      h
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Pending OT Hours
                    </div>
                  </div>
                </div>

                {/* Overtime Requests Table */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Pending Overtime Requests
                  </h3>
                  <div className="space-y-3">
                    {overtimeEntries.filter((ot) => ot.status === "pending")
                      .length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <CheckCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">
                          No pending approvals
                        </p>
                        <p className="text-sm">
                          All overtime requests have been reviewed
                        </p>
                      </div>
                    ) : (
                      overtimeEntries
                        .filter((ot) => ot.status === "pending")
                        .map((overtime) => (
                          <Card
                            key={overtime.id}
                            className="border-l-4 border-l-orange-500"
                          >
                            <CardContent className="p-4">
                              <div className="flex flex-col sm:flex-row justify-between gap-4">
                                <div className="flex-1 space-y-3">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <h4 className="font-semibold text-lg">
                                        {overtime.employeeName}
                                      </h4>
                                      <p className="text-sm text-muted-foreground">
                                        {overtime.date}
                                      </p>
                                    </div>
                                    <Badge
                                      variant="secondary"
                                      className="bg-orange-100 text-orange-700"
                                    >
                                      {(overtime.overtimeHours || 0).toFixed(1)}
                                      h OT
                                    </Badge>
                                  </div>

                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="text-muted-foreground">
                                        Project:
                                      </span>
                                      <p className="font-medium">
                                        {overtime.project || "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">
                                        Compensation:
                                      </span>
                                      <p className="font-medium">
                                        {overtime.compensationType === "pay"
                                          ? "Overtime Pay (1.5x)"
                                          : "Comp Time Off"}
                                      </p>
                                    </div>
                                    <div className="col-span-2">
                                      <span className="text-muted-foreground">
                                        Reason:
                                      </span>
                                      <p className="font-medium mt-1">
                                        {overtime.reason}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex sm:flex-col gap-2">
                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <Button
                                        size="sm"
                                        className="flex-1 sm:flex-none"
                                      >
                                        <Check className="w-4 h-4 mr-1" />
                                        Approve
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-[95vw] sm:max-w-md">
                                      <DialogHeader>
                                        <DialogTitle>
                                          Approve Overtime Request
                                        </DialogTitle>
                                        <DialogDescription>
                                          Approve{" "}
                                          {(
                                            overtime.overtimeHours || 0
                                          ).toFixed(1)}{" "}
                                          hours of overtime for{" "}
                                          {overtime.employeeName}
                                        </DialogDescription>
                                      </DialogHeader>
                                      <div className="space-y-4">
                                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                          <p className="text-sm">
                                            This will approve the overtime
                                            request. The employee will be
                                            notified.
                                          </p>
                                        </div>
                                        <Button
                                          className="w-full"
                                          onClick={() => {
                                            const updated = overtimeEntries.map(
                                              (ot) =>
                                                ot.id === overtime.id
                                                  ? {
                                                      ...ot,
                                                      status:
                                                        "approved" as const,
                                                      approvedBy:
                                                        currentUser?.name,
                                                      approvedAt:
                                                        new Date().toISOString(),
                                                    }
                                                  : ot,
                                            );
                                            setOvertimeEntries(updated);
                                            toast.success(
                                              `Approved ${(overtime.overtimeHours || 0).toFixed(1)}h overtime for ${overtime.employeeName}`,
                                            );

                                            // Create notification for employee
                                            const notification: Notification = {
                                              id: `notif-${Date.now()}`,
                                              title:
                                                "Overtime Request Approved",
                                              message: `Your overtime request for ${(overtime.overtimeHours || 0).toFixed(1)} hours on ${overtime.date} has been approved by ${currentUser?.name}`,
                                              type: "success",
                                              timestamp:
                                                new Date().toISOString(),
                                              read: false,
                                            };
                                            setNotifications([
                                              notification,
                                              ...notifications,
                                            ]);
                                          }}
                                        >
                                          Confirm Approval
                                        </Button>
                                      </div>
                                    </DialogContent>
                                  </Dialog>

                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1 sm:flex-none"
                                      >
                                        <X className="w-4 h-4 mr-1" />
                                        Reject
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-[95vw] sm:max-w-md">
                                      <DialogHeader>
                                        <DialogTitle>
                                          Reject Overtime Request
                                        </DialogTitle>
                                        <DialogDescription>
                                          Reject{" "}
                                          {(
                                            overtime.overtimeHours || 0
                                          ).toFixed(1)}{" "}
                                          hours of overtime for{" "}
                                          {overtime.employeeName}
                                        </DialogDescription>
                                      </DialogHeader>
                                      <div className="space-y-4">
                                        <div>
                                          <label className="text-sm font-medium">
                                            Reason for Rejection
                                          </label>
                                          <textarea
                                            id={`reject-reason-${overtime.id}`}
                                            placeholder="Explain why this overtime is being rejected..."
                                            className="w-full px-3 py-2 border rounded-md text-base min-h-[80px] mt-1"
                                          />
                                        </div>
                                        <Button
                                          variant="destructive"
                                          className="w-full"
                                          onClick={() => {
                                            const reason = (
                                              document.getElementById(
                                                `reject-reason-${overtime.id}`,
                                              ) as HTMLTextAreaElement
                                            )?.value;

                                            if (
                                              !reason ||
                                              reason.trim().length < 10
                                            ) {
                                              toast.error(
                                                "Please provide a reason for rejection (at least 10 characters)",
                                              );
                                              return;
                                            }

                                            const updated = overtimeEntries.map(
                                              (ot) =>
                                                ot.id === overtime.id
                                                  ? {
                                                      ...ot,
                                                      status:
                                                        "rejected" as const,
                                                      approvedBy:
                                                        currentUser?.name,
                                                      approvedAt:
                                                        new Date().toISOString(),
                                                      rejectionReason: reason,
                                                    }
                                                  : ot,
                                            );
                                            setOvertimeEntries(updated);
                                            toast.error(
                                              `Rejected overtime request for ${overtime.employeeName}`,
                                            );

                                            // Create notification for employee
                                            const notification: Notification = {
                                              id: `notif-${Date.now()}`,
                                              title:
                                                "Overtime Request Rejected",
                                              message: `Your overtime request for ${(overtime.overtimeHours || 0).toFixed(1)} hours on ${overtime.date} was rejected: ${reason}`,
                                              type: "error",
                                              timestamp:
                                                new Date().toISOString(),
                                              read: false,
                                            };
                                            setNotifications([
                                              notification,
                                              ...notifications,
                                            ]);
                                          }}
                                        >
                                          Confirm Rejection
                                        </Button>
                                      </div>
                                    </DialogContent>
                                  </Dialog>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                    )}
                  </div>
                </div>

                {/* Recently Reviewed */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Recently Reviewed
                  </h3>
                  <div className="space-y-2">
                    {overtimeEntries
                      .filter((ot) => ot.status !== "pending")
                      .slice(0, 5)
                      .map((overtime) => (
                        <div
                          key={overtime.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">
                                {overtime.employeeName}
                              </span>
                              <Badge variant="secondary">
                                {(overtime.overtimeHours || 0).toFixed(1)}h
                              </Badge>
                              <Badge
                                variant={
                                  overtime.status === "approved"
                                    ? "default"
                                    : "destructive"
                                }
                              >
                                {overtime.status}
                              </Badge>
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {overtime.date} • {overtime.project || "General"}
                            </div>
                          </div>
                        </div>
                      ))}
                    {overtimeEntries.filter((ot) => ot.status !== "pending")
                      .length === 0 && (
                      <p className="text-center py-4 text-muted-foreground text-sm">
                        No reviewed requests yet
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      {/* Profile Section */}
      {activeSection === "profile" && (
        <UserProfileSection
          currentUser={currentUser}
          employeeProfiles={employeeProfiles}
          setEmployeeProfiles={setEmployeeProfiles}
        />
      )}

      {/* Settings Section */}
      {activeSection === "settings" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gear className="w-5 h-5" />
                Settings
              </CardTitle>
              <CardDescription>
                Manage your preferences and account settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Profile Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Name</label>
                    <input
                      type="text"
                      value={userProfile.name}
                      disabled
                      className="w-full p-2 border rounded-lg mt-1 bg-muted"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <input
                      type="email"
                      value={userProfile.email}
                      disabled
                      className="w-full p-2 border rounded-lg mt-1 bg-muted"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Department</label>
                    <input
                      type="text"
                      value={userProfile.department}
                      disabled
                      className="w-full p-2 border rounded-lg mt-1 bg-muted"
                    />
                  </div>
                </div>
              </div>

              {/* Language Preferences */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">
                  Language Preferences
                </h3>
                <div>
                  <label className="text-sm font-medium">Select Language</label>
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant={i18n.language === "en" ? "default" : "outline"}
                      onClick={() => handleLanguageChange("en")}
                      size="sm"
                    >
                      English
                    </Button>
                    <Button
                      variant={i18n.language === "es" ? "default" : "outline"}
                      onClick={() => handleLanguageChange("es")}
                      size="sm"
                    >
                      Español
                    </Button>
                    <Button
                      variant={i18n.language === "fr" ? "default" : "outline"}
                      onClick={() => handleLanguageChange("fr")}
                      size="sm"
                    >
                      Français
                    </Button>
                  </div>
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">
                  Notification Preferences
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Email Notifications</div>
                      <div className="text-sm text-muted-foreground">
                        Receive updates via email
                      </div>
                    </div>
                    <Button
                      variant={
                        notificationPreferences.email ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() =>
                        setNotificationPreferences((prev) => ({
                          ...prev,
                          email: !prev.email,
                        }))
                      }
                    >
                      {notificationPreferences.email ? "On" : "Off"}
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Push Notifications</div>
                      <div className="text-sm text-muted-foreground">
                        Browser push notifications
                      </div>
                    </div>
                    <Button
                      variant={
                        notificationPreferences.push ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() =>
                        setNotificationPreferences((prev) => ({
                          ...prev,
                          push: !prev.push,
                        }))
                      }
                    >
                      {notificationPreferences.push ? "On" : "Off"}
                    </Button>
                  </div>

                  {/* Auto-Checkout Notification Preferences */}
                  <NotificationPreferences />

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">In-App Notifications</div>
                      <div className="text-sm text-muted-foreground">
                        Show notifications in app
                      </div>
                    </div>
                    <Button
                      variant={
                        notificationPreferences.inApp ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() =>
                        setNotificationPreferences((prev) => ({
                          ...prev,
                          inApp: !prev.inApp,
                        }))
                      }
                    >
                      {notificationPreferences.inApp ? "On" : "Off"}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setShowChangePassword(true)}
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={restartOnboarding}
                  >
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Restart Onboarding Tour
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setKeyboardShortcutsOpen(true)}
                  >
                    <Keyboard className="w-4 h-4 mr-2" />
                    View Keyboard Shortcuts
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setAboutDialogOpen(true)}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    About FlowHR
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Organization Settings - Admin/SuperAdmin Only */}
          {(currentUser?.role === "admin" ||
            currentUser?.role === "superadmin") &&
            currentOrg && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Organization Settings
                  </CardTitle>
                  <CardDescription>
                    Manage your organization configuration
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Basic Info */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Basic Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">
                          Organization Name
                        </label>
                        <Input
                          value={currentOrg.name}
                          onChange={(e) =>
                            handleUpdateOrganization(currentOrg.id, {
                              name: e.target.value,
                            })
                          }
                          placeholder="Enter organization name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Timezone</label>
                        <Input
                          value={currentOrg.timezone}
                          onChange={(e) =>
                            handleUpdateOrganization(currentOrg.id, {
                              timezone: e.target.value,
                            })
                          }
                          placeholder="e.g., America/New_York"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Currency</label>
                        <Input
                          value={currentOrg.currency}
                          onChange={(e) =>
                            handleUpdateOrganization(currentOrg.id, {
                              currency: e.target.value,
                            })
                          }
                          placeholder="e.g., USD"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Branding */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold mb-4">Branding</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">
                          Primary Color
                        </label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="color"
                            value={currentOrg.primaryColor || "#3b82f6"}
                            onChange={(e) =>
                              handleUpdateOrganization(currentOrg.id, {
                                primaryColor: e.target.value,
                              })
                            }
                            className="w-20 h-10"
                          />
                          <Input
                            value={currentOrg.primaryColor || "#3b82f6"}
                            onChange={(e) =>
                              handleUpdateOrganization(currentOrg.id, {
                                primaryColor: e.target.value,
                              })
                            }
                            placeholder="#3b82f6"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Logo URL</label>
                        <Input
                          value={currentOrg.logo || ""}
                          onChange={(e) =>
                            handleUpdateOrganization(currentOrg.id, {
                              logo: e.target.value,
                            })
                          }
                          placeholder="https://example.com/logo.png"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">
                          Custom Domain
                        </label>
                        <Input
                          value={currentOrg.domain || ""}
                          onChange={(e) =>
                            handleUpdateOrganization(currentOrg.id, {
                              domain: e.target.value,
                            })
                          }
                          placeholder="hr.yourcompany.com"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Work Settings */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Work Configuration
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">
                          Working Hours Per Day
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="24"
                          value={currentOrg.settings.workingHoursPerDay}
                          onChange={(e) =>
                            handleUpdateOrganization(currentOrg.id, {
                              settings: {
                                ...currentOrg.settings,
                                workingHoursPerDay: parseInt(e.target.value),
                              },
                            })
                          }
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">
                          Working Days Per Week
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="7"
                          value={currentOrg.settings.workingDaysPerWeek}
                          onChange={(e) =>
                            handleUpdateOrganization(currentOrg.id, {
                              settings: {
                                ...currentOrg.settings,
                                workingDaysPerWeek: parseInt(e.target.value),
                              },
                            })
                          }
                          className="mt-1"
                        />
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">Enable Geofencing</div>
                            <div className="text-sm text-muted-foreground">
                              Track attendance based on location
                            </div>
                          </div>
                          <Button
                            variant={
                              currentOrg.settings.enableGeofencing
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() =>
                              handleUpdateOrganization(currentOrg.id, {
                                settings: {
                                  ...currentOrg.settings,
                                  enableGeofencing:
                                    !currentOrg.settings.enableGeofencing,
                                },
                              })
                            }
                          >
                            {currentOrg.settings.enableGeofencing
                              ? "On"
                              : "Off"}
                          </Button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">
                              Enable Overtime Tracking
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Allow overtime requests
                            </div>
                          </div>
                          <Button
                            variant={
                              currentOrg.settings.enableOvertimeTracking
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() =>
                              handleUpdateOrganization(currentOrg.id, {
                                settings: {
                                  ...currentOrg.settings,
                                  enableOvertimeTracking:
                                    !currentOrg.settings.enableOvertimeTracking,
                                },
                              })
                            }
                          >
                            {currentOrg.settings.enableOvertimeTracking
                              ? "On"
                              : "Off"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Info */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold mb-4">Subscription</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Plan:</span>
                        <Badge variant="outline">
                          {currentOrg.subscriptionPlan}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Status:</span>
                        <Badge
                          variant={
                            currentOrg.status === "active"
                              ? "default"
                              : "secondary"
                          }
                        >
                          {currentOrg.status}
                        </Badge>
                      </div>
                      {currentOrg.subscriptionExpiresAt && (
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Expires:</span>
                          <span className="text-sm">
                            {new Date(
                              currentOrg.subscriptionExpiresAt,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* User Invitations */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">
                        User Invitations
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setBulkInvitationDialogOpen(true)}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Bulk Invite
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setInvitationDialogOpen(true)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Send Invitation
                        </Button>
                      </div>
                    </div>

                    {/* Invitations List */}
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {(organizationInvitations || [])
                        .filter((inv) => inv.organizationId === currentOrgId)
                        .sort(
                          (a, b) =>
                            new Date(b.invitedAt).getTime() -
                            new Date(a.invitedAt).getTime(),
                        )
                        .map((invitation) => {
                          const isExpired =
                            new Date(invitation.expiresAt) < new Date();
                          const statusColors = {
                            pending: "bg-yellow-100 text-yellow-700",
                            accepted: "bg-green-100 text-green-700",
                            expired: "bg-gray-100 text-gray-700",
                            cancelled: "bg-red-100 text-red-700",
                          };

                          return (
                            <div
                              key={invitation.id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">
                                    {invitation.email}
                                  </span>
                                  <Badge
                                    className={
                                      statusColors[
                                        isExpired &&
                                        invitation.status === "pending"
                                          ? "expired"
                                          : invitation.status
                                      ]
                                    }
                                  >
                                    {isExpired &&
                                    invitation.status === "pending"
                                      ? "Expired"
                                      : invitation.status}
                                  </Badge>
                                  <Badge variant="outline">
                                    {invitation.role}
                                  </Badge>
                                </div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  Invited{" "}
                                  {new Date(
                                    invitation.invitedAt,
                                  ).toLocaleDateString()}{" "}
                                  • Expires{" "}
                                  {new Date(
                                    invitation.expiresAt,
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {invitation.status === "pending" &&
                                  !isExpired && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                          handleResendInvitation(invitation.id)
                                        }
                                      >
                                        Resend
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() =>
                                          handleCancelInvitation(invitation.id)
                                        }
                                      >
                                        Cancel
                                      </Button>
                                    </>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                      {(organizationInvitations || []).filter(
                        (inv) => inv.organizationId === currentOrgId,
                      ).length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          No invitations sent yet
                        </div>
                      )}
                    </div>

                    {/* Import from CSV */}
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-4"
                      onClick={() => setCsvImportDialogOpen(true)}
                    >
                      <UploadSimple className="w-4 h-4 mr-2" />
                      Import from CSV
                    </Button>
                  </div>

                  {/* Roles and Permissions */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">
                        Roles & Permissions
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleExportRolesMatrix}
                        >
                          <DownloadSimple className="w-4 h-4 mr-2" />
                          Export Matrix
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRoleAuditDialogOpen(true)}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          View Audit Log
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditingRole(null);
                            setRoleFormData({
                              name: "",
                              description: "",
                              permissions: [],
                              inheritsFrom: undefined,
                            });
                            setRoleDialogOpen(true);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create Role
                        </Button>
                      </div>
                    </div>

                    {/* Roles List */}
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {(customRoles || [])
                        .filter((role) => role.organizationId === currentOrgId)
                        .sort((a, b) =>
                          a.isCustom === b.isCustom ? 0 : a.isCustom ? 1 : -1,
                        )
                        .map((role) => {
                          const assignmentCount = (
                            roleAssignments || []
                          ).filter((a) => a.roleId === role.id).length;

                          return (
                            <div
                              key={role.id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">
                                    {role.name}
                                  </span>
                                  {!role.isCustom && (
                                    <Badge variant="secondary">System</Badge>
                                  )}
                                  <Badge variant="outline">
                                    {assignmentCount} users
                                  </Badge>
                                </div>
                                {role.description && (
                                  <div className="text-sm text-muted-foreground mt-1">
                                    {role.description}
                                  </div>
                                )}
                                <div className="text-xs text-muted-foreground mt-1">
                                  {role.permissions.length} modules •{" "}
                                  {role.permissions.reduce(
                                    (sum, p) => sum + p.actions.length,
                                    0,
                                  )}{" "}
                                  permissions
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCloneRole(role.id)}
                                >
                                  Clone
                                </Button>
                                {role.isCustom && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setEditingRole(role);
                                        setRoleFormData({
                                          name: role.name,
                                          description: role.description || "",
                                          permissions: role.permissions,
                                          inheritsFrom: role.inheritsFrom,
                                        });
                                        setRoleDialogOpen(true);
                                      }}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() =>
                                        handleDeleteCustomRole(role.id)
                                      }
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      {(customRoles || []).filter(
                        (r) => r.organizationId === currentOrgId,
                      ).length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          No roles defined yet
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Shift Management Section - Admin/SuperAdmin Only */}
                  {(currentUser?.role === "admin" ||
                    currentUser?.role === "superadmin") && (
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold mb-4">
                        Shift Management
                      </h3>
                      <ShiftManagement />
                    </div>
                  )}

                  {/* Shift Assignment Section - Admin/SuperAdmin Only */}
                  {(currentUser?.role === "admin" ||
                    currentUser?.role === "superadmin") && (
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold mb-4">
                        Shift Assignments
                      </h3>
                      <ShiftAssignmentManager />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
        </div>
      )}

      {/* Work From Home (WFH) Section */}
      {activeSection === "wfh" && (
        <div className="space-y-6">
          {/* My WFH Requests */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <House className="w-5 h-5" />
                    My WFH Requests
                  </CardTitle>
                  <CardDescription>
                    Request and manage your work from home days
                  </CardDescription>
                </div>
                <Button onClick={() => setWfhDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Request
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {wfhRequests.filter((r) => r.employeeId === currentUser?.id)
                .length === 0 ? (
                <div className="text-center py-12">
                  <House className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    No WFH Requests
                  </h3>
                  <p className="text-slate-500 mb-4">
                    Submit your first work from home request
                  </p>
                  <Button onClick={() => setWfhDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Request
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {wfhRequests
                    .filter((r) => r.employeeId === currentUser?.id)
                    .sort(
                      (a, b) =>
                        new Date(b.requestedAt).getTime() -
                        new Date(a.requestedAt).getTime(),
                    )
                    .map((request) => {
                      const statusColors = {
                        pending:
                          "bg-yellow-100 text-yellow-700 border-yellow-200",
                        approved:
                          "bg-green-100 text-green-700 border-green-200",
                        rejected: "bg-red-100 text-red-700 border-red-200",
                      };

                      return (
                        <Card
                          key={request.id}
                          className="hover:shadow-md transition-shadow"
                        >
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge
                                    className={statusColors[request.status]}
                                  >
                                    {request.status.toUpperCase()}
                                  </Badge>
                                  {request.recurring && (
                                    <Badge
                                      variant="outline"
                                      className="bg-blue-50 text-blue-700 border-blue-200"
                                    >
                                      Recurring
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm font-medium text-slate-900 mb-2">
                                  {new Date(
                                    request.startDate,
                                  ).toLocaleDateString()}{" "}
                                  -{" "}
                                  {new Date(
                                    request.endDate,
                                  ).toLocaleDateString()}
                                </p>
                                <p className="text-sm text-slate-600 mb-2">
                                  {request.reason}
                                </p>
                                {request.recurring && request.recurringDays && (
                                  <p className="text-xs text-slate-500">
                                    Recurring on:{" "}
                                    {request.recurringDays
                                      .map(
                                        (d) =>
                                          d.charAt(0).toUpperCase() +
                                          d.slice(1),
                                      )
                                      .join(", ")}
                                  </p>
                                )}
                                <p className="text-xs text-slate-500 mt-2">
                                  Requested:{" "}
                                  {new Date(
                                    request.requestedAt,
                                  ).toLocaleDateString()}
                                </p>
                                {request.reviewedAt && (
                                  <div className="mt-2 p-2 bg-slate-50 rounded-md">
                                    <p className="text-xs font-medium text-slate-700">
                                      Reviewed by {request.reviewedByName} on{" "}
                                      {new Date(
                                        request.reviewedAt,
                                      ).toLocaleDateString()}
                                    </p>
                                    {request.reviewNotes && (
                                      <p className="text-xs text-slate-600 mt-1">
                                        {request.reviewNotes}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                              {request.status === "pending" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleCancelWFHRequest(request.id)
                                  }
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team WFH Requests (for managers) */}
          {(currentUser?.role === "manager" ||
            currentUser?.role === "hr" ||
            currentUser?.role === "admin" ||
            currentUser?.role === "superadmin") && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Team WFH Requests
                </CardTitle>
                <CardDescription>
                  Review and approve work from home requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                {wfhRequests.filter((r) => r.status === "pending").length ===
                0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-slate-600">No pending requests</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {wfhRequests
                      .filter((r) => r.status === "pending")
                      .sort(
                        (a, b) =>
                          new Date(b.requestedAt).getTime() -
                          new Date(a.requestedAt).getTime(),
                      )
                      .map((request) => (
                        <Card
                          key={request.id}
                          className="border-l-4 border-l-yellow-400"
                        >
                          <CardContent className="pt-4">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-slate-900">
                                    {request.employeeName}
                                  </p>
                                  <p className="text-sm text-slate-600">
                                    {request.employeeDepartment}
                                  </p>
                                </div>
                                <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                                  PENDING
                                </Badge>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm font-medium">
                                  {new Date(
                                    request.startDate,
                                  ).toLocaleDateString()}{" "}
                                  -{" "}
                                  {new Date(
                                    request.endDate,
                                  ).toLocaleDateString()}
                                </p>
                                <p className="text-sm text-slate-600">
                                  {request.reason}
                                </p>
                                {request.recurring && request.recurringDays && (
                                  <p className="text-xs text-slate-500">
                                    Recurring:{" "}
                                    {request.recurringDays
                                      .map(
                                        (d) =>
                                          d.charAt(0).toUpperCase() +
                                          d.slice(1),
                                      )
                                      .join(", ")}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2 pt-2">
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button size="sm" variant="default">
                                      <Check className="w-4 h-4 mr-1" />
                                      Approve
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>
                                        Approve WFH Request
                                      </DialogTitle>
                                      <DialogDescription>
                                        Approve work from home request for{" "}
                                        {request.employeeName}
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                      <div className="space-y-2">
                                        <Label
                                          htmlFor={`approve-notes-${request.id}`}
                                        >
                                          Notes (optional)
                                        </Label>
                                        <textarea
                                          id={`approve-notes-${request.id}`}
                                          className="w-full min-h-[80px] rounded-md border border-slate-300 p-3"
                                          placeholder="Add any approval notes..."
                                        />
                                      </div>
                                    </div>
                                    <DialogFooter>
                                      <Button
                                        onClick={() => {
                                          const notes = (
                                            document.getElementById(
                                              `approve-notes-${request.id}`,
                                            ) as HTMLTextAreaElement
                                          )?.value;
                                          handleApproveWFHRequest(
                                            request.id,
                                            notes,
                                          );
                                        }}
                                      >
                                        Approve Request
                                      </Button>
                                    </DialogFooter>
                                  </DialogContent>
                                </Dialog>
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button size="sm" variant="destructive">
                                      <X className="w-4 h-4 mr-1" />
                                      Reject
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>
                                        Reject WFH Request
                                      </DialogTitle>
                                      <DialogDescription>
                                        Provide a reason for rejecting this
                                        request
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                      <div className="space-y-2">
                                        <Label
                                          htmlFor={`reject-notes-${request.id}`}
                                        >
                                          Rejection Reason *
                                        </Label>
                                        <textarea
                                          id={`reject-notes-${request.id}`}
                                          className="w-full min-h-[80px] rounded-md border border-slate-300 p-3"
                                          placeholder="Please explain why this request is being rejected..."
                                          required
                                        />
                                      </div>
                                    </div>
                                    <DialogFooter>
                                      <Button
                                        variant="destructive"
                                        onClick={() => {
                                          const notes = (
                                            document.getElementById(
                                              `reject-notes-${request.id}`,
                                            ) as HTMLTextAreaElement
                                          )?.value;
                                          if (notes.trim()) {
                                            handleRejectWFHRequest(
                                              request.id,
                                              notes,
                                            );
                                          } else {
                                            toast.error(
                                              "Please provide a rejection reason",
                                            );
                                          }
                                        }}
                                      >
                                        Reject Request
                                      </Button>
                                    </DialogFooter>
                                  </DialogContent>
                                </Dialog>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* New WFH Request Dialog */}
          <Dialog open={wfhDialogOpen} onOpenChange={setWfhDialogOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>New Work From Home Request</DialogTitle>
                <DialogDescription>
                  Submit a request to work from home
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="wfh-start-date">Start Date *</Label>
                    <Input
                      id="wfh-start-date"
                      type="date"
                      value={wfhStartDate}
                      onChange={(e) => setWfhStartDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wfh-end-date">End Date *</Label>
                    <Input
                      id="wfh-end-date"
                      type="date"
                      value={wfhEndDate}
                      onChange={(e) => setWfhEndDate(e.target.value)}
                      min={
                        wfhStartDate || new Date().toISOString().split("T")[0]
                      }
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wfh-reason">Reason *</Label>
                  <textarea
                    id="wfh-reason"
                    className="w-full min-h-[100px] rounded-md border border-slate-300 p-3"
                    placeholder="Please provide a reason for your WFH request..."
                    value={wfhReason}
                    onChange={(e) => setWfhReason(e.target.value)}
                    required
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="wfh-recurring"
                    checked={wfhRecurring}
                    onChange={(e) => setWfhRecurring(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="wfh-recurring">
                    This is a recurring request
                  </Label>
                </div>
                {wfhRecurring && (
                  <div className="space-y-2">
                    <Label>Select Recurring Days</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        "monday",
                        "tuesday",
                        "wednesday",
                        "thursday",
                        "friday",
                      ].map((day) => (
                        <div key={day} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`day-${day}`}
                            checked={wfhRecurringDays.includes(day)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setWfhRecurringDays([...wfhRecurringDays, day]);
                              } else {
                                setWfhRecurringDays(
                                  wfhRecurringDays.filter((d) => d !== day),
                                );
                              }
                            }}
                            className="rounded border-slate-300"
                          />
                          <Label
                            htmlFor={`day-${day}`}
                            className="capitalize text-sm"
                          >
                            {day.slice(0, 3)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setWfhDialogOpen(false);
                    setWfhStartDate("");
                    setWfhEndDate("");
                    setWfhReason("");
                    setWfhRecurring(false);
                    setWfhRecurringDays([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (wfhStartDate && wfhEndDate && wfhReason.trim()) {
                      if (wfhRecurring && wfhRecurringDays.length === 0) {
                        toast.error("Please select at least one recurring day");
                        return;
                      }
                      handleSubmitWFHRequest(
                        wfhStartDate,
                        wfhEndDate,
                        wfhReason,
                        wfhRecurring,
                        wfhRecurring ? wfhRecurringDays : undefined,
                      );
                      setWfhDialogOpen(false);
                      setWfhStartDate("");
                      setWfhEndDate("");
                      setWfhReason("");
                      setWfhRecurring(false);
                      setWfhRecurringDays([]);
                    } else {
                      toast.error("Please fill in all required fields");
                    }
                  }}
                >
                  Submit Request
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* User Management Section */}
      {activeSection === "users" &&
        (currentUser?.role === "admin" ||
          currentUser?.role === "superadmin" ||
          currentUser?.role === "hr") && (
          <UserManagement
            currentUser={currentUser}
            onInitiateOffboarding={handleInitiateOffboarding}
          />
        )}

      {/* Organization Management Section */}
      {activeSection === "organization" &&
        (currentUser?.role === "admin" || currentUser?.role === "superadmin") &&
        currentOrg && (
          <div className="space-y-6">
            {/* Departments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Departments
                </CardTitle>
                <CardDescription>
                  Manage organizational departments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orgDepartments
                    .filter((d) => d.organizationId === currentOrgId)
                    .map((dept) => (
                      <div
                        key={dept.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div>
                          <div className="font-medium">{dept.name}</div>
                          <div className="text-sm text-muted-foreground">
                            Code: {dept.code}
                          </div>
                          {dept.description && (
                            <div className="text-sm text-muted-foreground">
                              {dept.description}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setOrgDepartments(
                              orgDepartments.filter((d) => d.id !== dept.id),
                            );
                            toast.success("Department removed");
                          }}
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}

                  {orgDepartments.filter(
                    (d) => d.organizationId === currentOrgId,
                  ).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No departments yet. Add your first department below.
                    </div>
                  )}

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="w-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Department
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Department</DialogTitle>
                        <DialogDescription>
                          Create a new department for your organization
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const formData = new FormData(e.currentTarget);
                          handleAddDepartment({
                            organizationId: currentOrgId!,
                            name: formData.get("name") as string,
                            code: formData.get("code") as string,
                            description: formData.get("description") as string,
                          });
                          (e.target as HTMLFormElement).reset();
                        }}
                        className="space-y-4"
                      >
                        <div>
                          <label className="text-sm font-medium">
                            Department Name
                          </label>
                          <Input
                            name="name"
                            required
                            placeholder="Engineering"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">
                            Department Code
                          </label>
                          <Input
                            name="code"
                            required
                            placeholder="ENG"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">
                            Description (Optional)
                          </label>
                          <Input
                            name="description"
                            placeholder="Software development team"
                            className="mt-1"
                          />
                        </div>
                        <Button type="submit" className="w-full">
                          Add Department
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>

            {/* Locations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Locations
                </CardTitle>
                <CardDescription>
                  Manage office locations and branches
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orgLocations
                    .filter((l) => l.organizationId === currentOrgId)
                    .map((location) => (
                      <div
                        key={location.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div>
                          <div className="font-medium">{location.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {location.city}, {location.state},{" "}
                            {location.country}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Timezone: {location.timezone}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setOrgLocations(
                              orgLocations.filter((l) => l.id !== location.id),
                            );
                            toast.success("Location removed");
                          }}
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}

                  {orgLocations.filter((l) => l.organizationId === currentOrgId)
                    .length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No locations yet. Add your first location below.
                    </div>
                  )}

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="w-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Location
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add New Location</DialogTitle>
                        <DialogDescription>
                          Add an office or branch location
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const formData = new FormData(e.currentTarget);
                          handleAddLocation({
                            organizationId: currentOrgId!,
                            name: formData.get("name") as string,
                            address: formData.get("address") as string,
                            city: formData.get("city") as string,
                            state: formData.get("state") as string,
                            country: formData.get("country") as string,
                            postalCode: formData.get("postalCode") as string,
                            timezone: formData.get("timezone") as string,
                          });
                          (e.target as HTMLFormElement).reset();
                        }}
                        className="space-y-4"
                      >
                        <div>
                          <label className="text-sm font-medium">
                            Location Name
                          </label>
                          <Input
                            name="name"
                            required
                            placeholder="Headquarters"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Address</label>
                          <Input
                            name="address"
                            required
                            placeholder="123 Main St"
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">City</label>
                            <Input
                              name="city"
                              required
                              placeholder="New York"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">State</label>
                            <Input
                              name="state"
                              required
                              placeholder="NY"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">
                              Country
                            </label>
                            <Input
                              name="country"
                              required
                              placeholder="USA"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">
                              Postal Code
                            </label>
                            <Input
                              name="postalCode"
                              required
                              placeholder="10001"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium">
                            Timezone
                          </label>
                          <Input
                            name="timezone"
                            required
                            placeholder="America/New_York"
                            className="mt-1"
                          />
                        </div>
                        <Button type="submit" className="w-full">
                          Add Location
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>

            {/* Organization Data & Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GearSix className="w-5 h-5" />
                  Organization Data & Settings
                </CardTitle>
                <CardDescription>
                  Export data, view audit logs, and manage organization settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Data Export */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-start gap-3">
                    <DownloadSimple className="w-5 h-5 mt-0.5 text-primary" />
                    <div className="flex-1">
                      <h4 className="font-medium">Export Organization Data</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Download a complete export of all organization data
                        including employees, attendance, leaves, timesheets, and
                        more. Data is exported in JSON format for easy import
                        and portability.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleExportOrganizationData}
                    className="w-full"
                    variant="outline"
                  >
                    <DownloadSimple className="w-4 h-4 mr-2" />
                    Export All Data (JSON)
                  </Button>
                </div>

                {/* Organization Info */}
                <div className="p-4 border rounded-lg space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-5 h-5 text-primary" />
                    <h4 className="font-medium">Organization Information</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        Organization ID:
                      </span>
                      <p className="font-mono text-xs mt-1">{currentOrg.id}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <p className="mt-1">
                        <Badge
                          variant={
                            currentOrg.status === "active"
                              ? "default"
                              : "secondary"
                          }
                        >
                          {currentOrg.status}
                        </Badge>
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Plan:</span>
                      <p className="mt-1 capitalize">
                        <Badge variant="outline">
                          {currentOrg.subscriptionPlan}
                        </Badge>
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Created:</span>
                      <p className="mt-1">
                        {new Date(currentOrg.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Timezone:</span>
                      <p className="mt-1">{currentOrg.timezone}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Currency:</span>
                      <p className="mt-1">{currentOrg.currency}</p>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 border rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {
                        (employees || []).filter(
                          (e) => e.organizationId === currentOrgId,
                        ).length
                      }
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Total Employees
                    </div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {
                        (orgDepartments || []).filter(
                          (d) => d.organizationId === currentOrgId,
                        ).length
                      }
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Departments
                    </div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {
                        (orgLocations || []).filter(
                          (l) => l.organizationId === currentOrgId,
                        ).length
                      }
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Locations
                    </div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {
                        (attendanceRecords || []).filter((a) => {
                          const emp = employees.find(
                            (e) => e.id === a.employeeId,
                          );
                          return emp?.organizationId === currentOrgId;
                        }).length
                      }
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Attendance Records
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subscription & Billing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CurrencyDollar className="w-5 h-5" />
                  Subscription & Billing
                </CardTitle>
                <CardDescription>
                  Manage subscription plans, billing, and feature limits
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Plan */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Current Plan</h4>
                      <p className="text-sm text-muted-foreground capitalize">
                        {currentOrg.subscriptionPlan}
                      </p>
                    </div>
                    <Badge
                      variant={
                        currentOrg.status === "active"
                          ? "default"
                          : currentOrg.status === "trial"
                            ? "secondary"
                            : "destructive"
                      }
                    >
                      {currentOrg.status}
                    </Badge>
                  </div>
                  {currentOrg.subscriptionExpiresAt && (
                    <p className="text-sm text-muted-foreground">
                      {currentOrg.status === "trial"
                        ? "Trial ends: "
                        : "Renews: "}
                      {new Date(
                        currentOrg.subscriptionExpiresAt,
                      ).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {/* Billing Alerts */}
                {(billingAlerts || []).filter(
                  (a) => a.organizationId === currentOrgId && !a.acknowledgedAt,
                ).length > 0 && (
                  <div className="space-y-2">
                    {(billingAlerts || [])
                      .filter(
                        (a) =>
                          a.organizationId === currentOrgId &&
                          !a.acknowledgedAt,
                      )
                      .map((alert) => (
                        <div
                          key={alert.id}
                          className={`p-3 border rounded-lg flex items-start gap-3 ${
                            alert.severity === "error"
                              ? "border-red-500 bg-red-50 dark:bg-red-950"
                              : alert.severity === "warning"
                                ? "border-yellow-500 bg-yellow-50 dark:bg-yellow-950"
                                : "border-blue-500 bg-blue-50 dark:bg-blue-950"
                          }`}
                        >
                          <Warning
                            className={`w-5 h-5 mt-0.5 ${
                              alert.severity === "error"
                                ? "text-red-600"
                                : alert.severity === "warning"
                                  ? "text-yellow-600"
                                  : "text-blue-600"
                            }`}
                          />
                          <div className="flex-1">
                            <p className="text-sm">{alert.message}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const updated = (billingAlerts || []).map((a) =>
                                a.id === alert.id
                                  ? {
                                      ...a,
                                      acknowledgedAt: new Date().toISOString(),
                                    }
                                  : a,
                              );
                              setBillingAlerts(updated);
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                  </div>
                )}

                {/* Available Plans */}
                <div>
                  <h4 className="font-medium mb-3">Available Plans</h4>
                  <div className="grid md:grid-cols-4 gap-4">
                    {subscriptionPlans.map((plan) => (
                      <div
                        key={plan.id}
                        className={`p-4 border rounded-lg space-y-3 ${
                          currentOrg.subscriptionPlan === plan.name
                            ? "border-primary bg-primary/5"
                            : ""
                        }`}
                      >
                        <div>
                          <h5 className="font-medium">{plan.displayName}</h5>
                          <p className="text-2xl font-bold mt-2">
                            ${plan.price}
                            <span className="text-sm font-normal text-muted-foreground">
                              /mo
                            </span>
                          </p>
                        </div>
                        <ul className="space-y-1 text-sm">
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            {plan.features.maxEmployees === "unlimited"
                              ? "Unlimited"
                              : plan.features.maxEmployees}{" "}
                            employees
                          </li>
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            {plan.features.attendanceTracking
                              ? "Attendance tracking"
                              : ""}
                          </li>
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            {plan.features.leaveManagement
                              ? "Leave management"
                              : ""}
                          </li>
                          {plan.features.timesheets && (
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-600" />
                              Timesheets
                            </li>
                          )}
                          {plan.features.customRoles && (
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-600" />
                              Custom roles
                            </li>
                          )}
                          {plan.features.prioritySupport && (
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-600" />
                              Priority support
                            </li>
                          )}
                        </ul>
                        {currentOrg.subscriptionPlan !== plan.name && (
                          <Button
                            onClick={() => handleUpgradeSubscription(plan.name)}
                            className="w-full"
                            size="sm"
                          >
                            {subscriptionPlans.findIndex(
                              (p) => p.name === currentOrg.subscriptionPlan,
                            ) <
                            subscriptionPlans.findIndex(
                              (p) => p.name === plan.name,
                            )
                              ? "Upgrade"
                              : "Downgrade"}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Feature Usage */}
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-medium">Feature Usage</h4>
                  <div className="space-y-2 text-sm">
                    {(() => {
                      const plan = subscriptionPlans.find(
                        (p) => p.name === currentOrg.subscriptionPlan,
                      );
                      const employeeCount = (employees || []).filter(
                        (e) => e.organizationId === currentOrgId,
                      ).length;
                      const deptCount = (orgDepartments || []).filter(
                        (d) => d.organizationId === currentOrgId,
                      ).length;
                      const locCount = (orgLocations || []).filter(
                        (l) => l.organizationId === currentOrgId,
                      ).length;

                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <span>Employees:</span>
                            <span className="font-medium">
                              {employeeCount} /{" "}
                              {plan?.features.maxEmployees === "unlimited"
                                ? "∞"
                                : plan?.features.maxEmployees}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Departments:</span>
                            <span className="font-medium">
                              {deptCount} /{" "}
                              {plan?.features.maxDepartments === "unlimited"
                                ? "∞"
                                : plan?.features.maxDepartments}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Locations:</span>
                            <span className="font-medium">
                              {locCount} /{" "}
                              {plan?.features.maxLocations === "unlimited"
                                ? "∞"
                                : plan?.features.maxLocations}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Invoices */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Invoices</h4>
                    <Button
                      onClick={handleGenerateInvoice}
                      size="sm"
                      variant="outline"
                    >
                      <Receipt className="w-4 h-4 mr-2" />
                      Generate Invoice
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(billingInvoices || [])
                      .filter((inv) => inv.organizationId === currentOrgId)
                      .sort(
                        (a, b) =>
                          new Date(b.createdAt).getTime() -
                          new Date(a.createdAt).getTime(),
                      )
                      .slice(0, 5)
                      .map((invoice) => (
                        <div
                          key={invoice.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div>
                            <div className="font-medium">
                              {invoice.invoiceNumber}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {new Date(invoice.createdAt).toLocaleDateString()}{" "}
                              • ${invoice.amount}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                invoice.status === "paid"
                                  ? "default"
                                  : invoice.status === "overdue"
                                    ? "destructive"
                                    : "secondary"
                              }
                            >
                              {invoice.status}
                            </Badge>
                            {invoice.status !== "paid" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  handleMarkInvoicePaid(invoice.id)
                                }
                              >
                                Mark Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    {(billingInvoices || []).filter(
                      (inv) => inv.organizationId === currentOrgId,
                    ).length === 0 && (
                      <div className="text-center py-6 text-muted-foreground text-sm">
                        No invoices yet. Click "Generate Invoice" to create one.
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* User Invitations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  User Invitations
                </CardTitle>
                <CardDescription>
                  Invite users to join your organization via email
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Send Single Invitation */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Send Invitation
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Send Invitation</DialogTitle>
                      <DialogDescription>
                        Invite a user to join your organization
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        if (currentUser && currentOrgId) {
                          handleSendInvitation({
                            organizationId: currentOrgId,
                            email: formData.get("email") as string,
                            role: formData.get(
                              "role",
                            ) as OrganizationInvitation["role"],
                            departmentId:
                              (formData.get("department") as string) ||
                              undefined,
                            invitedBy: currentUser.id,
                          });
                          (e.target as HTMLFormElement).reset();
                        }
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="text-sm font-medium">
                          Email Address
                        </label>
                        <Input
                          name="email"
                          type="email"
                          required
                          placeholder="user@example.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Role</label>
                        <select
                          name="role"
                          required
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        >
                          <option value="employee">Employee</option>
                          <option value="manager">Manager</option>
                          <option value="hr">HR</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">
                          Department (Optional)
                        </label>
                        <select
                          name="department"
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        >
                          <option value="">Select department</option>
                          {(orgDepartments || [])
                            .filter((d) => d.organizationId === currentOrgId)
                            .map((dept) => (
                              <option key={dept.id} value={dept.id}>
                                {dept.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      <Button type="submit" className="w-full">
                        Send Invitation
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Pending Invitations */}
                <div>
                  <h4 className="font-medium mb-3">Pending Invitations</h4>
                  <div className="space-y-2">
                    {(organizationInvitations || [])
                      .filter(
                        (inv) =>
                          inv.organizationId === currentOrgId &&
                          inv.status === "pending",
                      )
                      .map((invitation) => (
                        <div
                          key={invitation.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div>
                            <div className="font-medium">
                              {invitation.email}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Role: {invitation.role} • Expires:{" "}
                              {new Date(
                                invitation.expiresAt,
                              ).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const updated = (
                                  organizationInvitations || []
                                ).map((inv) =>
                                  inv.id === invitation.id
                                    ? {
                                        ...inv,
                                        token: crypto.randomUUID(),
                                        invitedAt: new Date().toISOString(),
                                        expiresAt: new Date(
                                          Date.now() + 7 * 24 * 60 * 60 * 1000,
                                        ).toISOString(),
                                      }
                                    : inv,
                                );
                                setOrganizationInvitations(updated);
                                toast.success("Invitation resent");
                              }}
                            >
                              Resend
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                handleCancelInvitation(invitation.id)
                              }
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    {(organizationInvitations || []).filter(
                      (inv) =>
                        inv.organizationId === currentOrgId &&
                        inv.status === "pending",
                    ).length === 0 && (
                      <div className="text-center py-6 text-muted-foreground text-sm">
                        No pending invitations
                      </div>
                    )}
                  </div>
                </div>

                {/* Bulk Invite via CSV */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-start gap-3">
                    <UploadSimple className="w-5 h-5 mt-0.5 text-primary" />
                    <div className="flex-1">
                      <h4 className="font-medium">Bulk Import from CSV</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Upload a CSV file with columns: email, role, department
                        (optional)
                      </p>
                    </div>
                  </div>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const csvData = event.target?.result as string;
                          handleImportInvitationsFromCSV(csvData);
                        };
                        reader.readAsText(file);
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Custom Roles & Permissions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserGear className="w-5 h-5" />
                  Custom Roles & Permissions
                </CardTitle>
                <CardDescription>
                  Create and manage custom roles with granular permissions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Create Custom Role */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Custom Role
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create Custom Role</DialogTitle>
                      <DialogDescription>
                        Define a new role with specific permissions
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        const name = formData.get("name") as string;
                        const description = formData.get(
                          "description",
                        ) as string;

                        // Collect selected permissions
                        const permissions: Permission[] = [];
                        const modules: PermissionModule[] = [
                          "attendance",
                          "leaves",
                          "timesheets",
                          "employees",
                          "reports",
                          "settings",
                          "shifts",
                          "expenses",
                          "documents",
                          "performance",
                          "onboarding",
                          "offboarding",
                        ];

                        modules.forEach((module) => {
                          const actions: PermissionAction[] = [];
                          const moduleActions: PermissionAction[] = [
                            "view",
                            "create",
                            "edit",
                            "delete",
                            "approve",
                          ];
                          moduleActions.forEach((action) => {
                            if (formData.get(`${module}-${action}`) === "on") {
                              actions.push(action);
                            }
                          });
                          if (actions.length > 0) {
                            permissions.push({ module, actions });
                          }
                        });

                        if (currentUser && currentOrgId) {
                          handleCreateCustomRole({
                            organizationId: currentOrgId,
                            name,
                            description,
                            permissions,
                            createdBy: currentUser.id,
                          });
                          (e.target as HTMLFormElement).reset();
                        }
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="text-sm font-medium">Role Name</label>
                        <Input
                          name="name"
                          required
                          placeholder="Custom Manager"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">
                          Description
                        </label>
                        <Input
                          name="description"
                          placeholder="Manages specific departments"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          Permissions
                        </label>
                        <div className="grid grid-cols-2 gap-4 max-h-60 overflow-y-auto p-4 border rounded-lg">
                          {(
                            [
                              "attendance",
                              "leaves",
                              "timesheets",
                              "employees",
                              "reports",
                              "settings",
                            ] as PermissionModule[]
                          ).map((module) => (
                            <div key={module} className="space-y-2">
                              <h5 className="font-medium capitalize">
                                {module}
                              </h5>
                              <div className="space-y-1 text-sm">
                                {(
                                  [
                                    "view",
                                    "create",
                                    "edit",
                                    "delete",
                                    "approve",
                                  ] as PermissionAction[]
                                ).map((action) => (
                                  <label
                                    key={action}
                                    className="flex items-center gap-2"
                                  >
                                    <input
                                      type="checkbox"
                                      name={`${module}-${action}`}
                                      className="rounded"
                                    />
                                    <span className="capitalize">{action}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button type="submit" className="w-full">
                        Create Role
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Custom Roles List */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Custom Roles</h4>
                    <Button
                      onClick={handleExportRolesMatrix}
                      size="sm"
                      variant="outline"
                    >
                      <DownloadSimple className="w-4 h-4 mr-2" />
                      Export Matrix
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(customRoles || [])
                      .filter((role) => role.organizationId === currentOrgId)
                      .map((role) => (
                        <div key={role.id} className="p-3 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{role.name}</span>
                                {!role.isCustom && (
                                  <Badge variant="secondary">System</Badge>
                                )}
                              </div>
                              {role.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {role.description}
                                </p>
                              )}
                              <div className="text-xs text-muted-foreground mt-2">
                                {role.permissions.length} modules • Created{" "}
                                {new Date(role.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCloneRole(role.id)}
                              >
                                Clone
                              </Button>
                              {role.isCustom && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    handleDeleteCustomRole(role.id)
                                  }
                                >
                                  <Trash className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    {(customRoles || []).filter(
                      (role) => role.organizationId === currentOrgId,
                    ).length === 0 && (
                      <div className="text-center py-6 text-muted-foreground text-sm">
                        No custom roles yet. Create your first custom role
                        above.
                      </div>
                    )}
                  </div>
                </div>

                {/* Role Audit Log */}
                {(roleAuditLogs || []).filter(
                  (log) => log.organizationId === currentOrgId,
                ).length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">Recent Activity</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {(roleAuditLogs || [])
                        .filter((log) => log.organizationId === currentOrgId)
                        .sort(
                          (a, b) =>
                            new Date(b.performedAt).getTime() -
                            new Date(a.performedAt).getTime(),
                        )
                        .slice(0, 15)
                        .map((log) => (
                          <div
                            key={log.id}
                            className="text-sm p-2 border rounded"
                          >
                            <span className="capitalize">
                              {log.action.replace("-", " ")}
                            </span>
                            : {log.roleName}
                            <span className="text-muted-foreground ml-2">
                              {new Date(log.performedAt).toLocaleString()}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Organization Deactivation */}
            <Card className="border-red-200 dark:border-red-900">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <Warning className="w-5 h-5" />
                  Danger Zone
                </CardTitle>
                <CardDescription>
                  Deactivate or permanently delete organization data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {currentOrg.status !== "suspended" ? (
                  <div className="p-4 border border-red-200 dark:border-red-900 rounded-lg space-y-3">
                    <div>
                      <h4 className="font-medium">Deactivate Organization</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Suspends the organization for 30 days. You can
                        reactivate within this period. After 30 days, data will
                        be permanently deleted.
                      </p>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="destructive">
                          Deactivate Organization
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Deactivate Organization</DialogTitle>
                          <DialogDescription>
                            Are you sure you want to deactivate this
                            organization? This will suspend all access for 30
                            days.
                          </DialogDescription>
                        </DialogHeader>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.currentTarget);
                            handleDeactivateOrganization(
                              formData.get("reason") as string,
                            );
                            (e.target as HTMLFormElement).reset();
                          }}
                          className="space-y-4"
                        >
                          <div>
                            <label className="text-sm font-medium">
                              Reason (Optional)
                            </label>
                            <Input
                              name="reason"
                              placeholder="Why are you deactivating?"
                              className="mt-1"
                            />
                          </div>
                          <Button
                            type="submit"
                            variant="destructive"
                            className="w-full"
                          >
                            Confirm Deactivation
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                ) : (
                  <div className="p-4 border border-yellow-200 dark:border-yellow-900 rounded-lg space-y-3 bg-yellow-50 dark:bg-yellow-950">
                    <div>
                      <h4 className="font-medium">Organization Suspended</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        This organization is currently suspended.
                        {(() => {
                          const deactivation = (orgDeactivations || []).find(
                            (d) =>
                              d.organizationId === currentOrgId &&
                              !d.reactivatedAt,
                          );
                          if (deactivation) {
                            const daysLeft = Math.ceil(
                              (new Date(
                                deactivation.gracePeriodEndsAt,
                              ).getTime() -
                                Date.now()) /
                                (24 * 60 * 60 * 1000),
                            );
                            return ` You have ${daysLeft} days remaining to reactivate before permanent deletion.`;
                          }
                          return "";
                        })()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleReactivateOrganization}
                        variant="default"
                      >
                        Reactivate Organization
                      </Button>
                      <Button
                        onClick={handleArchiveOrganizationData}
                        variant="outline"
                      >
                        <DownloadSimple className="w-4 h-4 mr-2" />
                        Archive Data
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

      {/* Documents Management Section */}
      {activeSection === "documents" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Folder className="w-5 h-5" />
                Employee Documents
              </CardTitle>
              <CardDescription>
                Upload and manage employee documents with version control and
                access permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Filter and Search */}
                <div className="flex gap-4 flex-wrap">
                  <Input
                    placeholder="Search documents..."
                    className="max-w-sm"
                    id="document-search"
                  />
                  <select
                    className="px-3 py-2 border rounded-md"
                    defaultValue="all"
                    id="document-category-filter"
                  >
                    <option value="all">All Categories</option>
                    <option value="identification">Identification</option>
                    <option value="contract">Contracts</option>
                    <option value="certification">Certifications</option>
                    <option value="performance">Performance Reviews</option>
                    <option value="other">Other</option>
                  </select>
                  <select
                    className="px-3 py-2 border rounded-md"
                    defaultValue="all"
                    id="document-employee-filter"
                  >
                    <option value="all">All Employees</option>
                    {employeeProfiles.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Upload Document Dialog */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button>
                      <UploadSimple className="w-4 h-4 mr-2" />
                      Upload Document
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Upload Employee Document</DialogTitle>
                      <DialogDescription>
                        Upload a document for an employee with proper
                        categorization and access control
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        const employeeId = formData.get("employeeId") as string;
                        const category = formData.get(
                          "category",
                        ) as EmployeeDocument["category"];
                        const accessLevel = formData.get(
                          "accessLevel",
                        ) as EmployeeDocument["accessLevel"];
                        const expiryDate = formData.get("expiryDate") as string;
                        const description = formData.get(
                          "description",
                        ) as string;
                        const fileInput = document.getElementById(
                          "document-file-input",
                        ) as HTMLInputElement;
                        const file = fileInput?.files?.[0];

                        if (!employeeId || !category || !file) {
                          toast.error("Please fill all required fields");
                          return;
                        }

                        const employee = employeeProfiles.find(
                          (e) => e.id === employeeId,
                        );
                        if (!employee) {
                          toast.error("Employee not found");
                          return;
                        }

                        // Convert file to base64
                        const reader = new FileReader();
                        reader.onload = () => {
                          const base64Data = reader.result as string;

                          // Check for existing versions
                          const existingDocs = employeeDocuments.filter(
                            (d) =>
                              d.employeeId === employeeId &&
                              d.fileName === file.name &&
                              d.category === category,
                          );
                          const latestVersion =
                            existingDocs.length > 0
                              ? Math.max(...existingDocs.map((d) => d.version))
                              : 0;

                          const newDocument: EmployeeDocument = {
                            id: `doc-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                            employeeId,
                            employeeName: employee.name,
                            fileName: file.name,
                            fileSize: file.size,
                            fileType: file.type,
                            category,
                            uploadedAt: new Date().toISOString(),
                            uploadedBy: currentUser!.id,
                            uploadedByName: currentUser!.name,
                            expiryDate: expiryDate || undefined,
                            accessLevel,
                            version: latestVersion + 1,
                            previousVersionId:
                              existingDocs[existingDocs.length - 1]?.id,
                            description: description || undefined,
                            base64Data,
                          };

                          setEmployeeDocuments([
                            ...employeeDocuments,
                            newDocument,
                          ]);
                          toast.success(
                            `Document uploaded successfully (v${newDocument.version})`,
                          );
                          e.currentTarget.reset();
                        };
                        reader.onerror = () => {
                          toast.error("Failed to read file");
                        };
                        reader.readAsDataURL(file);
                      }}
                      className="space-y-4"
                    >
                      <div className="grid gap-4">
                        <div>
                          <label className="text-sm font-medium">
                            Employee *
                          </label>
                          <select
                            name="employeeId"
                            required
                            className="w-full px-3 py-2 border rounded-md mt-1"
                          >
                            <option value="">Select employee...</option>
                            {employeeProfiles.map((emp) => (
                              <option key={emp.id} value={emp.id}>
                                {emp.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="text-sm font-medium">
                            Document Category *
                          </label>
                          <select
                            name="category"
                            required
                            className="w-full px-3 py-2 border rounded-md mt-1"
                          >
                            <option value="">Select category...</option>
                            <option value="identification">
                              Identification
                            </option>
                            <option value="contract">Contract</option>
                            <option value="certification">Certification</option>
                            <option value="performance">
                              Performance Review
                            </option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-sm font-medium">
                            Access Level *
                          </label>
                          <select
                            name="accessLevel"
                            required
                            className="w-full px-3 py-2 border rounded-md mt-1"
                          >
                            <option value="hr">HR Only</option>
                            <option value="manager">Manager + HR</option>
                            <option value="employee">
                              Employee + Manager + HR
                            </option>
                          </select>
                        </div>

                        <div>
                          <label className="text-sm font-medium">File *</label>
                          <Input
                            id="document-file-input"
                            type="file"
                            required
                            className="mt-1"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Supported: PDF, Word documents, Images (Max 5MB)
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium">
                            Expiry Date (Optional)
                          </label>
                          <Input
                            name="expiryDate"
                            type="date"
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium">
                            Description (Optional)
                          </label>
                          <textarea
                            name="description"
                            className="w-full px-3 py-2 border rounded-md mt-1"
                            rows={3}
                            placeholder="Add notes about this document..."
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button type="submit">Upload Document</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Documents List */}
                <div className="space-y-3">
                  {employeeDocuments.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Folder className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No documents uploaded yet</p>
                      <p className="text-sm">
                        Upload your first employee document to get started
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Group by employee */}
                      {employeeProfiles
                        .filter((emp) =>
                          employeeDocuments.some(
                            (doc) => doc.employeeId === emp.id,
                          ),
                        )
                        .map((emp) => {
                          const empDocs = employeeDocuments
                            .filter((doc) => doc.employeeId === emp.id)
                            .sort(
                              (a, b) =>
                                new Date(b.uploadedAt).getTime() -
                                new Date(a.uploadedAt).getTime(),
                            );

                          return (
                            <Card key={emp.id} className="overflow-hidden">
                              <CardHeader className="bg-muted/50">
                                <CardTitle className="text-lg flex items-center gap-2">
                                  <span className="text-2xl">{emp.avatar}</span>
                                  {emp.name}
                                  <Badge variant="secondary">
                                    {empDocs.length} documents
                                  </Badge>
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="pt-4">
                                <div className="space-y-2">
                                  {empDocs.map((doc) => {
                                    const isExpired =
                                      doc.expiryDate &&
                                      new Date(doc.expiryDate) < new Date();
                                    const isExpiringSoon =
                                      doc.expiryDate &&
                                      new Date(doc.expiryDate) <=
                                        new Date(
                                          Date.now() + 30 * 24 * 60 * 60 * 1000,
                                        ) &&
                                      !isExpired;

                                    return (
                                      <div
                                        key={doc.id}
                                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                                      >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                          <FileText className="w-5 h-5 flex-shrink-0 text-blue-500" />
                                          <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">
                                              {doc.fileName}
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                              <Badge
                                                variant="outline"
                                                className="capitalize"
                                              >
                                                {doc.category}
                                              </Badge>
                                              <span>v{doc.version}</span>
                                              <span>•</span>
                                              <span>
                                                {new Date(
                                                  doc.uploadedAt,
                                                ).toLocaleDateString()}
                                              </span>
                                              <span>•</span>
                                              <span>
                                                {(doc.fileSize / 1024).toFixed(
                                                  1,
                                                )}{" "}
                                                KB
                                              </span>
                                              {doc.expiryDate && (
                                                <>
                                                  <span>•</span>
                                                  <span
                                                    className={
                                                      isExpired
                                                        ? "text-red-600 font-medium"
                                                        : isExpiringSoon
                                                          ? "text-orange-600 font-medium"
                                                          : ""
                                                    }
                                                  >
                                                    Expires:{" "}
                                                    {new Date(
                                                      doc.expiryDate,
                                                    ).toLocaleDateString()}
                                                  </span>
                                                </>
                                              )}
                                            </div>
                                            {doc.description && (
                                              <div className="text-xs text-muted-foreground mt-1">
                                                {doc.description}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          {isExpired && (
                                            <Badge
                                              variant="destructive"
                                              className="mr-2"
                                            >
                                              Expired
                                            </Badge>
                                          )}
                                          {isExpiringSoon && (
                                            <Badge
                                              variant="outline"
                                              className="mr-2 border-orange-500 text-orange-600"
                                            >
                                              Expiring Soon
                                            </Badge>
                                          )}
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              // Download document
                                              const link =
                                                document.createElement("a");
                                              link.href = doc.base64Data;
                                              link.download = doc.fileName;
                                              link.click();
                                              toast.success(
                                                "Document downloaded",
                                              );
                                            }}
                                          >
                                            Download
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                              if (
                                                confirm(
                                                  "Are you sure you want to delete this document?",
                                                )
                                              ) {
                                                setEmployeeDocuments(
                                                  employeeDocuments.filter(
                                                    (d) => d.id !== doc.id,
                                                  ),
                                                );
                                                toast.success(
                                                  "Document deleted",
                                                );
                                              }
                                            }}
                                          >
                                            <Trash className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                    </>
                  )}
                </div>

                {/* Document Statistics */}
                {employeeDocuments.length > 0 && (
                  <Card className="bg-muted/50">
                    <CardHeader>
                      <CardTitle className="text-sm">
                        Document Statistics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">
                            Total Documents
                          </div>
                          <div className="text-2xl font-bold">
                            {employeeDocuments.length}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">
                            Total Size
                          </div>
                          <div className="text-2xl font-bold">
                            {(
                              employeeDocuments.reduce(
                                (sum, doc) => sum + doc.fileSize,
                                0,
                              ) /
                              1024 /
                              1024
                            ).toFixed(2)}{" "}
                            MB
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">
                            Expiring Soon
                          </div>
                          <div className="text-2xl font-bold text-orange-600">
                            {
                              employeeDocuments.filter(
                                (doc) =>
                                  doc.expiryDate &&
                                  new Date(doc.expiryDate) <=
                                    new Date(
                                      Date.now() + 30 * 24 * 60 * 60 * 1000,
                                    ) &&
                                  new Date(doc.expiryDate) >= new Date(),
                              ).length
                            }
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Expired</div>
                          <div className="text-2xl font-bold text-red-600">
                            {
                              employeeDocuments.filter(
                                (doc) =>
                                  doc.expiryDate &&
                                  new Date(doc.expiryDate) < new Date(),
                              ).length
                            }
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expense Reimbursement Management Section */}
      {activeSection === "expenses" && (
        <div className="space-y-6">
          {/* My Expenses */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                My Expense Reimbursements
              </CardTitle>
              <CardDescription>
                Submit and track your expense reimbursement requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Submit New Expense Dialog */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      New Expense Request
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Submit Expense Reimbursement</DialogTitle>
                      <DialogDescription>
                        Submit an expense for reimbursement with receipt
                        attachments
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        const category = formData.get(
                          "category",
                        ) as ExpenseReimbursement["category"];
                        const amount = parseFloat(
                          formData.get("amount") as string,
                        );
                        const date = formData.get("date") as string;
                        const description = formData.get(
                          "description",
                        ) as string;
                        const fileInput = document.getElementById(
                          "expense-receipt-input",
                        ) as HTMLInputElement;
                        const files = fileInput?.files;

                        if (!category || !amount || !date || !description) {
                          toast.error("Please fill all required fields");
                          return;
                        }

                        if (amount <= 0) {
                          toast.error("Amount must be greater than 0");
                          return;
                        }

                        const attachments: ExpenseReimbursement["receiptAttachments"] =
                          [];

                        // Process file attachments
                        const processFiles = async () => {
                          if (files && files.length > 0) {
                            for (let i = 0; i < files.length; i++) {
                              const file = files[i];
                              const reader = new FileReader();
                              await new Promise<void>((resolve, reject) => {
                                reader.onload = () => {
                                  attachments.push({
                                    id: `receipt-${Date.now()}-${i}`,
                                    fileName: file.name,
                                    fileSize: file.size,
                                    fileType: file.type,
                                    base64Data: reader.result as string,
                                  });
                                  resolve();
                                };
                                reader.onerror = () => reject();
                                reader.readAsDataURL(file);
                              });
                            }
                          }

                          const newExpense: ExpenseReimbursement = {
                            id: `exp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                            employeeId: currentUser!.id,
                            employeeName: currentUser!.name,
                            employeeDepartment: currentUser!.department,
                            category,
                            amount,
                            currency: currentOrg?.currency || "USD",
                            date,
                            description,
                            status: "submitted",
                            receiptAttachments: attachments,
                            submittedAt: new Date().toISOString(),
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                          };

                          setExpenseReimbursements([
                            ...expenseReimbursements,
                            newExpense,
                          ]);
                          toast.success("Expense submitted successfully");
                          e.currentTarget.reset();
                        };

                        processFiles().catch(() => {
                          toast.error("Failed to process receipt attachments");
                        });
                      }}
                      className="space-y-4"
                    >
                      <div className="grid gap-4">
                        <div>
                          <label className="text-sm font-medium">
                            Category *
                          </label>
                          <select
                            name="category"
                            required
                            className="w-full px-3 py-2 border rounded-md mt-1"
                          >
                            <option value="">Select category...</option>
                            <option value="travel">Travel</option>
                            <option value="meals">Meals & Entertainment</option>
                            <option value="accommodation">Accommodation</option>
                            <option value="equipment">Equipment</option>
                            <option value="training">
                              Training & Development
                            </option>
                            <option value="supplies">Office Supplies</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">
                              Amount *
                            </label>
                            <Input
                              name="amount"
                              type="number"
                              step="0.01"
                              min="0"
                              required
                              placeholder="0.00"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">
                              Date *
                            </label>
                            <Input
                              name="date"
                              type="date"
                              required
                              className="mt-1"
                              max={new Date().toISOString().split("T")[0]}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-sm font-medium">
                            Description *
                          </label>
                          <textarea
                            name="description"
                            required
                            className="w-full px-3 py-2 border rounded-md mt-1"
                            rows={3}
                            placeholder="Provide details about this expense..."
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium">
                            Receipt Attachments
                          </label>
                          <Input
                            id="expense-receipt-input"
                            type="file"
                            multiple
                            className="mt-1"
                            accept=".pdf,.jpg,.jpeg,.png"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Upload receipts (PDF, JPG, PNG). Multiple files
                            supported.
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button type="submit">Submit for Approval</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* My Expenses List */}
                <div className="space-y-3">
                  {expenseReimbursements
                    .filter((exp) => exp.employeeId === currentUser?.id)
                    .sort(
                      (a, b) =>
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime(),
                    )
                    .map((expense) => (
                      <Card key={expense.id}>
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className="capitalize">
                                  {expense.category}
                                </Badge>
                                <Badge
                                  variant={
                                    expense.status === "approved"
                                      ? "default"
                                      : expense.status === "reimbursed"
                                        ? "default"
                                        : expense.status === "rejected"
                                          ? "destructive"
                                          : "secondary"
                                  }
                                >
                                  {expense.status}
                                </Badge>
                              </div>
                              <div className="font-semibold text-lg mb-1">
                                {expense.currency} {expense.amount.toFixed(2)}
                              </div>
                              <div className="text-sm text-muted-foreground space-y-1">
                                <div>{expense.description}</div>
                                <div>
                                  Date:{" "}
                                  {new Date(expense.date).toLocaleDateString()}
                                </div>
                                <div>
                                  Submitted:{" "}
                                  {new Date(
                                    expense.submittedAt!,
                                  ).toLocaleDateString()}
                                </div>
                                {expense.receiptAttachments.length > 0 && (
                                  <div className="flex items-center gap-1 text-blue-600">
                                    <FileText className="w-4 h-4" />
                                    {expense.receiptAttachments.length}{" "}
                                    receipt(s) attached
                                  </div>
                                )}
                              </div>
                              {expense.reviewNotes && (
                                <div className="mt-2 p-2 bg-muted rounded text-sm">
                                  <div className="font-medium">
                                    Review Notes:
                                  </div>
                                  <div>{expense.reviewNotes}</div>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              {expense.receiptAttachments.map((receipt) => (
                                <Button
                                  key={receipt.id}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const link = document.createElement("a");
                                    link.href = receipt.base64Data;
                                    link.download = receipt.fileName;
                                    link.click();
                                    toast.success("Receipt downloaded");
                                  }}
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  Receipt
                                </Button>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                  {expenseReimbursements.filter(
                    (exp) => exp.employeeId === currentUser?.id,
                  ).length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <CurrencyDollar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No expense reimbursements yet</p>
                      <p className="text-sm">
                        Submit your first expense request to get started
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Approvals (for managers, HR, admins) */}
          {(currentUser?.role === "manager" ||
            currentUser?.role === "hr" ||
            currentUser?.role === "admin" ||
            currentUser?.role === "superadmin") && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Expense Approvals
                </CardTitle>
                <CardDescription>
                  Review and approve expense reimbursement requests from your
                  team
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {expenseReimbursements
                    .filter(
                      (exp) =>
                        exp.status === "submitted" &&
                        exp.employeeId !== currentUser?.id,
                    )
                    .sort(
                      (a, b) =>
                        new Date(b.submittedAt!).getTime() -
                        new Date(a.submittedAt!).getTime(),
                    )
                    .map((expense) => (
                      <Card
                        key={expense.id}
                        className="border-l-4 border-l-yellow-500"
                      >
                        <CardContent className="pt-6">
                          <div className="space-y-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-semibold">
                                    {expense.employeeName}
                                  </span>
                                  <Badge variant="secondary">
                                    {expense.employeeDepartment}
                                  </Badge>
                                  <Badge className="capitalize">
                                    {expense.category}
                                  </Badge>
                                </div>
                                <div className="font-bold text-xl mb-2">
                                  {expense.currency} {expense.amount.toFixed(2)}
                                </div>
                                <div className="text-sm text-muted-foreground space-y-1">
                                  <div>{expense.description}</div>
                                  <div>
                                    Expense Date:{" "}
                                    {new Date(
                                      expense.date,
                                    ).toLocaleDateString()}
                                  </div>
                                  <div>
                                    Submitted:{" "}
                                    {new Date(
                                      expense.submittedAt!,
                                    ).toLocaleDateString()}
                                  </div>
                                  {expense.receiptAttachments.length > 0 && (
                                    <div className="flex items-center gap-2 mt-2">
                                      {expense.receiptAttachments.map(
                                        (receipt) => (
                                          <Button
                                            key={receipt.id}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              const link =
                                                document.createElement("a");
                                              link.href = receipt.base64Data;
                                              link.download = receipt.fileName;
                                              link.click();
                                            }}
                                          >
                                            <FileText className="w-4 h-4 mr-1" />
                                            {receipt.fileName}
                                          </Button>
                                        ),
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex gap-2 pt-2 border-t">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="default" size="sm">
                                    <Check className="w-4 h-4 mr-1" />
                                    Approve
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Approve Expense</DialogTitle>
                                    <DialogDescription>
                                      Approve this expense reimbursement request
                                    </DialogDescription>
                                  </DialogHeader>
                                  <form
                                    onSubmit={(e) => {
                                      e.preventDefault();
                                      const formData = new FormData(
                                        e.currentTarget,
                                      );
                                      const notes = formData.get(
                                        "notes",
                                      ) as string;

                                      setExpenseReimbursements(
                                        expenseReimbursements.map((exp) =>
                                          exp.id === expense.id
                                            ? {
                                                ...exp,
                                                status: "approved",
                                                reviewedBy: currentUser!.id,
                                                reviewedByName:
                                                  currentUser!.name,
                                                reviewedAt:
                                                  new Date().toISOString(),
                                                reviewNotes:
                                                  notes || "Approved",
                                                updatedAt:
                                                  new Date().toISOString(),
                                              }
                                            : exp,
                                        ),
                                      );
                                      toast.success(
                                        "Expense approved successfully",
                                      );
                                    }}
                                    className="space-y-4"
                                  >
                                    <div>
                                      <label className="text-sm font-medium">
                                        Approval Notes (Optional)
                                      </label>
                                      <textarea
                                        name="notes"
                                        className="w-full px-3 py-2 border rounded-md mt-1"
                                        rows={3}
                                        placeholder="Add any notes..."
                                      />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                      <Button type="submit">
                                        Confirm Approval
                                      </Button>
                                    </div>
                                  </form>
                                </DialogContent>
                              </Dialog>

                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="destructive" size="sm">
                                    <X className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Reject Expense</DialogTitle>
                                    <DialogDescription>
                                      Provide a reason for rejecting this
                                      expense request
                                    </DialogDescription>
                                  </DialogHeader>
                                  <form
                                    onSubmit={(e) => {
                                      e.preventDefault();
                                      const formData = new FormData(
                                        e.currentTarget,
                                      );
                                      const notes = formData.get(
                                        "notes",
                                      ) as string;

                                      if (!notes) {
                                        toast.error(
                                          "Please provide a reason for rejection",
                                        );
                                        return;
                                      }

                                      setExpenseReimbursements(
                                        expenseReimbursements.map((exp) =>
                                          exp.id === expense.id
                                            ? {
                                                ...exp,
                                                status: "rejected",
                                                reviewedBy: currentUser!.id,
                                                reviewedByName:
                                                  currentUser!.name,
                                                reviewedAt:
                                                  new Date().toISOString(),
                                                reviewNotes: notes,
                                                updatedAt:
                                                  new Date().toISOString(),
                                              }
                                            : exp,
                                        ),
                                      );
                                      toast.success("Expense rejected");
                                    }}
                                    className="space-y-4"
                                  >
                                    <div>
                                      <label className="text-sm font-medium">
                                        Rejection Reason *
                                      </label>
                                      <textarea
                                        name="notes"
                                        required
                                        className="w-full px-3 py-2 border rounded-md mt-1"
                                        rows={3}
                                        placeholder="Explain why this expense is being rejected..."
                                      />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        type="submit"
                                        variant="destructive"
                                      >
                                        Confirm Rejection
                                      </Button>
                                    </div>
                                  </form>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                  {expenseReimbursements.filter(
                    (exp) =>
                      exp.status === "submitted" &&
                      exp.employeeId !== currentUser?.id,
                  ).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No pending expense approvals</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Expense Statistics */}
          {expenseReimbursements.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Expense Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Total Submitted
                    </div>
                    <div className="text-2xl font-bold">
                      {
                        expenseReimbursements.filter(
                          (exp) => exp.employeeId === currentUser?.id,
                        ).length
                      }
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Pending</div>
                    <div className="text-2xl font-bold text-yellow-600">
                      {
                        expenseReimbursements.filter(
                          (exp) =>
                            exp.employeeId === currentUser?.id &&
                            exp.status === "submitted",
                        ).length
                      }
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Approved
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {
                        expenseReimbursements.filter(
                          (exp) =>
                            exp.employeeId === currentUser?.id &&
                            exp.status === "approved",
                        ).length
                      }
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Rejected
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      {
                        expenseReimbursements.filter(
                          (exp) =>
                            exp.employeeId === currentUser?.id &&
                            exp.status === "rejected",
                        ).length
                      }
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Total Amount
                    </div>
                    <div className="text-2xl font-bold">
                      {currentOrg?.currency || "USD"}{" "}
                      {expenseReimbursements
                        .filter(
                          (exp) =>
                            exp.employeeId === currentUser?.id &&
                            exp.status !== "rejected",
                        )
                        .reduce((sum, exp) => sum + exp.amount, 0)
                        .toFixed(2)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="max-w-2xl">
          <ChangePassword
            currentUser={currentUser!}
            users={employeeProfiles.map((emp) => ({
              id: emp.id,
              name: emp.name,
              email: emp.email,
              password: emp.password,
              role: emp.role,
              department: emp.department,
              avatar: emp.avatar,
              joinedAt: emp.createdAt,
              phone: emp.phone,
            }))}
            onPasswordChange={(updatedUsers) => {
              setEmployeeProfiles(
                updatedUsers.map((user) => ({
                  id: user.id,
                  name: user.name,
                  email: user.email,
                  password: user.password,
                  role: user.role,
                  department: user.department,
                  avatar: user.avatar,
                  phone: user.phone || "",
                  createdAt: user.joinedAt,
                  skills: [],
                  projects: [],
                })),
              );
              setShowChangePassword(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Leave Request Dialog */}
      <Dialog
        open={leaveDialogOpen}
        onOpenChange={(open) => {
          setLeaveDialogOpen(open);
          if (!open) {
            // Reset form fields when dialog closes to ensure clean state for next use
            // This prevents stale data from remaining in form inputs across dialog sessions
            setLeaveStartDate(undefined);
            setLeaveEndDate(undefined);
            setLeaveType("vacation");
            setLeaveReason("");
            setLeaveAttachments([]);
          }
        }}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
            <DialogDescription>Submit a new leave request</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLeaveSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="leave-start-date">Start Date</label>
                <input
                  id="leave-start-date"
                  type="date"
                  className="w-full p-2 border rounded text-base"
                  value={
                    leaveStartDate
                      ? leaveStartDate.toISOString().split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    setLeaveStartDate(e.target.valueAsDate || undefined)
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="leave-end-date">End Date</label>
                <input
                  id="leave-end-date"
                  type="date"
                  className="w-full p-2 border rounded text-base"
                  value={
                    leaveEndDate ? leaveEndDate.toISOString().split("T")[0] : ""
                  }
                  onChange={(e) =>
                    setLeaveEndDate(e.target.valueAsDate || undefined)
                  }
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="leave-type">Leave Type</label>
              <select
                id="leave-type"
                className="w-full p-2 border rounded text-base"
                value={leaveType}
                onChange={(e) =>
                  setLeaveType(
                    e.target.value as
                      | "vacation"
                      | "sick"
                      | "personal"
                      | "emergency",
                  )
                }
                required
              >
                <option value="">Select Type</option>
                <option value="vacation">Vacation</option>
                <option value="sick">Sick Leave</option>
                <option value="personal">Personal</option>
              </select>
            </div>
            <div className="space-y-2">
              <label htmlFor="leave-reason">Reason</label>
              <input
                id="leave-reason"
                className="w-full p-2 border rounded text-base"
                value={leaveReason}
                onChange={(e) => setLeaveReason(e.target.value)}
                required
              />
            </div>

            {/* File Attachments */}
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Attachments{" "}
                <span className="text-xs text-muted-foreground">
                  (Optional, max 3 files, 2MB each)
                </span>
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(
                      file,
                      setLeaveAttachments,
                      leaveAttachments,
                    );
                    e.target.value = ""; // Reset input
                  }
                }}
                className="w-full p-2 border rounded text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Allowed: JPG, PNG, PDF
              </p>

              {/* Attachment List */}
              {leaveAttachments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {leaveAttachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center justify-between p-2 bg-muted rounded text-sm"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{att.name}</span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          ({formatFileSize(att.size)})
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleRemoveAttachment(
                            att.id,
                            setLeaveAttachments,
                            leaveAttachments,
                          )
                        }
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? "Submitting..." : "Submit Request"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Timesheet Dialog */}
      <Dialog
        open={timesheetDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            resetLogHoursForm();
          }
          setTimesheetDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log Hours</DialogTitle>
            <DialogDescription>Add a new timesheet entry</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTimesheetSubmit} className="space-y-4">
            <div className="space-y-2">
              <label>Date</label>
              <input
                type="date"
                className="w-full p-2 border rounded text-base"
                value={
                  timesheetDate ? timesheetDate.toISOString().split("T")[0] : ""
                }
                onChange={(e) =>
                  setTimesheetDate(e.target.valueAsDate || new Date())
                }
                required
              />
            </div>
            <div className="space-y-2">
              <label>Project</label>
              <input
                className="w-full p-2 border rounded text-base"
                value={timesheetProject}
                onChange={(e) => setTimesheetProject(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label>Task</label>
              <input
                className="w-full p-2 border rounded text-base"
                value={timesheetTask}
                onChange={(e) => setTimesheetTask(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label>Hours</label>
              <input
                type="number"
                step="0.5"
                className="w-full p-2 border rounded text-base"
                value={timesheetHours}
                onChange={(e) => setTimesheetHours(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label>Description</label>
              <input
                className="w-full p-2 border rounded text-base"
                value={timesheetDescription}
                onChange={(e) => setTimesheetDescription(e.target.value)}
              />
            </div>

            {/* File Attachments */}
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Attachments{" "}
                <span className="text-xs text-muted-foreground">
                  (Optional, max 3 files, 2MB each)
                </span>
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(
                      file,
                      setTimesheetAttachments,
                      timesheetAttachments,
                    );
                    e.target.value = ""; // Reset input
                  }
                }}
                className="w-full p-2 border rounded text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Allowed: JPG, PNG, PDF (receipts, invoices, etc.)
              </p>

              {/* Attachment List */}
              {timesheetAttachments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {timesheetAttachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center justify-between p-2 bg-muted rounded text-sm"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{att.name}</span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          ({formatFileSize(att.size)})
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleRemoveAttachment(
                            att.id,
                            setTimesheetAttachments,
                            timesheetAttachments,
                          )
                        }
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button type="submit" disabled={isLoading}>
              Submit Entry
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="mt-auto bg-white border-t border-slate-200 py-4 px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>© 2026 AAHANA TECH. All Rights Reserved.</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="mailto:wasim.akh2@gmail.com"
              className="hover:text-primary transition-colors"
              data-testid="footer-contact"
            >
              Contact Support
            </a>
            <span className="text-xs">FlowHR v3.0.0</span>
          </div>
        </div>
      </footer>

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <div className="fixed bottom-6 right-6">
          <Button
            size="lg"
            onClick={handleCheckIn}
            className={`h-14 w-14 rounded-full shadow-lg transition-all duration-300 hover:scale-110 ${
              isCheckedIn
                ? "bg-[var(--destructive)] hover:bg-[var(--destructive)] opacity-75 hover:opacity-90"
                : "bg-[var(--success)] hover:bg-[var(--success)] opacity-90 hover:opacity-100"
            }`}
          >
            {isCheckedIn ? (
              <Clock className="w-6 h-6" />
            ) : (
              <CheckCircle className="w-6 h-6" />
            )}
          </Button>
        </div>
      )}

      {/* Old tabs section removed - now using sidebar navigation */}

      {/* Floating Action Button for Mobile */}
      <div className="fixed bottom-6 right-6 sm:hidden">
        <Dialog>
          <DialogTrigger asChild>
            <Button size="lg" className="h-14 w-14 rounded-full shadow-lg">
              <Plus className="w-6 h-6" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Quick Actions</DialogTitle>
              <DialogDescription>What would you like to do?</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-20 flex flex-col space-y-2"
                onClick={() => {
                  if (!isCheckedIn) handleCheckIn();
                  else if (isCheckedIn) handleCheckIn();
                }}
              >
                <CheckCircle className="w-6 h-6" />
                <span className="text-xs">
                  {isCheckedIn ? "Check Out" : "Check In"}
                </span>
              </Button>

              {isCheckedIn && (
                <Button
                  variant="outline"
                  className="h-20 flex flex-col space-y-2"
                  onClick={handleBreakToggle}
                >
                  {isOnBreak ? (
                    <Play className="w-6 h-6" />
                  ) : (
                    <Pause className="w-6 h-6" />
                  )}
                  <span className="text-xs">
                    {isOnBreak ? "End Break" : "Take Break"}
                  </span>
                </Button>
              )}

              <Button
                variant="outline"
                className="h-20 flex flex-col space-y-2"
                onClick={() => setLeaveDialogOpen(true)}
              >
                <CalendarIcon className="w-6 h-6" />
                <span className="text-xs">Request Leave</span>
              </Button>

              <Button
                variant="outline"
                className="h-20 flex flex-col space-y-2"
                onClick={() => openTimesheetDialog()}
              >
                <Clock className="w-6 h-6" />
                <span className="text-xs">Log Hours</span>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Onboarding Dialog */}
      <Dialog
        open={onboardingDialogOpen}
        onOpenChange={setOnboardingDialogOpen}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              {onboardingState?.steps[onboardingState?.currentStep || 0]?.title}
            </DialogTitle>
            <DialogDescription>
              {
                onboardingState?.steps[onboardingState?.currentStep || 0]
                  ?.description
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Progress indicator */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>
                  {(onboardingState?.currentStep || 0) + 1} of{" "}
                  {onboardingState?.steps.length || 0}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      (((onboardingState?.currentStep || 0) + 1) /
                        (onboardingState?.steps.length || 1)) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            {/* Step content */}
            <div className="min-h-[200px] flex items-center justify-center">
              {onboardingState?.currentStep === 0 && (
                <div className="text-center space-y-4">
                  <div className="text-6xl">🎉</div>
                  <h3 className="text-xl font-semibold">Welcome to FlowHR!</h3>
                  <p className="text-muted-foreground max-w-md">
                    Your new HR management system that makes tracking
                    attendance, managing leaves, and logging hours feel natural
                    and effortless.
                  </p>
                </div>
              )}

              {onboardingState?.currentStep === 1 && (
                <div className="text-center space-y-4">
                  <div className="text-6xl">👤</div>
                  <h3 className="text-xl font-semibold">Set Up Your Profile</h3>
                  <p className="text-muted-foreground max-w-md">
                    Your profile information helps personalize your FlowHR
                    experience. You can update it anytime by clicking the
                    profile button in the top right.
                  </p>
                  <Button
                    onClick={() => {
                      setProfileDialogOpen(true);
                      completeOnboardingStep("profile-setup");
                    }}
                    className="mt-4"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Open Profile
                  </Button>
                </div>
              )}

              {onboardingState?.currentStep === 2 && (
                <div className="text-center space-y-4">
                  <div className="text-6xl">⏰</div>
                  <h3 className="text-xl font-semibold">
                    Try Your First Check-in
                  </h3>
                  <p className="text-muted-foreground max-w-md">
                    Start tracking your attendance with a simple check-in.
                    FlowHR automatically detects if you're late and helps build
                    your attendance streak.
                  </p>
                  <Button
                    onClick={() => {
                      setOnboardingDialogOpen(false);
                      if (!isCheckedIn) {
                        handleCheckIn();
                      }
                    }}
                    className="mt-4"
                    disabled={isCheckedIn}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {isCheckedIn ? "Already Checked In!" : "Check In Now"}
                  </Button>
                </div>
              )}

              {onboardingState?.currentStep === 3 && (
                <div className="text-center space-y-4">
                  <div className="text-6xl">🗂️</div>
                  <h3 className="text-xl font-semibold">
                    Explore Key Features
                  </h3>
                  <p className="text-muted-foreground max-w-md">
                    FlowHR has several main sections: Dashboard overview,
                    Attendance tracking, Leave management, Timesheet logging,
                    Team directory, and Reports. Try clicking through the tabs
                    below to explore.
                  </p>
                  <Button
                    onClick={() => {
                      completeOnboardingStep("explore-tabs");
                      nextOnboardingStep();
                    }}
                    className="mt-4"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    I've Explored the Tabs
                  </Button>
                </div>
              )}

              {onboardingState?.currentStep === 4 && (
                <div className="text-center space-y-4">
                  <div className="text-6xl">📊</div>
                  <h3 className="text-xl font-semibold">
                    Log Your First Hours
                  </h3>
                  <p className="text-muted-foreground max-w-md">
                    Track time spent on different projects and tasks. This helps
                    with productivity insights and project management.
                  </p>
                  <Button
                    onClick={() => {
                      openTimesheetDialog();
                      setOnboardingDialogOpen(false);
                    }}
                    className="mt-4"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Log Hours
                  </Button>
                </div>
              )}

              {onboardingState?.currentStep === 5 && (
                <div className="text-center space-y-4">
                  <div className="text-6xl">🎯</div>
                  <h3 className="text-xl font-semibold">Set a Weekly Goal</h3>
                  <p className="text-muted-foreground max-w-md">
                    Weekly goals help you stay motivated and track your
                    productivity. Set targets for hours worked, tasks completed,
                    or projects finished.
                  </p>
                  <Button
                    onClick={() => {
                      setGoalDialogOpen(true);
                      setOnboardingDialogOpen(false);
                    }}
                    className="mt-4"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Set Goal
                  </Button>
                </div>
              )}

              {onboardingState?.currentStep === 6 && (
                <div className="text-center space-y-4">
                  <div className="text-6xl">⌨️</div>
                  <h3 className="text-xl font-semibold">
                    Learn Keyboard Shortcuts
                  </h3>
                  <p className="text-muted-foreground max-w-md">
                    Power users can save time with keyboard shortcuts. Press
                    Alt+C to check in/out, Alt+T to log hours, and more!
                  </p>
                  <Button
                    onClick={() => {
                      setKeyboardShortcutsOpen(true);
                      completeOnboardingStep("keyboard-shortcuts");
                    }}
                    className="mt-4"
                  >
                    <Keyboard className="w-4 h-4 mr-2" />
                    View Shortcuts
                  </Button>
                </div>
              )}

              {onboardingState?.currentStep === 7 && (
                <div className="text-center space-y-4">
                  <div className="text-6xl">🚀</div>
                  <h3 className="text-xl font-semibold">You're All Set!</h3>
                  <p className="text-muted-foreground max-w-md">
                    Congratulations! You've completed the FlowHR onboarding
                    tour. You can always restart this tour from the help menu if
                    needed.
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                    <Badge
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Profile Setup
                    </Badge>
                    <Badge
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" />
                      First Check-in
                    </Badge>
                    <Badge
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Feature Exploration
                    </Badge>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation buttons */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={skipOnboarding}>
                Skip Tour
              </Button>

              <div className="flex gap-2">
                {(onboardingState?.currentStep || 0) > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOnboardingState(
                        (
                          current = {
                            isFirstTime: true,
                            currentStep: 0,
                            steps: [],
                            showTooltip: false,
                            completedSteps: [],
                          },
                        ) => ({
                          ...current,
                          currentStep: Math.max(current.currentStep - 1, 0),
                        }),
                      );
                    }}
                  >
                    Previous
                  </Button>
                )}

                {(onboardingState?.currentStep || 0) <
                (onboardingState?.steps.length || 0) - 1 ? (
                  <Button onClick={nextOnboardingStep}>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      completeOnboardingStep("completion");
                      setOnboardingState(
                        (
                          current = {
                            isFirstTime: true,
                            currentStep: 0,
                            steps: [],
                            showTooltip: false,
                            completedSteps: [],
                          },
                        ) => ({
                          ...current,
                          isFirstTime: false,
                        }),
                      );
                      setOnboardingDialogOpen(false);
                      toast.success(
                        "🎉 Welcome to FlowHR! You're ready to go!",
                      );
                    }}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Get Started
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Attendance Dialog */}
      <Dialog
        open={manualEntryDialogOpen}
        onOpenChange={setManualEntryDialogOpen}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manual Attendance Entry</DialogTitle>
            <DialogDescription>
              Add or update attendance record manually
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleManualAttendanceSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date</label>
              <Input
                type="date"
                value={manualEntryDate?.toISOString().split("T")[0] || ""}
                onChange={(e) => setManualEntryDate(new Date(e.target.value))}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Check In Time</label>
              <Input
                type="time"
                value={manualCheckInTime}
                onChange={(e) => setManualCheckInTime(e.target.value)}
                required
                placeholder="09:00"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Check Out Time (Optional)
              </label>
              <Input
                type="time"
                value={manualCheckOutTime}
                onChange={(e) => setManualCheckOutTime(e.target.value)}
                placeholder="17:00"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <select
                className="w-full p-2 border rounded-md"
                value={manualStatus}
                onChange={(e) =>
                  setManualStatus(
                    e.target.value as
                      | "present"
                      | "absent"
                      | "late"
                      | "half-day",
                  )
                }
                required
              >
                <option value="present">Present</option>
                <option value="late">Late</option>
                <option value="half-day">Half Day</option>
                <option value="absent">Absent</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Location</label>
              <Input
                type="text"
                value={manualLocation}
                onChange={(e) => setManualLocation(e.target.value)}
                placeholder="Office - Main Building"
                required
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setManualEntryDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Attendance"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Onboarding Checklist Details Dialog */}
      <Dialog
        open={onboardingChecklistDialogOpen}
        onOpenChange={setOnboardingChecklistDialogOpen}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              Onboarding Checklist
            </DialogTitle>
            <DialogDescription>
              Track completion of onboarding tasks
            </DialogDescription>
          </DialogHeader>
          {selectedOnboardingChecklistId &&
            (() => {
              const checklist = employeeOnboardingChecklists.find(
                (c) => c.id === selectedOnboardingChecklistId,
              );
              if (!checklist) return null;

              const completedCount = checklist.items.filter(
                (item) => item.completedAt,
              ).length;
              const totalCount = checklist.items.length;
              const percentage = Math.round(
                (completedCount / totalCount) * 100,
              );

              return (
                <div className="space-y-6">
                  {/* Header Info */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold">
                          {checklist.employeeName}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {checklist.department}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-primary">
                          {percentage}%
                        </div>
                        <div className="text-sm text-slate-600">
                          {completedCount}/{totalCount} Complete
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5">
                      <div
                        className="bg-primary h-2.5 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Tasks by Category */}
                  {[
                    "documentation",
                    "equipment",
                    "access",
                    "training",
                    "orientation",
                    "compliance",
                  ].map((category) => {
                    const categoryTasks = checklist.items.filter(
                      (item) => item.category === category,
                    );
                    if (categoryTasks.length === 0) return null;

                    const categoryIcons = {
                      documentation: List,
                      equipment: Briefcase,
                      access: User,
                      training: GraduationCap,
                      orientation: Users,
                      compliance: CheckCircle,
                    };
                    const Icon =
                      categoryIcons[category as keyof typeof categoryIcons];

                    return (
                      <div key={category} className="space-y-3">
                        <h4 className="font-semibold text-slate-900 capitalize flex items-center gap-2">
                          <Icon className="w-5 h-5 text-primary" />
                          {category}
                        </h4>
                        <div className="space-y-2">
                          {categoryTasks.map((item) => (
                            <div
                              key={item.id}
                              className={`p-4 border rounded-lg ${item.completedAt ? "bg-green-50 border-green-200" : "bg-white border-slate-200"}`}
                            >
                              <div className="flex items-start gap-3">
                                <button
                                  onClick={() => {
                                    const updatedChecklists =
                                      employeeOnboardingChecklists.map((c) =>
                                        c.id === checklist.id
                                          ? {
                                              ...c,
                                              items: c.items.map((i) =>
                                                i.id === item.id
                                                  ? item.completedAt
                                                    ? {
                                                        ...i,
                                                        completedAt: undefined,
                                                        completedBy: undefined,
                                                      }
                                                    : {
                                                        ...i,
                                                        completedAt:
                                                          new Date().toISOString(),
                                                        completedBy:
                                                          currentUser?.id,
                                                      }
                                                  : i,
                                              ),
                                              updatedAt:
                                                new Date().toISOString(),
                                            }
                                          : c,
                                      );

                                    // Update status
                                    const updatedChecklist =
                                      updatedChecklists.find(
                                        (c) => c.id === checklist.id,
                                      );
                                    if (updatedChecklist) {
                                      const newCompletedCount =
                                        updatedChecklist.items.filter(
                                          (i) => i.completedAt,
                                        ).length;
                                      const newPercentage = Math.round(
                                        (newCompletedCount /
                                          updatedChecklist.items.length) *
                                          100,
                                      );
                                      updatedChecklist.completedPercentage =
                                        newPercentage;
                                      updatedChecklist.status =
                                        newPercentage === 100
                                          ? "completed"
                                          : newPercentage > 0
                                            ? "in-progress"
                                            : "not-started";
                                    }

                                    setEmployeeOnboardingChecklists(
                                      updatedChecklists,
                                    );
                                    toast.success(
                                      item.completedAt
                                        ? "Task marked as incomplete"
                                        : "Task completed!",
                                    );
                                  }}
                                  className={`mt-1 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                    item.completedAt
                                      ? "bg-green-500 border-green-500"
                                      : "bg-white border-slate-300 hover:border-primary"
                                  }`}
                                >
                                  {item.completedAt && (
                                    <Check
                                      className="w-3 h-3 text-white"
                                      weight="bold"
                                    />
                                  )}
                                </button>
                                <div className="flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <h5
                                        className={`font-medium ${item.completedAt ? "text-green-900 line-through" : "text-slate-900"}`}
                                      >
                                        {item.title}
                                        {item.required && (
                                          <span className="text-red-500 ml-1">
                                            *
                                          </span>
                                        )}
                                      </h5>
                                      <p className="text-sm text-slate-600 mt-1">
                                        {item.description}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-600">
                                    {item.dueDate && (
                                      <span className="flex items-center gap-1">
                                        <CalendarIcon className="w-3 h-3" />
                                        Due:{" "}
                                        {new Date(
                                          item.dueDate,
                                        ).toLocaleDateString()}
                                      </span>
                                    )}
                                    {item.assignedTo && (
                                      <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        Assigned: {item.assignedTo}
                                      </span>
                                    )}
                                    {item.completedAt && (
                                      <span className="flex items-center gap-1 text-green-600">
                                        <CheckCircle className="w-3 h-3" />
                                        Completed:{" "}
                                        {new Date(
                                          item.completedAt,
                                        ).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
        </DialogContent>
      </Dialog>

      {/* Create Onboarding Checklist Dialog */}
      <Dialog
        open={createChecklistDialogOpen}
        onOpenChange={setCreateChecklistDialogOpen}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Onboarding Checklist</DialogTitle>
            <DialogDescription>
              Create a new onboarding checklist for an employee
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const employeeId = formData.get("employeeId") as string;
              const employee = teamMembers.find((m) => m.id === employeeId);

              if (!employee) {
                toast.error("Please select a valid employee");
                return;
              }

              const newChecklist: EmployeeOnboardingChecklist = {
                id: crypto.randomUUID(),
                employeeId: employee.id,
                employeeName: employee.name,
                department: employee.department,
                startDate: formData.get("startDate") as string,
                status: "not-started",
                completedPercentage: 0,
                createdAt: new Date().toISOString(),
                createdBy: currentUser?.id || "system",
                updatedAt: new Date().toISOString(),
                items: [
                  {
                    id: crypto.randomUUID(),
                    title: "Complete Employment Forms",
                    description:
                      "Fill out and submit all required employment documentation",
                    category: "documentation",
                    required: true,
                    assignedTo: employee.id,
                  },
                  {
                    id: crypto.randomUUID(),
                    title: "IT Equipment Setup",
                    description: "Receive laptop, phone, and access cards",
                    category: "equipment",
                    required: true,
                    assignedTo: "IT Department",
                  },
                  {
                    id: crypto.randomUUID(),
                    title: "System Access Provisioning",
                    description:
                      "Get credentials for email, HRMS, and other systems",
                    category: "access",
                    required: true,
                    assignedTo: "IT Department",
                  },
                  {
                    id: crypto.randomUUID(),
                    title: "Company Orientation",
                    description: "Attend company orientation session",
                    category: "orientation",
                    required: true,
                    assignedTo: "HR Team",
                  },
                  {
                    id: crypto.randomUUID(),
                    title: "Security & Compliance Training",
                    description: "Complete mandatory security training",
                    category: "compliance",
                    required: true,
                    assignedTo: employee.id,
                  },
                  {
                    id: crypto.randomUUID(),
                    title: "Role-Specific Training",
                    description: "Complete job-specific training",
                    category: "training",
                    required: true,
                    assignedTo: employee.id,
                  },
                ],
              };

              setEmployeeOnboardingChecklists([
                ...employeeOnboardingChecklists,
                newChecklist,
              ]);
              setCreateChecklistDialogOpen(false);
              toast.success("Onboarding checklist created successfully!");
              e.currentTarget.reset();
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <label className="text-sm font-medium">Employee</label>
              <select
                name="employeeId"
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Select an employee</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} - {member.department}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                name="startDate"
                defaultValue={new Date().toISOString().split("T")[0]}
                required
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateChecklistDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Create Checklist</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Keyboard Shortcuts Dialog */}
      <Dialog
        open={keyboardShortcutsOpen}
        onOpenChange={setKeyboardShortcutsOpen}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Keyboard Shortcuts</DialogTitle>
            <DialogDescription>
              Use these shortcuts to navigate FlowHR faster
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Navigation</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span>Check In/Out</span>
                  <kbd className="px-2 py-1 bg-muted rounded border">Alt+C</kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span>Open Timesheet</span>
                  <kbd className="px-2 py-1 bg-muted rounded border">Alt+T</kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span>Request Leave</span>
                  <kbd className="px-2 py-1 bg-muted rounded border">Alt+L</kbd>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">General</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span>Open Search</span>
                  <kbd className="px-2 py-1 bg-muted rounded border">
                    Ctrl+K / ⌘K
                  </kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span>Toggle Theme</span>
                  <kbd className="px-2 py-1 bg-muted rounded border">
                    Ctrl+Shift+T
                  </kbd>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* About FlowHR Dialog */}
      <Dialog open={aboutDialogOpen} onOpenChange={setAboutDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-6 h-6" />
              About FlowHR
            </DialogTitle>
            <DialogDescription>
              Smart HR Management System for Modern Workplaces
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Company Info */}
            <div>
              <h3 className="font-semibold text-lg mb-3">
                Company Information
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="font-medium text-slate-900">AAHANA TECH</p>
                  <p className="text-slate-600">
                    Innovative HR Technology Solutions
                  </p>
                </div>
                <div className="mt-4">
                  <p className="text-slate-600">
                    FlowHR is a comprehensive HR management system designed to
                    streamline employee management, attendance tracking, leave
                    management, and performance evaluation. Built with modern
                    technologies to provide a seamless experience for HR
                    professionals and employees.
                  </p>
                </div>
              </div>
            </div>

            {/* Application Info */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3">
                Application Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Version:</span>
                  <span className="font-medium">1.0.0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Build Date:</span>
                  <span className="font-medium">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Platform:</span>
                  <span className="font-medium">GitHub Spark</span>
                </div>
              </div>
            </div>

            {/* Technology Stack */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3">Technology Stack</h3>
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <span className="w-32 font-medium">Frontend:</span>
                  <span>React 19, TypeScript, Vite</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-32 font-medium">UI Library:</span>
                  <span>Shadcn/ui, Tailwind CSS v4</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-32 font-medium">Icons:</span>
                  <span>Phosphor Icons, Lucide Icons</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-32 font-medium">Data Persistence:</span>
                  <span>GitHub Spark KV Store</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-32 font-medium">Testing:</span>
                  <span>Playwright, Vitest</span>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3">Key Features</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Employee Management</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Attendance Tracking</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Leave Management</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Timesheet Management</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Performance Reviews</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Shift Scheduling</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Employee Onboarding</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Employee Offboarding</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>WFH Request Tracking</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Document Management</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Expense Reimbursement</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Multi-Channel Notifications</span>
                </div>
              </div>
            </div>

            {/* Contact & Support */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3">Contact & Support</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-slate-600">Email:</span>
                  <a
                    href="mailto:support@aahnatech.com"
                    className="text-primary hover:underline font-medium"
                  >
                    support@aahnatech.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-600">Website:</span>
                  <a
                    href="https://aahnatech.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    www.aahnatech.com
                  </a>
                </div>
              </div>
            </div>

            {/* License & Acknowledgments */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3">
                License & Acknowledgments
              </h3>
              <div className="space-y-3 text-sm text-slate-600">
                <p>
                  © {new Date().getFullYear()} AAHANA TECH. All rights reserved.
                </p>
                <p>
                  FlowHR uses open-source software. We acknowledge and thank the
                  following projects:
                </p>
                <div className="pl-4 space-y-1">
                  <p>• React - MIT License</p>
                  <p>• TypeScript - Apache License 2.0</p>
                  <p>• Vite - MIT License</p>
                  <p>• Tailwind CSS - MIT License</p>
                  <p>• Shadcn/ui - MIT License</p>
                  <p>• Radix UI - MIT License</p>
                  <p>• Phosphor Icons - MIT License</p>
                  <p>• Lucide Icons - ISC License</p>
                  <p>• Recharts - MIT License</p>
                  <p>• date-fns - MIT License</p>
                  <p>• Sonner - MIT License</p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Session Timeout Warning Dialog */}
      <Dialog
        open={sessionTimeoutDialogOpen}
        onOpenChange={setSessionTimeoutDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Warning className="w-6 h-6" />
              Session Timeout Warning
            </DialogTitle>
            <DialogDescription>
              You have been inactive for a while. Your session will expire soon.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                Your session will automatically end in approximately{" "}
                {SESSION_WARNING_MINUTES} minutes due to inactivity. Click "Stay
                Logged In" to continue your session.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  handleLogout();
                  setSessionTimeoutDialogOpen(false);
                }}
              >
                <SignOut className="w-4 h-4 mr-2" />
                Log Out
              </Button>
              <Button
                onClick={() => {
                  setLastActivityTime(Date.now());
                  setSessionTimeoutDialogOpen(false);
                  toast.success("Session extended successfully");
                }}
              >
                Stay Logged In
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Global Search Dialog */}
      <CommandDialog
        open={searchOpen}
        onOpenChange={(open) => {
          if (!open) setSearchValue(""); // Clear search when dialog closes
          setSearchOpen(open);
        }}
      >
        <CommandInput
          placeholder="Search employees, attendance, leaves..."
          value={searchValue}
          onValueChange={setSearchValue}
        />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          {/* Search Employees/Team Members */}
          <CommandGroup
            heading={`Employees (${filteredMembers.slice(0, 15).length})`}
          >
            {filteredMembers
              .sort((a, b) => {
                // Prioritize exact matches
                const aExact =
                  a.name.toLowerCase() === searchValue.toLowerCase() ||
                  a.email.toLowerCase() === searchValue.toLowerCase();
                const bExact =
                  b.name.toLowerCase() === searchValue.toLowerCase() ||
                  b.email.toLowerCase() === searchValue.toLowerCase();
                if (aExact && !bExact) return -1;
                if (!aExact && bExact) return 1;
                return a.name.localeCompare(b.name);
              })
              .slice(0, 10)
              .map((member) => (
                <CommandItem
                  key={member.id}
                  value={`${member.name.toLowerCase()} ${member.email.toLowerCase()} ${member.department.toLowerCase()}`}
                  onSelect={() => {
                    setActiveSection("team");
                    setSearchOpen(false);
                    setSearchValue("");
                    toast.success(`Navigated to ${member.name}'s profile`);
                  }}
                >
                  <User className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {member.name}
                      {(member.name.toLowerCase() ===
                        searchValue.toLowerCase() ||
                        member.email.toLowerCase() ===
                          searchValue.toLowerCase()) && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          Exact Match
                        </Badge>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {member.department} • {member.email}
                    </span>
                  </div>
                </CommandItem>
              ))}
          </CommandGroup>

          {/* Search Leave Requests */}
          <CommandGroup
            heading={`Leave Requests (${filteredLeaves.slice(0, 15).length})`}
          >
            {filteredLeaves
              .sort(
                (a, b) =>
                  new Date(b.requestedAt || 0).getTime() -
                  new Date(a.requestedAt || 0).getTime(),
              )
              .slice(0, 10)
              .map((leave) => (
                <CommandItem
                  key={leave.id}
                  value={`${leave.employeeName.toLowerCase()} ${leave.type.toLowerCase()} ${leave.status.toLowerCase()} ${leave.startDate} ${leave.endDate}`}
                  onSelect={() => {
                    setActiveSection("leaves");
                    setSearchOpen(false);
                    setSearchValue("");
                    toast.success("Navigated to Leave Management");
                  }}
                >
                  <CalendarCheck className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {leave.employeeName} - {leave.type}
                      <Badge
                        variant={
                          leave.status === "approved"
                            ? "default"
                            : leave.status === "pending"
                              ? "secondary"
                              : "destructive"
                        }
                        className="ml-2 text-xs"
                      >
                        {leave.status}
                      </Badge>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {leave.startDate} to {leave.endDate}
                    </span>
                  </div>
                </CommandItem>
              ))}
          </CommandGroup>

          {/* Search Timesheet Entries */}
          <CommandGroup
            heading={`Timesheet Entries (${filteredTimesheets.slice(0, 15).length})`}
          >
            {filteredTimesheets
              .sort(
                (a, b) =>
                  new Date(b.date).getTime() - new Date(a.date).getTime(),
              )
              .slice(0, 10)
              .map((entry) => (
                <CommandItem
                  key={entry.id}
                  value={`${entry.project.toLowerCase()} ${entry.task.toLowerCase()} ${entry.description.toLowerCase()} ${entry.date}`}
                  onSelect={() => {
                    setActiveSection("timesheet");
                    setSearchOpen(false);
                    setSearchValue("");
                    toast.success("Navigated to Timesheet");
                  }}
                >
                  <Clock className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {entry.project} - {entry.task}
                      <Badge variant="outline" className="ml-2 text-xs">
                        {entry.hours}h
                      </Badge>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(entry.date).toLocaleDateString()} •{" "}
                      {entry.description.substring(0, 50)}
                      {entry.description.length > 50 && "..."}
                    </span>
                  </div>
                </CommandItem>
              ))}
          </CommandGroup>

          {/* Quick Actions */}
          <CommandSeparator />
          <CommandGroup heading="Quick Actions">
            <CommandItem
              onSelect={() => {
                handleCheckIn();
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              <span>Check In/Out</span>
              <CommandShortcut>Alt+C</CommandShortcut>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setLeaveDialogOpen(true);
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <CalendarDots className="mr-2 h-4 w-4" />
              <span>Request Leave</span>
              <CommandShortcut>Alt+L</CommandShortcut>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                openTimesheetDialog();
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <Clock className="mr-2 h-4 w-4" />
              <span>Log Hours</span>
              <CommandShortcut>Alt+T</CommandShortcut>
            </CommandItem>
          </CommandGroup>

          {/* Navigation */}
          <CommandSeparator />
          <CommandGroup heading="Navigate">
            <CommandItem
              onSelect={() => {
                setActiveSection("dashboard");
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <House className="mr-2 h-4 w-4" />
              <span>Dashboard</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setActiveSection("attendance");
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <ClockAfternoon className="mr-2 h-4 w-4" />
              <span>Attendance</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setActiveSection("leaves");
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <CalendarDots className="mr-2 h-4 w-4" />
              <span>Leave Management</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setActiveSection("timesheet");
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <Clock className="mr-2 h-4 w-4" />
              <span>Timesheet</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setActiveSection("team");
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <Users className="mr-2 h-4 w-4" />
              <span>Team Directory</span>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setActiveSection("reports");
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <ChartLine className="mr-2 h-4 w-4" />
              <span>Reports</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* Invitation Dialog */}
      <Dialog
        open={invitationDialogOpen}
        onOpenChange={setInvitationDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send User Invitation</DialogTitle>
            <DialogDescription>
              Invite a new user to join your organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Email Address</label>
              <Input
                type="email"
                value={invitationEmail}
                onChange={(e) => setInvitationEmail(e.target.value)}
                placeholder="user@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Role</label>
              <select
                value={invitationRole}
                onChange={(e) =>
                  setInvitationRole(
                    e.target.value as OrganizationInvitation["role"],
                  )
                }
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="hr">HR</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">
                Department (Optional)
              </label>
              <select
                value={invitationDepartmentId || ""}
                onChange={(e) =>
                  setInvitationDepartmentId(e.target.value || undefined)
                }
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="">No Department</option>
                {(orgDepartments || [])
                  .filter((d) => d.organizationId === currentOrgId)
                  .map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Location (Optional)</label>
              <select
                value={invitationLocationId || ""}
                onChange={(e) =>
                  setInvitationLocationId(e.target.value || undefined)
                }
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="">No Location</option>
                {(orgLocations || [])
                  .filter((l) => l.organizationId === currentOrgId)
                  .map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => {
                  if (!invitationEmail || !currentOrgId || !currentUser) return;
                  handleSendInvitation({
                    organizationId: currentOrgId,
                    email: invitationEmail,
                    role: invitationRole,
                    departmentId: invitationDepartmentId,
                    locationId: invitationLocationId,
                    invitedBy: currentUser.id,
                  });
                  setInvitationDialogOpen(false);
                  setInvitationEmail("");
                  setInvitationRole("employee");
                  setInvitationDepartmentId(undefined);
                  setInvitationLocationId(undefined);
                }}
                className="flex-1"
              >
                Send Invitation
              </Button>
              <Button
                variant="outline"
                onClick={() => setInvitationDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Invitation Dialog */}
      <Dialog
        open={bulkInvitationDialogOpen}
        onOpenChange={setBulkInvitationDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Send Invitations</DialogTitle>
            <DialogDescription>
              Invite multiple users at once (one email per line)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Email Addresses</label>
              <textarea
                value={bulkInvitationEmails}
                onChange={(e) => setBulkInvitationEmails(e.target.value)}
                placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                className="w-full mt-1 px-3 py-2 border rounded-md min-h-[150px]"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Role for All</label>
              <select
                value={bulkInvitationRole}
                onChange={(e) =>
                  setBulkInvitationRole(
                    e.target.value as OrganizationInvitation["role"],
                  )
                }
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="hr">HR</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => {
                  if (!bulkInvitationEmails || !currentOrgId || !currentUser)
                    return;
                  const emails = bulkInvitationEmails
                    .split("\n")
                    .map((e) => e.trim())
                    .filter((e) => e && e.includes("@"));
                  handleBulkSendInvitations(emails, bulkInvitationRole);
                  setBulkInvitationDialogOpen(false);
                  setBulkInvitationEmails("");
                  setBulkInvitationRole("employee");
                }}
                className="flex-1"
              >
                Send{" "}
                {
                  bulkInvitationEmails
                    .split("\n")
                    .filter((e) => e.trim() && e.includes("@")).length
                }{" "}
                Invitations
              </Button>
              <Button
                variant="outline"
                onClick={() => setBulkInvitationDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvImportDialogOpen} onOpenChange={setCsvImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Invitations from CSV</DialogTitle>
            <DialogDescription>
              CSV must contain 'email', 'role', and optionally 'department'
              columns
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">CSV Data</label>
              <textarea
                value={csvImportData}
                onChange={(e) => setCsvImportData(e.target.value)}
                placeholder="email,role,department&#10;user1@example.com,employee,Engineering&#10;user2@example.com,manager,Sales"
                className="w-full mt-1 px-3 py-2 border rounded-md min-h-[200px] font-mono text-sm"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              Example format:
              <pre className="mt-2 p-2 bg-muted rounded-md">
                email,role,department user@example.com,employee,Engineering
                manager@example.com,manager,Sales
              </pre>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => {
                  handleImportInvitationsFromCSV(csvImportData);
                  setCsvImportDialogOpen(false);
                  setCsvImportData("");
                }}
                className="flex-1"
              >
                Import Invitations
              </Button>
              <Button
                variant="outline"
                onClick={() => setCsvImportDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Create/Edit Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRole ? "Edit Role" : "Create Custom Role"}
            </DialogTitle>
            <DialogDescription>
              Define role name, description, and permissions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Role Name</label>
              <Input
                value={roleFormData.name}
                onChange={(e) =>
                  setRoleFormData({ ...roleFormData, name: e.target.value })
                }
                placeholder="e.g., Team Lead, Project Manager"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={roleFormData.description}
                onChange={(e) =>
                  setRoleFormData({
                    ...roleFormData,
                    description: e.target.value,
                  })
                }
                placeholder="Brief description of this role"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Permissions
              </label>
              <div className="text-xs text-muted-foreground mb-2">
                Select modules and actions for this role
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-md p-3">
                {(
                  [
                    "attendance",
                    "leaves",
                    "timesheets",
                    "employees",
                    "reports",
                    "settings",
                  ] as PermissionModule[]
                ).map((module) => {
                  const modulePermission = roleFormData.permissions.find(
                    (p) => p.module === module,
                  );
                  return (
                    <div key={module} className="border-b pb-2 last:border-0">
                      <div className="font-medium text-sm capitalize mb-1">
                        {module}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {(
                          [
                            "view",
                            "create",
                            "edit",
                            "delete",
                            "approve",
                          ] as PermissionAction[]
                        ).map((action) => (
                          <label
                            key={action}
                            className="flex items-center text-xs"
                          >
                            <input
                              type="checkbox"
                              checked={
                                modulePermission?.actions.includes(action) ||
                                false
                              }
                              onChange={(e) => {
                                const newPermissions = [
                                  ...roleFormData.permissions,
                                ];
                                const moduleIndex = newPermissions.findIndex(
                                  (p) => p.module === module,
                                );

                                if (e.target.checked) {
                                  if (moduleIndex >= 0) {
                                    newPermissions[moduleIndex].actions.push(
                                      action,
                                    );
                                  } else {
                                    newPermissions.push({
                                      module,
                                      actions: [action],
                                    });
                                  }
                                } else {
                                  if (moduleIndex >= 0) {
                                    newPermissions[moduleIndex].actions =
                                      newPermissions[
                                        moduleIndex
                                      ].actions.filter((a) => a !== action);
                                    if (
                                      newPermissions[moduleIndex].actions
                                        .length === 0
                                    ) {
                                      newPermissions.splice(moduleIndex, 1);
                                    }
                                  }
                                }

                                setRoleFormData({
                                  ...roleFormData,
                                  permissions: newPermissions,
                                });
                              }}
                              className="mr-1"
                            />
                            {action}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => {
                  if (!roleFormData.name || !currentOrgId || !currentUser)
                    return;

                  if (editingRole) {
                    handleUpdateCustomRole(editingRole.id, {
                      name: roleFormData.name,
                      description: roleFormData.description,
                      permissions: roleFormData.permissions,
                      inheritsFrom: roleFormData.inheritsFrom,
                    });
                  } else {
                    handleCreateCustomRole({
                      organizationId: currentOrgId,
                      name: roleFormData.name,
                      description: roleFormData.description,
                      permissions: roleFormData.permissions,
                      inheritsFrom: roleFormData.inheritsFrom,
                      createdBy: currentUser.id,
                    });
                  }

                  setRoleDialogOpen(false);
                  setEditingRole(null);
                  setRoleFormData({
                    name: "",
                    description: "",
                    permissions: [],
                    inheritsFrom: undefined,
                  });
                }}
                className="flex-1"
              >
                {editingRole ? "Update Role" : "Create Role"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setRoleDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Audit Log Dialog */}
      <Dialog open={roleAuditDialogOpen} onOpenChange={setRoleAuditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Role Assignment Audit Log</DialogTitle>
            <DialogDescription>
              Track all role-related changes and assignments
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(roleAuditLogs || [])
              .filter((log) => log.organizationId === currentOrgId)
              .sort(
                (a, b) =>
                  new Date(b.performedAt).getTime() -
                  new Date(a.performedAt).getTime(),
              )
              .map((log) => {
                const actionColors = {
                  "role-created": "bg-green-100 text-green-700",
                  "role-updated": "bg-blue-100 text-blue-700",
                  "role-deleted": "bg-red-100 text-red-700",
                  "role-assigned": "bg-purple-100 text-purple-700",
                  "role-unassigned": "bg-gray-100 text-gray-700",
                };

                return (
                  <div key={log.id} className="border p-3 rounded-md">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={actionColors[log.action]}>
                            {log.action.replace("-", " ").toUpperCase()}
                          </Badge>
                          <span className="font-medium">{log.roleName}</span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {new Date(log.performedAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            {(roleAuditLogs || []).filter(
              (log) => log.organizationId === currentOrgId,
            ).length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No audit logs yet
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recognition and Awards System */}
      {activeSection === "recognition" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Recognition & Awards
              </CardTitle>
              <CardDescription>
                Recognize and reward outstanding employee contributions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="send" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="send">Send</TabsTrigger>
                  <TabsTrigger value="received">Received</TabsTrigger>
                  <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
                  <TabsTrigger value="analytics">Analytics</TabsTrigger>
                </TabsList>

                {/* Send Recognition Tab */}
                <TabsContent value="send" className="space-y-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="w-4 h-4 mr-2" />
                        Send Recognition
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Send Recognition</DialogTitle>
                        <DialogDescription>
                          Recognize a colleague's outstanding contribution
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const formData = new FormData(e.currentTarget);
                          const recipient = formData.get("recipient") as string;
                          const type = formData.get("type") as string;
                          const reason = formData.get("reason") as string;

                          if (!recipient || !type || !reason) {
                            toast.error("Please fill all fields");
                            return;
                          }

                          const recognitionType = recognitionTypes.find(
                            (t) => t.id === type,
                          );
                          if (!recognitionType) return;

                          const recognition: Recognition = {
                            id: Math.random().toString(36).substr(2, 9),
                            senderId: currentUser?.id || "",
                            recipientId: recipient,
                            type: type,
                            points: recognitionType.points,
                            reason: reason,
                            createdAt: new Date().toISOString(),
                            reactions: [],
                          };

                          setRecognitions([...recognitions, recognition]);

                          // Add points to recipient
                          const currentPoints = userPoints[recipient] || 0;
                          setUserPoints({
                            ...userPoints,
                            [recipient]: currentPoints + recognitionType.points,
                          });

                          // Log transaction
                          setPointTransactions([
                            ...pointTransactions,
                            {
                              id: Math.random().toString(36).substr(2, 9),
                              userId: recipient,
                              amount: recognitionType.points,
                              type: "earned",
                              description: `${recognitionType.name} from ${currentUser?.name}`,
                              referenceId: recognition.id,
                              createdAt: new Date().toISOString(),
                            },
                          ]);

                          toast.success(
                            `${recognitionType.points} points awarded!`,
                          );
                          (e.target as HTMLFormElement).reset();
                        }}
                        className="space-y-4"
                      >
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Recipient
                          </label>
                          <select
                            name="recipient"
                            className="w-full px-3 py-2 border border-input rounded-md"
                          >
                            <option value="">Select employee</option>
                            {employees
                              .filter(
                                (e) =>
                                  e.organizationId === currentOrgId &&
                                  e.id !== currentUser?.id,
                              )
                              .map((e) => (
                                <option key={e.id} value={e.id}>
                                  {e.name}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Recognition Type
                          </label>
                          <select
                            name="type"
                            className="w-full px-3 py-2 border border-input rounded-md"
                          >
                            <option value="">Select type</option>
                            {recognitionTypes.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.points}pts)
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Reason
                          </label>
                          <textarea
                            name="reason"
                            placeholder="Describe why they deserve this recognition..."
                            className="w-full px-3 py-2 border border-input rounded-md text-sm"
                            rows={3}
                            maxLength={500}
                          />
                        </div>

                        <Button type="submit" className="w-full">
                          Send Recognition
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <div className="mt-6">
                    <h3 className="font-semibold mb-4">Recently Sent</h3>
                    <div className="space-y-2">
                      {recognitions
                        .filter((r) => r.senderId === currentUser?.id)
                        .sort(
                          (a, b) =>
                            new Date(b.createdAt).getTime() -
                            new Date(a.createdAt).getTime(),
                        )
                        .slice(0, 5)
                        .map((r) => (
                          <div
                            key={r.id}
                            className="flex items-center justify-between p-3 bg-muted rounded-lg"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-sm">
                                {
                                  employees.find((e) => e.id === r.recipientId)
                                    ?.name
                                }
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {r.reason}
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge variant="secondary">{r.points}pts</Badge>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </TabsContent>

                {/* Received Recognition Tab */}
                <TabsContent value="received" className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg mb-4">
                    <p className="font-semibold">
                      Total Points: {userPoints[currentUser?.id || ""] || 0}
                    </p>
                  </div>

                  <h3 className="font-semibold">Recognitions Received</h3>
                  <div className="space-y-2">
                    {recognitions
                      .filter((r) => r.recipientId === currentUser?.id)
                      .sort(
                        (a, b) =>
                          new Date(b.createdAt).getTime() -
                          new Date(a.createdAt).getTime(),
                      )
                      .map((r) => {
                        const sender = employees.find(
                          (e) => e.id === r.senderId,
                        );
                        const type = recognitionTypes.find(
                          (t) => t.id === r.type,
                        );
                        return (
                          <div key={r.id} className="p-3 bg-muted rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-medium text-sm">
                                  {type?.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  from {sender?.name}
                                </p>
                                <p className="text-xs mt-1">{r.reason}</p>
                              </div>
                              <Badge variant="default">{r.points}pts</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              {new Date(r.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        );
                      })}
                    {recognitions.filter(
                      (r) => r.recipientId === currentUser?.id,
                    ).length === 0 && (
                      <p className="text-center py-8 text-muted-foreground">
                        No recognitions yet
                      </p>
                    )}
                  </div>
                </TabsContent>

                {/* Leaderboard Tab */}
                <TabsContent value="leaderboard" className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-4">
                      Top Performers This Month
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(userPoints)
                        .map(([userId, points]) => ({
                          userId,
                          employee: employees.find((e) => e.id === userId),
                          points,
                        }))
                        .filter((item) => item.employee)
                        .sort((a, b) => b.points - a.points)
                        .slice(0, 15)
                        .map((item, idx) => (
                          <div
                            key={item.userId}
                            className="flex items-center justify-between p-3 bg-muted rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="text-lg font-bold text-primary w-6">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {item.employee?.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {item.employee?.department}
                                </p>
                              </div>
                            </div>
                            <Badge>{item.points} pts</Badge>
                          </div>
                        ))}
                    </div>
                  </div>
                </TabsContent>

                {/* Analytics Tab */}
                <TabsContent value="analytics" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-blue-50">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <p className="text-muted-foreground text-sm">
                            Total Recognitions
                          </p>
                          <p className="text-3xl font-bold text-primary">
                            {recognitions.length}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <p className="text-muted-foreground text-sm">
                            Total Points Awarded
                          </p>
                          <p className="text-3xl font-bold text-green-600">
                            {Object.values(userPoints).reduce(
                              (a, b) => a + b,
                              0,
                            )}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rewards Marketplace */}
      {activeSection === "rewards" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Rewards Marketplace
              </CardTitle>
              <CardDescription>
                Redeem your recognition points for rewards
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="catalog" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="catalog">Catalog</TabsTrigger>
                  <TabsTrigger value="cart">
                    Cart ({rewardCart.length})
                  </TabsTrigger>
                  <TabsTrigger value="orders">Orders</TabsTrigger>
                </TabsList>

                {/* Catalog Tab */}
                <TabsContent value="catalog" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {rewards
                      .filter((r) => r.status === "active")
                      .map((reward) => (
                        <Card
                          key={reward.id}
                          className="hover:shadow-md transition-shadow"
                        >
                          <CardContent className="pt-4">
                            <div className="bg-muted h-40 rounded-lg mb-3 flex items-center justify-center">
                              <Gift className="w-12 h-12 text-muted-foreground" />
                            </div>
                            <h3 className="font-semibold text-sm">
                              {reward.name}
                            </h3>
                            <p className="text-xs text-muted-foreground mb-2">
                              {reward.description}
                            </p>
                            <div className="flex items-center justify-between">
                              <Badge variant="outline">{reward.cost} pts</Badge>
                              <Button
                                size="sm"
                                onClick={() => {
                                  const existing = rewardCart.find(
                                    (item) => item.rewardId === reward.id,
                                  );
                                  if (existing) {
                                    setRewardCart(
                                      rewardCart.map((item) =>
                                        item.rewardId === reward.id
                                          ? {
                                              ...item,
                                              quantity: item.quantity + 1,
                                            }
                                          : item,
                                      ),
                                    );
                                  } else {
                                    setRewardCart([
                                      ...rewardCart,
                                      {
                                        rewardId: reward.id,
                                        quantity: 1,
                                        cost: reward.cost,
                                      },
                                    ]);
                                  }
                                  toast.success("Added to cart");
                                }}
                              >
                                Add to Cart
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </TabsContent>

                {/* Cart Tab */}
                <TabsContent value="cart" className="space-y-4 mt-4">
                  {rewardCart.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">
                      Your cart is empty
                    </p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {rewardCart.map((item) => {
                          const reward = rewards.find(
                            (r) => r.id === item.rewardId,
                          );
                          return (
                            <div
                              key={item.rewardId}
                              className="flex items-center justify-between p-3 bg-muted rounded-lg"
                            >
                              <div>
                                <p className="font-medium text-sm">
                                  {reward?.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Qty: {item.quantity}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">
                                  {item.cost * item.quantity} pts
                                </p>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setRewardCart(
                                      rewardCart.filter(
                                        (c) => c.rewardId !== item.rewardId,
                                      ),
                                    )
                                  }
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="font-semibold mb-2">
                          Total:{" "}
                          {rewardCart.reduce(
                            (sum, item) => sum + item.cost * item.quantity,
                            0,
                          )}{" "}
                          pts
                        </p>
                        <p className="text-xs text-muted-foreground mb-3">
                          Your balance: {userPoints[currentUser?.id || ""] || 0}{" "}
                          pts
                        </p>
                        <Button
                          className="w-full"
                          onClick={() => {
                            const totalCost = rewardCart.reduce(
                              (sum, item) => sum + item.cost * item.quantity,
                              0,
                            );
                            const balance =
                              userPoints[currentUser?.id || ""] || 0;

                            if (totalCost > balance) {
                              toast.error("Insufficient points");
                              return;
                            }

                            const order: RedemptionOrder = {
                              id: Math.random().toString(36).substr(2, 9),
                              userId: currentUser?.id || "",
                              items: rewardCart,
                              totalPoints: totalCost,
                              status: "pending",
                              deliveryMethod: "email",
                              createdAt: new Date().toISOString(),
                              updatedAt: new Date().toISOString(),
                            };

                            setRedemptionOrders([...redemptionOrders, order]);
                            setUserPoints({
                              ...userPoints,
                              [currentUser?.id || ""]: balance - totalCost,
                            });
                            setRewardCart([]);
                            toast.success("Order placed successfully!");
                          }}
                        >
                          Checkout
                        </Button>
                      </div>
                    </>
                  )}
                </TabsContent>

                {/* Orders Tab */}
                <TabsContent value="orders" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    {redemptionOrders
                      .filter((o) => o.userId === currentUser?.id)
                      .sort(
                        (a, b) =>
                          new Date(b.createdAt).getTime() -
                          new Date(a.createdAt).getTime(),
                      )
                      .map((order) => (
                        <div key={order.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-sm">
                              Order #{order.id.slice(0, 8)}
                            </p>
                            <Badge>{order.status}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </p>
                          <p className="font-medium">{order.totalPoints} pts</p>
                        </div>
                      ))}
                    {redemptionOrders.filter(
                      (o) => o.userId === currentUser?.id,
                    ).length === 0 && (
                      <p className="text-center py-8 text-muted-foreground">
                        No orders yet
                      </p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pulse Surveys */}
      {activeSection === "surveys" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smiley className="w-5 h-5" />
                Pulse Surveys
              </CardTitle>
              <CardDescription>
                Share feedback and insights to help improve the workplace
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="active" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="active">Active</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                  <TabsTrigger value="create">Create</TabsTrigger>
                </TabsList>

                {/* Active Surveys Tab */}
                <TabsContent value="active" className="space-y-4 mt-4">
                  <div className="space-y-3">
                    {surveys
                      .filter((s) => s.status === "active")
                      .map((survey) => (
                        <Card key={survey.id} className="bg-blue-50">
                          <CardContent className="pt-4">
                            <h3 className="font-semibold text-sm mb-1">
                              {survey.title}
                            </h3>
                            <p className="text-xs text-muted-foreground mb-3">
                              {survey.description}
                            </p>
                            <div className="flex items-center justify-between">
                              <p className="text-xs">
                                {survey.questions.length} questions • ~
                                {Math.ceil(survey.questions.length / 2)} min
                              </p>
                              <Button
                                size="sm"
                                onClick={() => {
                                  const response: SurveyResponse = {
                                    id: Math.random().toString(36).substr(2, 9),
                                    surveyId: survey.id,
                                    responderId: currentUser?.id,
                                    answers: survey.questions.map((q) => ({
                                      questionId: q.id,
                                      answer: "",
                                    })),
                                    submittedAt: new Date().toISOString(),
                                  };
                                  toast.info(
                                    "Survey response will be collected",
                                  );
                                }}
                              >
                                Take Survey
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    {surveys.filter((s) => s.status === "active").length ===
                      0 && (
                      <p className="text-center py-8 text-muted-foreground">
                        No active surveys
                      </p>
                    )}
                  </div>
                </TabsContent>

                {/* Completed Surveys Tab */}
                <TabsContent value="completed" className="space-y-4 mt-4">
                  <div className="space-y-3">
                    {surveyResponses
                      .filter((r) => r.responderId === currentUser?.id)
                      .map((response) => {
                        const survey = surveys.find(
                          (s) => s.id === response.surveyId,
                        );
                        return (
                          <Card key={response.id} className="bg-green-50">
                            <CardContent className="pt-4">
                              <h3 className="font-semibold text-sm">
                                {survey?.title}
                              </h3>
                              <p className="text-xs text-muted-foreground">
                                Submitted{" "}
                                {new Date(
                                  response.submittedAt,
                                ).toLocaleDateString()}
                              </p>
                            </CardContent>
                          </Card>
                        );
                      })}
                    {surveyResponses.filter(
                      (r) => r.responderId === currentUser?.id,
                    ).length === 0 && (
                      <p className="text-center py-8 text-muted-foreground">
                        No completed surveys
                      </p>
                    )}
                  </div>
                </TabsContent>

                {/* Create Survey Tab - HR Only */}
                {(currentUser?.role === "hr" ||
                  currentUser?.role === "admin" ||
                  currentUser?.role === "superadmin") && (
                  <TabsContent value="create" className="space-y-4 mt-4">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="w-4 h-4 mr-2" />
                          Create Survey
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Create Survey</DialogTitle>
                          <DialogDescription>
                            Create a new pulse survey for employees
                          </DialogDescription>
                        </DialogHeader>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.currentTarget);
                            const title = formData.get("title") as string;
                            const description = formData.get(
                              "description",
                            ) as string;

                            if (!title) {
                              toast.error("Please enter a title");
                              return;
                            }

                            const survey: Survey = {
                              id: Math.random().toString(36).substr(2, 9),
                              title,
                              description,
                              questions: [
                                {
                                  id: "1",
                                  text: "How satisfied are you with your role?",
                                  type: "rating",
                                  options: ["1", "2", "3", "4", "5"],
                                  required: true,
                                },
                              ],
                              status: "active",
                              anonymous: true,
                              distributedTo: employees
                                .filter(
                                  (e) => e.organizationId === currentOrgId,
                                )
                                .map((e) => e.id),
                              deadline: new Date(
                                Date.now() + 7 * 24 * 60 * 60 * 1000,
                              ).toISOString(),
                              createdAt: new Date().toISOString(),
                            };

                            setSurveys([...surveys, survey]);
                            toast.success("Survey created successfully!");
                            (e.target as HTMLFormElement).reset();
                          }}
                          className="space-y-4"
                        >
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Title
                            </label>
                            <Input
                              name="title"
                              placeholder="e.g., Weekly Engagement Survey"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Description
                            </label>
                            <textarea
                              name="description"
                              placeholder="Describe the purpose of this survey..."
                              className="w-full px-3 py-2 border border-input rounded-md text-sm"
                              rows={3}
                            />
                          </div>

                          <Button type="submit" className="w-full">
                            Create Survey
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </TabsContent>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Learning Management System */}
      {activeSection === "courses" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Learning Management System
              </CardTitle>
              <CardDescription>
                Access training courses and track your learning progress
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="catalog" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="catalog">Catalog</TabsTrigger>
                  <TabsTrigger value="enrolled">Enrolled</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                  <TabsTrigger value="progress">Progress</TabsTrigger>
                </TabsList>

                {/* Catalog Tab */}
                <TabsContent value="catalog" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {courses
                      .filter((c) => c.status === "published")
                      .map((course) => {
                        const enrollment = courseEnrollments.find(
                          (e) =>
                            e.userId === currentUser?.id &&
                            e.courseId === course.id,
                        );
                        return (
                          <Card
                            key={course.id}
                            className="hover:shadow-md transition-shadow"
                          >
                            <CardContent className="pt-4">
                              <div className="bg-gradient-to-br from-blue-100 to-purple-100 h-32 rounded-lg mb-3 flex items-center justify-center">
                                <BookOpen className="w-12 h-12 text-blue-600" />
                              </div>
                              <h3 className="font-semibold text-sm mb-1">
                                {course.title}
                              </h3>
                              <p className="text-xs text-muted-foreground mb-2">
                                {course.description}
                              </p>
                              <div className="flex items-center justify-between mb-3">
                                <Badge variant="outline" className="text-xs">
                                  {course.difficulty}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {course.duration} min
                                </span>
                              </div>
                              {enrollment ? (
                                <div>
                                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                    <div
                                      className="bg-blue-600 h-2 rounded-full"
                                      style={{
                                        width: `${enrollment.progress}%`,
                                      }}
                                    />
                                  </div>
                                  <p className="text-xs text-muted-foreground text-center">
                                    {enrollment.progress}% complete
                                  </p>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  className="w-full"
                                  onClick={() => {
                                    const newEnrollment: CourseEnrollment = {
                                      id: Math.random()
                                        .toString(36)
                                        .substr(2, 9),
                                      userId: currentUser?.id || "",
                                      courseId: course.id,
                                      status: "active",
                                      progress: 0,
                                      completedModules: [],
                                      enrolledAt: new Date().toISOString(),
                                    };
                                    setCourseEnrollments([
                                      ...courseEnrollments,
                                      newEnrollment,
                                    ]);
                                    toast.success("Enrolled in course!");
                                  }}
                                >
                                  Enroll Now
                                </Button>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                  {courses.filter((c) => c.status === "published").length ===
                    0 && (
                    <p className="text-center py-8 text-muted-foreground">
                      No courses available
                    </p>
                  )}
                </TabsContent>

                {/* Enrolled Tab */}
                <TabsContent value="enrolled" className="space-y-4 mt-4">
                  <div className="space-y-3">
                    {courseEnrollments
                      .filter(
                        (e) =>
                          e.userId === currentUser?.id && e.status === "active",
                      )
                      .map((enrollment) => {
                        const course = courses.find(
                          (c) => c.id === enrollment.courseId,
                        );
                        return (
                          <Card key={enrollment.id}>
                            <CardContent className="pt-4">
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold text-sm">
                                  {course?.title}
                                </h3>
                                <Badge>{enrollment.progress}%</Badge>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                                <div
                                  className="bg-blue-600 h-2 rounded-full transition-all"
                                  style={{ width: `${enrollment.progress}%` }}
                                />
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={() => {
                                  setCourseEnrollments(
                                    courseEnrollments.map((e) =>
                                      e.id === enrollment.id
                                        ? {
                                            ...e,
                                            progress: Math.min(
                                              e.progress + 25,
                                              100,
                                            ),
                                          }
                                        : e,
                                    ),
                                  );
                                }}
                              >
                                Continue Course
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    {courseEnrollments.filter(
                      (e) =>
                        e.userId === currentUser?.id && e.status === "active",
                    ).length === 0 && (
                      <p className="text-center py-8 text-muted-foreground">
                        No active enrollments
                      </p>
                    )}
                  </div>
                </TabsContent>

                {/* Completed Tab */}
                <TabsContent value="completed" className="space-y-4 mt-4">
                  <div className="space-y-3">
                    {courseEnrollments
                      .filter(
                        (e) =>
                          e.userId === currentUser?.id &&
                          e.status === "completed",
                      )
                      .map((enrollment) => {
                        const course = courses.find(
                          (c) => c.id === enrollment.courseId,
                        );
                        return (
                          <Card key={enrollment.id} className="bg-green-50">
                            <CardContent className="pt-4">
                              <h3 className="font-semibold text-sm mb-1">
                                {course?.title}
                              </h3>
                              <p className="text-xs text-muted-foreground">
                                Completed{" "}
                                {new Date(
                                  enrollment.completedAt!,
                                ).toLocaleDateString()}
                              </p>
                            </CardContent>
                          </Card>
                        );
                      })}
                    {courseEnrollments.filter(
                      (e) =>
                        e.userId === currentUser?.id &&
                        e.status === "completed",
                    ).length === 0 && (
                      <p className="text-center py-8 text-muted-foreground">
                        No completed courses yet
                      </p>
                    )}
                  </div>
                </TabsContent>

                {/* Progress Tab */}
                <TabsContent value="progress" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-blue-50">
                      <CardContent className="pt-4">
                        <p className="text-muted-foreground text-xs">
                          Courses Enrolled
                        </p>
                        <p className="text-2xl font-bold">
                          {
                            courseEnrollments.filter(
                              (e) => e.userId === currentUser?.id,
                            ).length
                          }
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                      <CardContent className="pt-4">
                        <p className="text-muted-foreground text-xs">
                          Courses Completed
                        </p>
                        <p className="text-2xl font-bold">
                          {
                            courseEnrollments.filter(
                              (e) =>
                                e.userId === currentUser?.id &&
                                e.status === "completed",
                            ).length
                          }
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Session Timeout Warning Modal */}
      <Dialog open={showTimeoutWarning} onOpenChange={setShowTimeoutWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Session Timeout Warning</DialogTitle>
            <DialogDescription>
              Your session will expire in less than a minute due to inactivity.
              Your work has been auto-saved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                lastActivity.current = Date.now();
                setShowTimeoutWarning(false);
              }}
            >
              Continue Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
